// Register / Edit API service page.
//
// Strict 1-to-1 port of xapi-new's RegisterPage:
//   - Same Zod schema (apiFormSchema), same defaults, same transformApiToForm/transformFormToApi.
//   - Same 4 submit branches: new (POST), edit-basic (PATCH), hotfix (PATCH version), release new (POST version).
//   - UI rebuilt under v2's design tokens — no Tailwind, just our --bg/--fg/--line system + Base UI primitives.
//
// Backend service methods used (see services.js): apiSvc.{create, get, updateBasic, updateVersion, createVersion, submitForReview}.

import React from 'react';
import { isFreeEndpoint } from '../endpoint-pricing.js';
import {
  useForm,
  useFieldArray,
  useWatch,
  useController,
  Controller,
  FormProvider,
  useFormContext,
} from 'react-hook-form';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { XPrim, XIcons } from '../primitives.jsx';
import { XApi } from '../services.js';
import { XShell } from '../shell.jsx';
import { useHostValidation } from '../use-host-validation.js';
import {
  Select,
  AiImportDialog,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Checkbox,
  Menu,
  MenuItem,
} from '../ui/index.js';
import { mergeImportedEndpoints, toOpenApiSpecInput, importedTransportSpecInput } from './workspace-sections/openapi-merge.js';
import {
  pricingRecordToArray,
  pricingArrayToRecord,
  TOKEN_PRICE_FIELDS,
} from './workspace-sections/pricing-utils.js';
import { DynamicRulesEditor } from './workspace-sections/dynamic-rules-editor.jsx';
import { WsEndpointFields } from './workspace-sections/ws-endpoint-fields.jsx';
import {
  DEFAULT_SANDBOX_CONFIG,
  DEFAULT_SANDBOX_OFFERING,
  SandboxProviderSection,
  buildSandboxBindingPayload,
  sandboxConfigSchema,
  transformSandboxBindingToForm,
} from './workspace-sections/sandbox-provider-section.jsx';
import { compileRules, deserializeRules } from './dynamic-rules.js';
import { useT } from '../i18n.jsx';
import { LogoUpload } from '../components/LogoUpload.jsx';

// Lazy-loaded JSON editor — pulls in rehype + refractor (~300KB gzip) for syntax
// highlighting. Most users never open JSON mode, so we shouldn't ship it eagerly.
// React.lazy returns a component compatible with Suspense; we wrap the editor
// usage in <Suspense> below so it gracefully falls back to a plain textarea
// during the network fetch.
const CodeEditor = React.lazy(() => import('@uiw/react-textarea-code-editor'));

// ============ Constants ============

export const CATEGORIES = [
  'AI-Models',
  'Sandbox',
  'Public-Utils',
  'Social',
  'Data-Analysis',
  'Crypto',
];
export const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
export const AUTH_TYPES = ['NONE', 'BEARER', 'HEADER', 'QUERY'];
// 协议固定的 Sandbox 创建路径：provider 不自由填写，标准化才能让消费者一份代码通吃。
export const SANDBOX_SURFACE_PATH = '/v1/sandboxes';

export const BILLING_TYPES = [
  'PER_CALL',
  'PER_TOKEN',
  'PER_RESOURCE',
  'DYNAMIC',
  'TIME_RENTAL',
  'SANDBOX',
];
export const BODY_TYPES = ['JSON', 'FORM_DATA', 'FORM_URLENCODED', 'NONE'];
export const BODY_TYPE_LABELS = {
  JSON: 'JSON',
  FORM_DATA: 'form-data',
  FORM_URLENCODED: 'x-www-form-urlencoded',
  NONE: 'none',
};
const BODY_FIELD_TYPES = ['text', 'file'];
export const STREAM_FORMATS = ['sse', 'ws', 'raw'];
export const PARAM_TYPES = ['string', 'number', 'boolean', 'object'];
export const ASYNC_MODES = ['NONE', 'OPTIONAL', 'REQUIRED'];

// 端点传输协议：HTTP（默认，存量端点无 protocol 视为 HTTP）| WEBSOCKET（provider 自助建 WS 端点）。
export const PROTOCOLS = ['HTTP', 'WEBSOCKET'];
// V2 async task system — see docs/异步任务/前端对接.md
export const TASK_STRATEGIES = ['WORKER_BLOCKING', 'UPSTREAM_POLLING'];
// Backend KNOWN_UPSTREAM_PROVIDERS (apps/api/src/api/api.service.ts).
// Future: GET /api/api-services/known-providers to fetch from server.
export const KNOWN_UPSTREAM_PROVIDERS = ['byteplus', 'openrouter'];
// Backend PROXY_TASK_GLOBAL_HOLD_AMOUNT_USD = 10 (libs/shared/src/constants/http.constants.ts).
export const FIXED_HOLD_MAX_USD = 10;

// Provider presets for token JSON paths.
const PROVIDER_PRESETS = {
  openai: {
    inputTokens: 'usage.prompt_tokens',
    outputTokens: 'usage.completion_tokens',
    model: 'model',
    requestModel: 'body.model',
    cacheCreationInputTokens: '',
    cacheReadInputTokens: '',
    cacheIncludedInInput: false,
  },
  anthropic: {
    inputTokens: 'usage.input_tokens',
    outputTokens: 'usage.output_tokens',
    model: 'model',
    requestModel: 'body.model',
    cacheCreationInputTokens: 'usage.cache_creation_input_tokens',
    cacheCreation5mInputTokens:
      'usage.cache_creation.ephemeral_5m_input_tokens',
    cacheCreation1hInputTokens:
      'usage.cache_creation.ephemeral_1h_input_tokens',
    cacheReadInputTokens: 'usage.cache_read_input_tokens',
    cacheIncludedInInput: false,
  },
  gemini: {
    inputTokens: 'usageMetadata.promptTokenCount',
    outputTokens: 'usageMetadata.candidatesTokenCount',
    model: 'modelVersion',
    requestModel: 'body.model',
    cacheCreationInputTokens: '',
    cacheReadInputTokens: '',
    cacheIncludedInInput: false,
  },
};

// ============ Defaults ============

const DEFAULT_AI_CONFIG = () => ({
  tokenJsonPaths: { ...PROVIDER_PRESETS.openai },
  supportsStreaming: false,
  streamFormat: null,
  modelPricing: [
    { modelName: 'default', inputPricePerToken: 1.0, outputPricePerToken: 2.0 },
  ],
});

let _epIdCounter = 0;
const nextEpId = () =>
  `e${Date.now().toString(36)}${(_epIdCounter++).toString(36)}`;

// rentalKey 是平台内部用来关联 CREATE / TERMINATE / STATUS 的稳定键，不是服务商
// 需要理解或维护的业务字段。基于端点表单 id 生成，保证同一份草稿反复渲染时不漂移。
const buildInternalRentalKey = (endpointId) => {
  const safeId = String(endpointId || nextEpId()).replace(
    /[^a-zA-Z0-9_-]/g,
    '',
  );
  return `rental_${(safeId || nextEpId()).slice(0, 56)}`;
};

const RENTAL_UNIT_LABELS = {
  SECOND: '秒',
  MINUTE: '分钟',
  HOUR: '小时',
};

const rentalGroupLabel = (endpoint, index) => {
  const name = String(endpoint?.name || '').trim();
  const path = String(endpoint?.path || '').trim();
  const identity = name || path || `创建接口 ${index + 1}`;
  const price = Number(endpoint?.rentalConfig?.pricing?.pricePerUnitUsd);
  const unit =
    RENTAL_UNIT_LABELS[endpoint?.rentalConfig?.pricing?.unit] || '小时';
  const priceLabel =
    Number.isFinite(price) && price > 0 ? ` · $${price}/${unit}` : '';
  return `${identity}${path && path !== identity ? ` · ${path}` : ''}${priceLabel}`;
};

// 缺省 WS 配置：仅在切到 WEBSOCKET 时才有意义；HTTP 端点保存不会发送它们（见 transformEndpointToApi）。
export const DEFAULT_WS_CONFIG = () => ({
  adapter: '',
  upstreamUrl: '',
  clientPath: '',
  subprotocols: [],
  idleTimeoutSec: undefined,
  maxDurationSec: undefined,
  maxConnPerKey: undefined,
  heartbeatSec: undefined,
  maxFrameBytes: undefined,
});
export const DEFAULT_WS_BILLING = () => ({
  kind: 'duration',
  perMinuteUsd: undefined,
  holdUsd: undefined,
});
export const DEFAULT_RENTAL_CONFIG = () => ({
  version: 1,
  rentalKey: '',
  role: 'CREATE',
  resourceIdResponsePath: 'data.id',
  resourceIdInput: { location: 'BODY', path: 'resourceId' },
  pricing: {
    unit: 'HOUR',
    pricePerUnitUsd: 1,
    minimumBillableSeconds: 60,
    maxRentalSeconds: 86400,
  },
  stateResponsePath: 'status',
  runningValues: ['running'],
  terminatedValues: ['terminated', 'stopped'],
  terminatedAtResponsePath: '',
});

export const INITIAL_ENDPOINT = (id) => ({
  id: id || nextEpId(),
  name: '',
  // 传输协议：缺省 HTTP，兼容存量端点（无 protocol 视为 HTTP）。
  protocol: 'HTTP',
  method: 'GET',
  path: '',
  group: '',
  costPerCall: 0.1,
  description: '',
  headers: [],
  params: [],
  pathParams: [],
  bodyType: 'JSON',
  bodySchema: '',
  bodyParams: [],
  responses: [
    {
      status_code: 200,
      label: 'success',
      type: 'json',
      body: { message: 'Success' },
    },
    {
      status_code: 400,
      label: 'error',
      type: 'json',
      body: { error: 'Bad Request' },
    },
  ],
  billingType: 'PER_CALL',
  estimatedMaxTokens: 1000,
  costPerResource: 0,
  resourceCountJsonPath: '',
  estimatedMaxResources: 10,
  privateHeaders: [],
  hidden: false,
  userOAuthProviderId: undefined,
  asyncMode: 'NONE',
  // V2: when UPSTREAM_POLLING the backend forces asyncMode=REQUIRED and reads
  // upstreamProvider / fixedHoldAmount. Defaults keep V1 (WORKER_BLOCKING) behavior.
  taskStrategy: 'WORKER_BLOCKING',
  upstreamProvider: undefined,
  fixedHoldAmount: undefined,
  aiConfig: DEFAULT_AI_CONFIG(),
  // ── AI Spec System（方案 X）────────────────────────────────
  // 这三个字段现在由后端自动判定(backend auto-association)；前端只读回显,
  // 不再编辑或发送。
  aiInterfaceSpecId: null,
  aiSpecDetectedBy: null,
  aiModelMappings: [],
  // 非 PER_TOKEN 的 AI 端点(如图像生成)由服务商声明它服务的那个上游模型;
  // 服务端据此自动映射到 canonical。PER_TOKEN 端点的模型来自 tokenPricing 的键。
  servedModel: '',
  // DYNAMIC 计费的规则构建器 UI-state(见 dynamic-rules.js)。仅 billingType==='DYNAMIC'
  // 时编辑/发送;序列化时 compileRules → 引擎 DynamicRulesConfig v2。
  dynamicRules: null,
  // TIME_RENTAL：切换到该计费类型时才初始化（见下方 rental effect）。普通端点
  // 若携带空 rentalKey，会在租赁 UI 不可见时被 schema 拦住，形成无法修正的错误。
  rentalConfig: undefined,
  // SANDBOX 计费：选中该类型时才初始化（见 SandboxBillingEffect），避免普通端点
  // 携带一份空的 sandbox 配置去参与校验/提交。
  sandboxConfig: null,
  // ── WebSocket（protocol==='WEBSOCKET' 时使用）────────────────────────────
  // wsConfig / wsBilling 仅在 WS 端点保存时发送；HTTP 端点这两个字段被剥掉。
  wsConfig: DEFAULT_WS_CONFIG(),
  wsBilling: DEFAULT_WS_BILLING(),
});

export const INITIAL_FORM = () => ({
  name: '',
  description: '',
  website: '',
  aboutMarkdown: '',
  logoUrl: '',
  category: CATEGORIES[0],
  host: '',
  baseUrl: '',
  baseUrls: [],
  openApiSpec: undefined,
  isPublic: true,
  accessMode: 'PROXY',
  rateLimitConfig: null,
  integrationMode: 'HTTP_PROXY',
  billingType: 'PER_CALL',
  authType: 'NONE',
  authConfig: { key: '', value: '' },
  privateHeaders: [],
  endpoints: [INITIAL_ENDPOINT('e1')],
  sandboxConfig: DEFAULT_SANDBOX_CONFIG(),
});

// ============ Zod schema (mirrors xapi-new apiFormSchema) ============

const tierSchema = z.object({
  inputTokenThreshold: z.number().positive(),
  inputPricePerToken: z.number().nonnegative(),
  outputPricePerToken: z.number().nonnegative(),
  cacheCreationPricePerToken: z.number().nonnegative().optional(),
  cacheCreation5mPricePerToken: z.number().nonnegative().optional(),
  cacheCreation1hPricePerToken: z.number().nonnegative().optional(),
  cacheReadPricePerToken: z.number().nonnegative().optional(),
  imagePricePerToken: z.number().nonnegative().optional(),
  audioInputPricePerToken: z.number().nonnegative().optional(),
  audioOutputPricePerToken: z.number().nonnegative().optional(),
  reasoningPricePerToken: z.number().nonnegative().optional(),
});

const modelPricingRowSchema = z.object({
  modelName: z.string(),
  inputPricePerToken: z.number().nonnegative(),
  outputPricePerToken: z.number().nonnegative(),
  cacheCreationPricePerToken: z.number().nonnegative().optional(),
  cacheCreation5mPricePerToken: z.number().nonnegative().optional(),
  cacheCreation1hPricePerToken: z.number().nonnegative().optional(),
  cacheReadPricePerToken: z.number().nonnegative().optional(),
  imagePricePerToken: z.number().nonnegative().optional(),
  audioInputPricePerToken: z.number().nonnegative().optional(),
  audioOutputPricePerToken: z.number().nonnegative().optional(),
  reasoningPricePerToken: z.number().nonnegative().optional(),
  toolCallPricing: z.record(z.string(), z.number().nonnegative()).optional(),
  tiers: z
    .array(tierSchema)
    .superRefine((tiers, ctx) => {
      const seen = new Set();
      tiers.forEach((t, i) => {
        if (seen.has(t.inputTokenThreshold)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Duplicate threshold',
            path: [i, 'inputTokenThreshold'],
          });
        }
        seen.add(t.inputTokenThreshold);
      });
    })
    .optional(),
});

const aiConfigSchema = z.object({
  tokenJsonPaths: z.object({
    inputTokens: z.string(),
    outputTokens: z.string(),
    model: z.string(),
    requestModel: z.string(),
    cacheCreationInputTokens: z.string().optional(),
    cacheReadInputTokens: z.string().optional(),
    cacheIncludedInInput: z.boolean().optional(),
  }),
  supportsStreaming: z.boolean(),
  streamFormat: z.enum(STREAM_FORMATS).nullable(),
  modelPricing: z.array(modelPricingRowSchema).superRefine((rows, ctx) => {
    const seen = new Map();
    rows.forEach((row, idx) => {
      const name = (row.modelName || '').trim();
      if (!name) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Model name is required',
          path: [idx, 'modelName'],
        });
        return;
      }
      const key = name.toLowerCase();
      if (seen.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Duplicate model name',
          path: [idx, 'modelName'],
        });
        return;
      }
      seen.set(key, idx);
    });
  }),
});

// WS 端点配置 schema。字段级只做形状/类型校验；必填/协议特定规则在 makeEndpointSchema
// 的 superRefine 里按 protocol 收口（避免影响 HTTP 端点）。
const wsConfigSchema = z
  .object({
    adapter: z.string().optional(),
    upstreamUrl: z.string().optional(),
    clientPath: z.string().optional(),
    subprotocols: z.array(z.string()).optional(),
    idleTimeoutSec: z.number().positive().optional(),
    maxDurationSec: z.number().positive().optional(),
    maxConnPerKey: z.number().positive().optional(),
    heartbeatSec: z.number().positive().optional(),
    maxFrameBytes: z.number().positive().optional(),
  })
  .optional();

const wsBillingSchema = z
  .object({
    kind: z.enum(['duration', 'realtime-token']).optional(),
    perMinuteUsd: z.number().nonnegative().optional(),
    holdUsd: z.number().nonnegative().optional(),
    // realtime-token：混合价（回退）+ 模态分档价（优先）
    inputTokenUsd: z.number().nonnegative().optional(),
    outputTokenUsd: z.number().nonnegative().optional(),
    inputTextTokenUsd: z.number().nonnegative().optional(),
    inputAudioTokenUsd: z.number().nonnegative().optional(),
    inputCachedTokenUsd: z.number().nonnegative().optional(),
    outputTextTokenUsd: z.number().nonnegative().optional(),
    outputAudioTokenUsd: z.number().nonnegative().optional(),
  })
  .optional();

const rentalConfigSchema = z.object({
  version: z.literal(1),
  rentalKey: z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/, {
    message: '请选择所属租赁计费组。',
  }),
  role: z.enum(['CREATE', 'TERMINATE', 'STATUS']),
  resourceIdResponsePath: z.string().optional(),
  resourceIdInput: z
    .object({
      location: z.enum(['PATH', 'QUERY', 'BODY']),
      path: z.string(),
    })
    .optional(),
  pricing: z
    .object({
      unit: z.enum(['SECOND', 'MINUTE', 'HOUR']),
      pricePerUnitUsd: z.number().positive(),
      minimumBillableSeconds: z.number().int().min(1).max(300),
      maxRentalSeconds: z.number().int().min(1).max(604800),
    })
    .optional(),
  stateResponsePath: z.string().optional(),
  runningValues: z.array(z.string()).optional(),
  terminatedValues: z.array(z.string()).optional(),
  terminatedAtResponsePath: z.string().optional(),
});

const endpointBaseObject = z.object({
  id: z.string(),
  name: z.string().min(1, { message: 'Endpoint name is required' }),
  // 传输协议。缺省 HTTP（存量端点无此字段 → 视为 HTTP）。
  protocol: z.enum(PROTOCOLS).default('HTTP'),
  method: z.string(),
  // HTTP 端点必填 path；WS 端点不用 path（在 superRefine 里按 protocol 放宽）。
  path: z.string().optional().default(''),
  costPerCall: z.number().min(0),
  description: z.string().optional(),
  // 分组折叠展示用的文件夹标签（同名归为一组）
  group: z.string().optional(),
  billingType: z.enum(BILLING_TYPES),
  estimatedMaxTokens: z.number().optional(),
  costPerResource: z.number().min(0).optional(),
  resourceCountJsonPath: z.string().optional(),
  estimatedMaxResources: z.number().min(1).optional(),
  headers: z.array(z.any()),
  params: z.array(z.any()),
  pathParams: z.array(z.any()),
  bodyType: z.enum(BODY_TYPES),
  bodySchema: z.any().optional(),
  bodyParams: z.array(z.any()).optional(),
  responses: z.array(z.any()),
  privateHeaders: z.array(z.any()),
  aiConfig: aiConfigSchema.optional(),
  // AI Spec System (方案 X) — see docs/plan/ai_spec_system.plan.md §6
  aiInterfaceSpecId: z.string().nullable().optional(),
  aiSpecDetectedBy: z.string().nullable().optional(),
  aiModelMappings: z
    .array(
      z.object({
        modelName: z.string(),
        canonicalId: z.string().nullable().optional(),
      }),
    )
    .optional(),
  // 非 PER_TOKEN AI 端点声明的上游模型名（服务端自动映射 canonical）。
  servedModel: z.string().optional(),
  // DYNAMIC 规则构建器 UI-state；后端 validate-dynamic-rules 才是权威校验,这里放宽。
  dynamicRules: z.any().nullable().optional(),
  hidden: z.boolean().optional(),
  userOAuthProviderId: z.string().optional(),
  asyncMode: z.enum(ASYNC_MODES).default('NONE'),
  // V2 — mirrors backend validateEndpointTaskConfig (api.service.ts:73-150)
  taskStrategy: z.enum(TASK_STRATEGIES).default('WORKER_BLOCKING'),
  upstreamProvider: z.string().optional(),
  fixedHoldAmount: z.number().min(0.01).max(FIXED_HOLD_MAX_USD).optional(),
  // WebSocket（protocol==='WEBSOCKET'）。字段形状在此，必填/协议规则在 superRefine。
  wsConfig: wsConfigSchema,
  wsBilling: wsBillingSchema,
  // 具体结构只在 TIME_RENTAL 分支校验。这里保留 unknown，是为了让普通端点即使
  // 带着旧版本遗留的 rentalConfig，也不会被一个不可见字段拦住保存。
  rentalConfig: z.unknown().optional(),
  sandboxConfig: z.unknown().optional().nullable(),
});

// 端点 schema 工厂。requireAiModels=false（草稿编辑）跳过 AI 完整性校验
// （servedModel / PER_TOKEN modelPricing）—— 草稿可缺模型边配边存,完整性在
// 提交审核 / 上架(ACTIVE)时收口。结构性规则(UPSTREAM_POLLING / fixedHoldAmount 等)始终生效。
const makeEndpointSchema = (requireAiModels = true) =>
  endpointBaseObject.superRefine((ep, ctx) => {
    // ── WebSocket 端点：走独立校验分支，跳过 HTTP 的 path / billing / AI 规则 ──
    // 至少 upstreamUrl 恒必填；adapter + wsBilling.perMinuteUsd 上架前必填（草稿态放宽，
    // 让 provider 边配边存，与 AI 端点同款语义）。
    if (ep.protocol === 'WEBSOCKET') {
      const ws = ep.wsConfig || {};
      const url = (ws.upstreamUrl || '').trim();
      if (!url) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['wsConfig', 'upstreamUrl'],
          message: 'Upstream URL is required for WebSocket endpoints.',
        });
      } else if (!/^wss?:\/\//i.test(url)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['wsConfig', 'upstreamUrl'],
          message: 'Upstream URL must start with wss:// or ws://.',
        });
      }
      if (requireAiModels) {
        if (!(ws.adapter && String(ws.adapter).trim())) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['wsConfig', 'adapter'],
            message: 'Adapter is required for WebSocket endpoints.',
          });
        }
        const kind = ep.wsBilling?.kind || 'duration';
        if (kind === 'duration') {
          const perMin = ep.wsBilling?.perMinuteUsd;
          if (perMin == null || !(Number(perMin) >= 0)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['wsBilling', 'perMinuteUsd'],
              message: 'Per-minute price is required for duration billing.',
            });
          }
        } else if (kind === 'realtime-token') {
          // realtime-token：至少配一档单价（混合或模态），否则该端点变成免费（bug）。
          const b = ep.wsBilling || {};
          const anyPrice = [
            'inputTokenUsd',
            'outputTokenUsd',
            'inputTextTokenUsd',
            'inputAudioTokenUsd',
            'inputCachedTokenUsd',
            'outputTextTokenUsd',
            'outputAudioTokenUsd',
          ].some((k) => Number(b[k]) > 0);
          if (!anyPrice) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['wsBilling', 'outputTextTokenUsd'],
              message:
                'At least one token price is required for realtime-token billing.',
            });
          }
        }
      }
      return;
    }
    // ── HTTP 端点：path 必填（WS 分支已放行，故这里补 HTTP 专属校验）──
    if (!(ep.path && String(ep.path).trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['path'],
        message: 'Path is required',
      });
    }
    if (ep.billingType === 'SANDBOX') {
      // 派生行（xAPI 按能力自动生成的 /terminate、/commands…）不带 sandboxConfig，
      // 也不该被要求配置：整个服务只有创建接口那一条承载配置。
      if (!ep.sandboxConfig && String(ep.path || '').trim()) return;
      // Sandbox 端点 = Adapter 接入声明。深度结构交给 sandboxConfigSchema（与服务端
      // SandboxProviderService.prepareOfferings 同一份语义），这里只把 issue 挂到端点路径下。
      const parsed = sandboxConfigSchema.safeParse(ep.sandboxConfig);
      if (!parsed.success) {
        parsed.error.issues.forEach((issue) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sandboxConfig', ...issue.path],
            message: issue.message,
          });
        });
      } else {
        const secret = String(ep.sandboxConfig?.webhookSecret || '').trim();
        if (!ep.sandboxConfig?.webhookConfigured && secret.length < 16) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sandboxConfig', 'webhookSecret'],
            message: '首次配置请填写至少 16 个字符的 Webhook HMAC 密钥。',
          });
        }
      }
      if (ep.protocol !== 'HTTP' || ep.aiConfig?.supportsStreaming) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['billingType'],
          message: 'Sandbox 接入仅支持非流式 HTTP 端点。',
        });
      }
      return;
    }
    if (ep.billingType === 'TIME_RENTAL') {
      if (!ep.rentalConfig) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['rentalConfig'],
          message: '请配置租赁计费。',
        });
      } else {
        const parsedRental = rentalConfigSchema.safeParse(ep.rentalConfig);
        if (!parsedRental.success) {
          parsedRental.error.issues.forEach((issue) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['rentalConfig', ...issue.path],
              message: issue.message,
            });
          });
          return;
        }
        const cfg = parsedRental.data;
        if (ep.protocol !== 'HTTP' || ep.aiConfig?.supportsStreaming) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['billingType'],
            message: '租赁计费仅支持非流式 HTTP 接口。',
          });
        }
        if (ep.asyncMode !== 'NONE' || ep.taskStrategy !== 'WORKER_BLOCKING') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['taskStrategy'],
            message: '租赁计费使用平台租赁监控，不能使用异步任务轮询。',
          });
        }
        if (ep.userOAuthProviderId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['userOAuthProviderId'],
            message: '租赁计费暂不支持消费者 OAuth。',
          });
        }
        if (cfg.role === 'CREATE') {
          if (!(cfg.resourceIdResponsePath || '').trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['rentalConfig', 'resourceIdResponsePath'],
              message: '请填写资源 ID 的响应路径。',
            });
          }
          if (!cfg.pricing) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['rentalConfig', 'pricing'],
              message: '请配置租赁价格。',
            });
          } else if (
            cfg.pricing.maxRentalSeconds < cfg.pricing.minimumBillableSeconds
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['rentalConfig', 'pricing', 'maxRentalSeconds'],
              message: '最长租赁时间不能小于最低计费秒数。',
            });
          }
        }
        if (cfg.role === 'TERMINATE' || cfg.role === 'STATUS') {
          if (!(cfg.resourceIdInput?.path || '').trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['rentalConfig', 'resourceIdInput', 'path'],
              message: '请填写资源 ID 注入字段。',
            });
          } else if (
            cfg.resourceIdInput?.location === 'PATH' &&
            !String(ep.path || '').includes(`:${cfg.resourceIdInput.path}`) &&
            !String(ep.path || '').includes(`{${cfg.resourceIdInput.path}}`)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['rentalConfig', 'resourceIdInput', 'path'],
              message: 'PATH 字段必须对应接口路径中的 :参数 或 {参数}。',
            });
          }
        }
        if (cfg.role === 'STATUS') {
          if (!(cfg.stateResponsePath || '').trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['rentalConfig', 'stateResponsePath'],
              message: '请填写资源状态响应路径。',
            });
          }
          if (!cfg.runningValues?.some((v) => String(v).trim())) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['rentalConfig', 'runningValues'],
              message: '请至少填写一个运行中状态值。',
            });
          }
          if (!cfg.terminatedValues?.some((v) => String(v).trim())) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['rentalConfig', 'terminatedValues'],
              message: '请至少填写一个已终止状态值。',
            });
          }
        }
      }
    }
    // Mirror of backend rules so the user sees errors instantly rather than waiting for a 400.
    if (ep.taskStrategy === 'UPSTREAM_POLLING') {
      // R1: billingType ∈ PER_TOKEN / PER_CALL / DYNAMIC（PER_RESOURCE/TIME_RENTAL 不支持）。
      // DYNAMIC = 异步 cost-passthrough（如 OpenRouter 视频，结算读上游 usage.cost）。
      if (
        ep.billingType !== 'PER_TOKEN' &&
        ep.billingType !== 'PER_CALL' &&
        ep.billingType !== 'DYNAMIC'
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['billingType'],
          message:
            'UPSTREAM_POLLING supports only PER_TOKEN, PER_CALL or DYNAMIC.',
        });
      }
      // R1b: DYNAMIC + UPSTREAM_POLLING 需 dynamicRules（结算读它）+ fixedHoldAmount（异步预扣占位）。
      if (ep.billingType === 'DYNAMIC') {
        if (!ep.dynamicRules) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dynamicRules'],
            message:
              'DYNAMIC with UPSTREAM_POLLING requires dynamicRules to be configured.',
          });
        }
        if (ep.fixedHoldAmount == null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['fixedHoldAmount'],
            message:
              'DYNAMIC with UPSTREAM_POLLING requires fixedHoldAmount to be set.',
          });
        }
      }
      // R2: upstreamProvider required, from the known list
      if (!ep.upstreamProvider) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['upstreamProvider'],
          message: 'Upstream provider is required for UPSTREAM_POLLING.',
        });
      } else if (!KNOWN_UPSTREAM_PROVIDERS.includes(ep.upstreamProvider)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['upstreamProvider'],
          message: `Unknown provider. Supported: ${KNOWN_UPSTREAM_PROVIDERS.join(', ')}`,
        });
      }
      // R3: PER_CALL → costPerCall must be > 0
      if (
        ep.billingType === 'PER_CALL' &&
        (!ep.costPerCall || ep.costPerCall <= 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['costPerCall'],
          message: 'PER_CALL with UPSTREAM_POLLING requires costPerCall > 0.',
        });
      }
    } else {
      // R5: WORKER_BLOCKING + fixedHoldAmount = 400 on the backend
      if (ep.fixedHoldAmount != null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fixedHoldAmount'],
          message:
            'fixedHoldAmount is only valid for UPSTREAM_POLLING strategy.',
        });
      }
    }
    // AI 模型完整性（镜像后端 aiAutoAssociate gate）—— 仅 requireAiModels 时强制（上架前）。
    // 草稿编辑不卡这里：可缺模型边配边存，提交审核 / ACTIVE 写入时再收口。
    if (requireAiModels) {
      // PER_TOKEN：模型来自 tokenPricing 的键，必须 ≥1 行（pertoken 区别处理）。
      if (
        ep.billingType === 'PER_TOKEN' &&
        (!ep.aiConfig?.modelPricing || ep.aiConfig.modelPricing.length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['aiConfig', 'modelPricing'],
          message: 'PER_TOKEN endpoints require at least one model price.',
        });
      }
      // 已识别为 AI 的非 PER_TOKEN 端点（如图像生成）必须声明 servedModel。
      if (
        ep.aiInterfaceSpecId &&
        ep.billingType !== 'PER_TOKEN' &&
        !(ep.servedModel && String(ep.servedModel).trim())
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['servedModel'],
          message: 'At least one served model is required for AI endpoints.',
        });
      }
    }
  });

const endpointSchema = makeEndpointSchema(true);

export const apiFormBaseSchema = z.object({
  name: z.string().min(1, { message: 'Service name is required' }),
  description: z.string().optional(),
  // Optional homepage; empty clears. Loose URL check (backend re-validates public URL).
  website: z
    .union([z.literal(''), z.string().url({ message: 'Must be a valid URL' })])
    .optional(),
  // Long-form About markdown (detail About tab). Market cards use short description.
  aboutMarkdown: z
    .string()
    .max(100_000, { message: 'About doc is too long (max 100k chars)' })
    .optional(),
  logoUrl: z.string().optional(),
  category: z.string(),
  host: z.string().optional(),
  // 草稿态允许空（骨架草稿尚未配上游）；非空则必须是合法 URL。提交审核由后端收口。
  baseUrl: z.union([
    z.literal(''),
    z.string().url({ message: 'Must be a valid URL' }),
  ]),
  // 多上游环境（default 之外的命名环境）。default = baseUrl，不在这里列。
  baseUrls: z
    .array(
      z.object({
        name: z
          .string()
          .regex(/^[a-zA-Z0-9_-]+$/, { message: '环境名只能含字母/数字/-/_' }),
        url: z.union([
          z.literal(''),
          z.string().url({ message: 'Must be a valid URL' }),
        ]),
      }),
    )
    .optional(),
  isPublic: z.boolean(),
  accessMode: z.enum(['PROXY', 'DIRECT']).default('PROXY'),
  rateLimitConfig: z
    .object({
      requests: z
        .number()
        .int()
        .min(1, { message: 'Minimum is 1 request' })
        .max(1_000_000, { message: 'Maximum is 1,000,000 requests' }),
      periodSeconds: z.number().int().min(1).max(86_400),
    })
    .nullable()
    .optional(),
  integrationMode: z
    .enum(['HTTP_PROXY', 'SANDBOX_PROVIDER_V1'])
    .default('HTTP_PROXY'),
  billingType: z.enum(BILLING_TYPES),
  authType: z.enum(AUTH_TYPES),
  authConfig: z.object({
    key: z.string().optional(),
    value: z.string().optional(),
  }),
  privateHeaders: z.array(z.object({ key: z.string(), value: z.string() })),
  openApiSpec: z.object({ spec: z.record(z.string(), z.unknown()) }).optional(),
  // Sandbox 配置只在选择 SANDBOX_PROVIDER_V1 时生效。普通 HTTP 服务不应被
  // 一个尚未填写的 Sandbox offering 阻塞保存或提交。
  sandboxConfig: z.unknown(),
});

const withRentalGroupValidation = (schema) =>
  schema.superRefine((form, ctx) => {
    // 端点上有 SANDBOX 配置时，那条端点才是真相源（后端也只读它来物化），顶层
    // form.sandboxConfig 只是 binding 当前状态的只读回显。
    //
    // 继续校验顶层会造成一个无解的死锁：顶层副本要等 binding 更新，binding 要等保存
    // 成功，而校验不过就保存不了。更糟的是顶层 sandboxConfig 在页面上没有任何 UI，
    // 服务商只看到「有 1 个字段需要修正」却找不到是哪个字段，在端点里改对也没用。
    const authoredOnEndpoint = (form.endpoints || []).some(
      (endpoint) =>
        endpoint?.billingType === 'SANDBOX' &&
        endpoint?.sandboxConfig &&
        typeof endpoint.sandboxConfig === 'object',
    );
    if (form.integrationMode === 'SANDBOX_PROVIDER_V1' && !authoredOnEndpoint) {
      const parsed = sandboxConfigSchema.safeParse(form.sandboxConfig);
      if (!parsed.success) {
        parsed.error.issues.forEach((issue) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sandboxConfig', ...issue.path],
            message: issue.message,
          });
        });
        return;
      }

      const secret = String(form.sandboxConfig?.webhookSecret || '').trim();
      if (!form.sandboxConfig?.webhookConfigured && secret.length < 16) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sandboxConfig', 'webhookSecret'],
          message: '首次配置请填写至少 16 个字符的 Webhook HMAC 密钥。',
        });
      }
    }
    const groups = new Map();
    (form.endpoints || []).forEach((ep, index) => {
      if (
        ep.billingType !== 'TIME_RENTAL' ||
        !ep.rentalConfig?.rentalKey ||
        !ep.rentalConfig?.role
      )
        return;
      const key = ep.rentalConfig.rentalKey;
      const group = groups.get(key) || {
        CREATE: [],
        TERMINATE: [],
        STATUS: [],
      };
      group[ep.rentalConfig.role].push(index);
      groups.set(key, group);
    });
    groups.forEach((group) => {
      ['CREATE', 'TERMINATE', 'STATUS'].forEach((role) => {
        if (group[role].length === 1) return;
        const index =
          group[role][0] ??
          group.CREATE[0] ??
          group.TERMINATE[0] ??
          group.STATUS[0] ??
          0;
        const createIndex = group.CREATE[0];
        const groupName =
          createIndex == null
            ? '未关联创建接口的租赁计费组'
            : `租赁计费组“${rentalGroupLabel(form.endpoints[createIndex], createIndex)}”`;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['endpoints', index, 'rentalConfig', 'role'],
          message: `${groupName}必须且只能配置一个 ${role} 接口（当前 ${group[role].length} 个）。`,
        });
      });
    });
  });

export const apiFormSchema = withRentalGroupValidation(
  apiFormBaseSchema.extend({
    // 草稿态允许 0 个端点（进 workspace 后再加）；提交审核由后端收口。
    endpoints: z.array(endpointSchema),
  }),
);

// 草稿编辑用：端点跳过 AI 模型完整性校验（结构性校验仍在）。未上线(草稿/沙箱)的
// 「管理 API」用它,让用户能边配边存;上架前(submit / ACTIVE)再用严格的 apiFormSchema。
export const apiDraftFormSchema = withRentalGroupValidation(
  apiFormBaseSchema.extend({ endpoints: z.array(makeEndpointSchema(false)) }),
);

// ============ Pricing converters (display ↔ wire) ============
//
// Wire format stores per-1-token prices; UI shows per-1M-token.
// transformApiToForm multiplies by 1_000_000 so the user sees a friendly number;
// transformFormToApi divides back.

// MILLION / pricingRecordToArray / pricingArrayToRecord 已抽到
// ./workspace-sections/pricing-utils.js（供 register + openapi-merge 共用，破循环依赖）——见顶部 import。

// ============ Transforms (wire DTO ↔ form state) ============
//
// transformApiToForm: hydrate the form from a backend ApiService DTO.
//   - object-shaped maps (headers/params/pathParams/privateHeaders) → array rows for useFieldArray
//   - "key:value" authConfig string → split into {key, value}
//   - per-1-token wire prices → per-1M display prices
//
// transformFormToApi (called by builders below): the inverse.

const isUuid = (s) =>
  typeof s === 'string' && s.length >= 20 && s.includes('-');

const objectMapToRows = (obj) =>
  Object.entries(obj || {}).map(([name, spec]) => ({
    name,
    type: (spec && (spec.schema?.type || spec.type)) || 'string',
    required: spec && spec.required != null ? spec.required : false,
    description: (spec && spec.description) || '',
    value:
      (spec && (spec.example != null ? spec.example : spec.schema?.default)) ||
      '',
  }));

const rowsToObjectMap = (rows) => {
  const out = {};
  for (const r of rows || []) {
    if (!r || !r.name) continue;
    out[r.name] = {
      description: r.description || '',
      required: !!r.required,
      schema: { type: r.type || 'string' },
      example: r.value || '',
    };
  }
  return out;
};

const headersArrayToObject = (rows) => {
  const out = {};
  for (const r of rows || []) {
    if (!r || !r.key) continue;
    out[r.key] = r.value || '';
  }
  return out;
};

const objectToHeadersArray = (obj) =>
  Object.entries(obj || {}).map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : String(value ?? ''),
  }));

const parseBodySchemaInput = (input) => {
  if (input == null || input === '') return undefined;
  if (typeof input === 'object') {
    if (input.schema || input.example) return input;
    return { schema: { type: 'object' }, example: input };
  }
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return undefined;
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') {
        if (parsed.schema || parsed.example) return parsed;
        return { schema: { type: 'object' }, example: parsed };
      }
    } catch {
      /* ignore */
    }
  }
  return undefined;
};

// 把表单态序列化成持久化的 bodySchema：
//   FORM_DATA / FORM_URLENCODED → { contentType, fields:[{key,type,value,description,required?}] }
//   JSON → parseBodySchemaInput；NONE → undefined
const serializeBody = (ep) => {
  if (ep.bodyType === 'NONE') return undefined;
  if (ep.bodyType === 'FORM_DATA' || ep.bodyType === 'FORM_URLENCODED') {
    const contentType =
      ep.bodyType === 'FORM_URLENCODED'
        ? 'application/x-www-form-urlencoded'
        : 'multipart/form-data';
    const fields = (ep.bodyParams || [])
      .filter((f) => f && f.key && String(f.key).trim())
      .map((f) => ({
        key: String(f.key).trim(),
        type: f.type === 'file' ? 'file' : 'text',
        value: f.value || '',
        description: f.description || '',
        ...(f.required ? { required: true } : {}),
      }));
    return { contentType, fields };
  }
  return parseBodySchemaInput(ep.bodySchema);
};

export const transformApiToForm = (api) => {
  if (!api) return INITIAL_FORM();
  const version = api.currentVersion || api.version || null;
  // Owner sees ALL endpoints including hidden ones. Per UX spec: hidden
  // endpoints are visually distinguished in the list (opacity + eye-off
  // icon) so the creator can tell apart "exposed to marketplace" vs
  // "hidden". Backend has already filtered hidden out for non-owner
  // viewers via findOne#safeEndpoints; this page is owner-only.
  //
  // Delete vs hide are DISTINCT (deletedAt is separate from the hidden
  // flag). Clicking the trash icon (removeEndpoint) drops a row from the
  // RHF form array. On save, the backend treats a missing endpoint as a
  // delete: endpoints with no traffic are hard-deleted; endpoints that
  // already have usage_records are soft-deleted via deletedAt (row kept
  // for billing/audit history, but excluded from every read path). Either
  // way the endpoint is GONE after reload — it does NOT reappear as a
  // hidden row. To merely unlist an endpoint, toggle `hidden` instead.
  const rawEndpoints = (version && version.endpoints) || api.endpoints || [];
  const endpoints = rawEndpoints;

  const authType = (version && version.authType) || api.authType || 'NONE';
  const serverAuthConfig =
    (version && version.authConfig) || api.authConfig || '';
  // 新版 authConfig 是无损 JSON（BEARER {v,token} / HEADER {v,headers} / QUERY {v,query}）；
  // 老式是有损分隔串（BEARER 裸 token / HEADER|QUERY "k:v"）。加载端两者都要认。
  let parsedAuthJson = null;
  try {
    const o = JSON.parse(serverAuthConfig);
    if (o && typeof o === 'object') parsedAuthJson = o;
  } catch {
    /* legacy 串 */
  }

  let authConfig = { key: '', value: '' };
  if (authType === 'BEARER') {
    // 单 token 输入框：新格式取 .token（避免显示整个 JSON 碎片），老格式用原串（读取端剥 Bearer 前缀自愈）。
    authConfig = {
      key: '',
      value:
        parsedAuthJson && typeof parsedAuthJson.token === 'string'
          ? parsedAuthJson.token
          : serverAuthConfig || '',
    };
  } else if (authType === 'HEADER' || authType === 'QUERY') {
    if (parsedAuthJson) {
      // 多头/多参：真凭据展示在「私有请求头」编辑器，单头字段留空（不塞 JSON 碎片、不诱导误编辑）。
      authConfig = { key: '', value: '' };
    } else {
      // 老式 "k:v" 串：回填单头字段（兼容存量单头服务）。
      const idx = (serverAuthConfig || '').indexOf(':');
      if (idx >= 0)
        authConfig = {
          key: serverAuthConfig.slice(0, idx),
          value: serverAuthConfig.slice(idx + 1),
        };
      else authConfig = { key: serverAuthConfig || '', value: '' };
    }
  }

  // 「私有请求头」编辑器 = HEADER/QUERY 多头凭据的源真相。老服务凭据可能只在 authConfig 里
  // （version.privateHeaders 为空，如 agent 注册 / 在跑修订）→ 从 authConfig 的 headers/query 回填，
  // 让它可见可编辑，且保存时凭 privateHeaders 无损重派生（避免"编辑器空+单头空 → 保存丢鉴权"）。
  let privateHeadersObj =
    (version && version.privateHeaders) || api.privateHeaders || {};
  const phEmpty =
    !privateHeadersObj || Object.keys(privateHeadersObj).length === 0;
  if (
    phEmpty &&
    parsedAuthJson &&
    (authType === 'HEADER' || authType === 'QUERY')
  ) {
    const src =
      authType === 'HEADER' ? parsedAuthJson.headers : parsedAuthJson.query;
    if (src && typeof src === 'object') privateHeadersObj = src;
  }
  const privateHeaders = objectToHeadersArray(privateHeadersObj);

  // 多上游环境：default 即 baseUrl，单独的命名环境放进 baseUrls 数组（排除 default）
  const versionBaseUrls = (version && version.baseUrls) || api.baseUrls || null;
  const extraBaseUrls =
    versionBaseUrls && typeof versionBaseUrls === 'object'
      ? Object.entries(versionBaseUrls)
          .filter(([k]) => k !== 'default')
          .map(([name, url]) => ({ name, url: url || '' }))
      : [];

  // Derive a service-level billingType from the dominant endpoint billing type — purely a UI hint.
  const serviceBillingType = endpoints.some(
    (ep) => ep.billingType === 'TIME_RENTAL',
  )
    ? 'TIME_RENTAL'
    : endpoints.some((ep) => ep.billingType === 'PER_TOKEN')
      ? 'PER_TOKEN'
      : endpoints.some((ep) => ep.billingType === 'PER_RESOURCE')
        ? 'PER_RESOURCE'
        : 'PER_CALL';

  // Strip the gateway suffix from the host so the user just sees their prefix.
  // Backend stores e.g. "weather-pro.p.xapi.to" — we want "weather-pro" in the prefix input.
  const rawHost = api.host || '';
  const hostPrefix =
    rawHost
      .replace(/\.p\.xapi\.to$/i, '')
      .replace(/^https?:\/\//i, '')
      .split('.')[0] || '';

  return {
    name: api.name || '',
    description: api.description || '',
    website: api.website || '',
    aboutMarkdown: api.aboutMarkdown || '',
    logoUrl: api.logoUrl || '',
    category: api.category || CATEGORIES[0],
    host: hostPrefix,
    baseUrl: (version && version.baseUrl) || api.baseUrl || '',
    baseUrls: extraBaseUrls,
    openApiSpec: toOpenApiSpecInput(version?.openApiSpec),
    isPublic: api.isPublic != null ? !!api.isPublic : true,
    accessMode: api.accessMode || 'PROXY',
    rateLimitConfig:
      api.rateLimitConfig && typeof api.rateLimitConfig === 'object'
        ? {
            requests: Number(api.rateLimitConfig.requests),
            periodSeconds: Number(api.rateLimitConfig.periodSeconds),
          }
        : null,
    integrationMode:
      api.integrationMode ||
      (api.sandboxBinding ? 'SANDBOX_PROVIDER_V1' : 'HTTP_PROXY'),
    billingType: serviceBillingType,
    authType,
    authConfig,
    privateHeaders,
    sandboxConfig: transformSandboxBindingToForm(api.sandboxBinding),
    // 骨架草稿(0 端点)不再种空 stub —— schema 已允许空 endpoints，避免空 stub 的
    // name/path 必填校验挡住「只想先存上游 baseUrl」。空时列表显示空状态 + 新增按钮。
    endpoints: endpoints.map((ep, idx) => {
      const epPrivateHeaders = objectToHeadersArray(ep.privateHeaders);
      // 承载配置的 Sandbox 端点要拿到已验证 binding（含 manifest），表单才能显示
      // 「从 Adapter 选规格」并按能力收窄字段；派生行保持 null。
      const sandboxConfigForEndpoint =
        ep.billingType === 'SANDBOX' && ep.sandboxConfig
          ? transformSandboxBindingToForm(api.sandboxBinding)
          : null;

      const headers = Array.isArray(ep.headers)
        ? ep.headers
        : objectMapToRows(ep.headers);
      const params = Array.isArray(ep.params)
        ? ep.params
        : objectMapToRows(ep.params);
      const pathParams = Array.isArray(ep.pathParams)
        ? ep.pathParams
        : objectMapToRows(ep.pathParams);

      // responses: prefer the array form; fall back to legacy successSchema/errorSchema/responseSchema if needed.
      let responses = Array.isArray(ep.responses) ? [...ep.responses] : [];
      if (responses.length === 0) {
        const tryParse = (raw) => {
          if (raw == null) return null;
          if (typeof raw === 'string') {
            const t = raw.trim();
            if (!t) return null;
            try {
              return JSON.parse(t);
            } catch {
              return raw;
            }
          }
          return raw;
        };
        const ok = tryParse(
          ep.successSchema ||
            (ep.responseSchema &&
              (ep.responseSchema['200']?.schema || ep.responseSchema['200'])),
        );
        const bad = tryParse(
          ep.errorSchema ||
            (ep.responseSchema &&
              (ep.responseSchema['400']?.schema || ep.responseSchema['400'])),
        );
        if (ok)
          responses.push({
            status_code: 200,
            label: 'success',
            type: 'json',
            body: ok,
          });
        if (bad)
          responses.push({
            status_code: 400,
            label: 'error',
            type: 'json',
            body: bad,
          });
      }
      if (responses.length === 0) {
        responses = [
          {
            status_code: 200,
            label: 'success',
            type: 'json',
            body: { message: 'Success' },
          },
          {
            status_code: 400,
            label: 'error',
            type: 'json',
            body: { error: 'Bad Request' },
          },
        ];
      }

      const bodySchemaIn = ep.bodySchema;
      const bodyCt =
        bodySchemaIn && typeof bodySchemaIn === 'object'
          ? bodySchemaIn.contentType
          : undefined;
      // legacy 兼容：旧版直连导入把 multipart 存成 {schema:{properties:{file:{format:binary}}}}，
      // 没有 contentType/fields 标记。若 schema 里有 format:binary / type:file 字段，按 form-data 回收。
      const legacyProps =
        bodySchemaIn &&
        typeof bodySchemaIn === 'object' &&
        bodySchemaIn.schema &&
        typeof bodySchemaIn.schema === 'object'
          ? bodySchemaIn.schema.properties
          : undefined;
      const legacyIsForm =
        legacyProps &&
        typeof legacyProps === 'object' &&
        Object.values(legacyProps).some(
          (p) => p && (p.format === 'binary' || p.type === 'file'),
        );
      const isFormBody =
        bodyCt === 'multipart/form-data' ||
        bodyCt === 'application/x-www-form-urlencoded' ||
        (bodySchemaIn &&
          typeof bodySchemaIn === 'object' &&
          Array.isArray(bodySchemaIn.fields)) ||
        legacyIsForm;
      let loadedBodyType = 'NONE';
      let bodySchemaStr = '';
      let bodyParams = [];
      if (isFormBody) {
        loadedBodyType =
          bodyCt === 'application/x-www-form-urlencoded'
            ? 'FORM_URLENCODED'
            : 'FORM_DATA';
        if (Array.isArray(bodySchemaIn.fields)) {
          bodyParams = bodySchemaIn.fields.map((f) => ({
            key: f.key || '',
            type: f.type || 'text',
            value: f.value || '',
            description: f.description || '',
            required: !!f.required,
          }));
        } else if (legacyProps) {
          const req = Array.isArray(bodySchemaIn.schema.required)
            ? bodySchemaIn.schema.required
            : [];
          bodyParams = Object.entries(legacyProps).map(([name, def]) => ({
            key: name,
            type:
              def && (def.format === 'binary' || def.type === 'file')
                ? 'file'
                : 'text',
            value: def && def.example != null ? String(def.example) : '',
            description:
              def && typeof def.description === 'string' ? def.description : '',
            required: req.includes(name),
          }));
        }
      } else {
        const hasBody =
          bodySchemaIn &&
          (typeof bodySchemaIn === 'object'
            ? Object.keys(bodySchemaIn).length > 0
            : !!bodySchemaIn);
        if (hasBody) {
          loadedBodyType = 'JSON';
          const example =
            bodySchemaIn &&
            typeof bodySchemaIn === 'object' &&
            bodySchemaIn.example
              ? bodySchemaIn.example
              : bodySchemaIn;
          try {
            bodySchemaStr = JSON.stringify(example, null, 2);
          } catch {
            bodySchemaStr = '';
          }
        }
      }

      const aiConfig = DEFAULT_AI_CONFIG();
      if (ep.tokenJsonPaths) {
        aiConfig.tokenJsonPaths = {
          ...aiConfig.tokenJsonPaths,
          ...ep.tokenJsonPaths,
        };
      }
      if (ep.supportsStreaming != null)
        aiConfig.supportsStreaming = !!ep.supportsStreaming;
      if (ep.streamFormat) aiConfig.streamFormat = ep.streamFormat;
      if (ep.tokenPricing && Object.keys(ep.tokenPricing).length > 0) {
        aiConfig.modelPricing = pricingRecordToArray(ep.tokenPricing);
      }

      // WS 端点回填：protocol 缺省 HTTP（存量端点无此字段）。wsConfig/wsBilling 存在则透传，
      // 补齐 UI 需要的缺省形状（subprotocols 数组、billing.kind），数值字段保持原样（可 undefined）。
      const protocol = ep.protocol === 'WEBSOCKET' ? 'WEBSOCKET' : 'HTTP';
      const wsIn =
        ep.wsConfig && typeof ep.wsConfig === 'object' ? ep.wsConfig : null;
      const wsConfig = wsIn
        ? {
            ...DEFAULT_WS_CONFIG(),
            ...wsIn,
            subprotocols: Array.isArray(wsIn.subprotocols)
              ? wsIn.subprotocols
              : [],
          }
        : DEFAULT_WS_CONFIG();
      const wsBillingIn =
        ep.wsBilling && typeof ep.wsBilling === 'object' ? ep.wsBilling : null;
      // 数值兜底：字符串/Decimal → number，保留 0，其余 → undefined。
      const wsNum = (v) => {
        if (v == null || v === '') return undefined;
        const n = typeof v === 'string' ? parseFloat(v) : v;
        return Number.isFinite(n) ? n : undefined;
      };
      // kind 与全部计费字段都回填（含 realtime-token 的模态分档/混合价），
      // 否则打开 realtime 端点会被误显示成 duration，且保存时（见 buildWsBillingWire）clobber。
      const wsBilling = wsBillingIn
        ? {
            kind:
              wsBillingIn.kind === 'realtime-token'
                ? 'realtime-token'
                : 'duration',
            perMinuteUsd: wsNum(wsBillingIn.perMinuteUsd),
            holdUsd: wsNum(wsBillingIn.holdUsd),
            inputTokenUsd: wsNum(wsBillingIn.inputTokenUsd),
            outputTokenUsd: wsNum(wsBillingIn.outputTokenUsd),
            inputTextTokenUsd: wsNum(wsBillingIn.inputTextTokenUsd),
            inputAudioTokenUsd: wsNum(wsBillingIn.inputAudioTokenUsd),
            inputCachedTokenUsd: wsNum(wsBillingIn.inputCachedTokenUsd),
            outputTextTokenUsd: wsNum(wsBillingIn.outputTextTokenUsd),
            outputAudioTokenUsd: wsNum(wsBillingIn.outputAudioTokenUsd),
          }
        : DEFAULT_WS_BILLING();
      const rentalIn =
        ep.billingType === 'TIME_RENTAL' &&
        ep.rentalConfig &&
        typeof ep.rentalConfig === 'object'
          ? ep.rentalConfig
          : null;
      const rentalConfig = rentalIn
        ? {
            ...DEFAULT_RENTAL_CONFIG(),
            ...rentalIn,
            resourceIdInput: {
              ...DEFAULT_RENTAL_CONFIG().resourceIdInput,
              ...(rentalIn.resourceIdInput || {}),
            },
            pricing: {
              ...DEFAULT_RENTAL_CONFIG().pricing,
              ...(rentalIn.pricing || {}),
              pricePerUnitUsd: wsNum(rentalIn.pricing?.pricePerUnitUsd) ?? 1,
              minimumBillableSeconds:
                wsNum(rentalIn.pricing?.minimumBillableSeconds) ?? 60,
              maxRentalSeconds:
                wsNum(rentalIn.pricing?.maxRentalSeconds) ?? 86400,
            },
            runningValues: Array.isArray(rentalIn.runningValues)
              ? rentalIn.runningValues
              : ['running'],
            terminatedValues: Array.isArray(rentalIn.terminatedValues)
              ? rentalIn.terminatedValues
              : ['terminated', 'stopped'],
          }
        : // 非租赁端点不要塞默认租赁配置：DEFAULT_RENTAL_CONFIG 的 rentalKey 是空串，
          // 而 schema 要求它匹配正则，于是每个后端来的端点都会报「请选择所属租赁计费组」
          // —— Sandbox 的 14 条派生端点会一次报 14 条，保存被彻底挡死。
          // rentalConfig 本就是 optional，TIME_RENTAL 的必填由 superRefine 负责。
          ep.billingType === 'TIME_RENTAL'
          ? DEFAULT_RENTAL_CONFIG()
          : undefined;

      return {
        id: ep.id || `e${idx + 1}`,
        // 跨修订稳定逻辑键：原样带着，保证改 path 时身份不变（diff 显示「修改」而非删+增）。
        endpointKey: ep.endpointKey || undefined,
        name: ep.name || '',
        protocol,
        method: ep.method || 'GET',
        path: ep.path || '',
        wsConfig,
        wsBilling,
        group: ep.group || '',
        costPerCall:
          typeof ep.costPerCall === 'string'
            ? parseFloat(ep.costPerCall) || 0
            : ep.costPerCall || 0,
        description: ep.description || '',
        headers,
        params,
        pathParams,
        bodyType: loadedBodyType,
        bodySchema: bodySchemaStr,
        bodyParams,
        responses,
        billingType: ep.billingType || 'PER_CALL',
        estimatedMaxTokens: ep.estimatedMaxTokens || 1000,
        costPerResource:
          typeof ep.costPerResource === 'string'
            ? parseFloat(ep.costPerResource) || 0
            : ep.costPerResource || 0,
        resourceCountJsonPath: ep.resourceCountJsonPath || '',
        estimatedMaxResources: ep.estimatedMaxResources || 10,
        privateHeaders: epPrivateHeaders,
        hidden: !!ep.hidden,
        userOAuthProviderId: ep.userOAuthProviderId || undefined,
        asyncMode: ASYNC_MODES.includes(ep.asyncMode) ? ep.asyncMode : 'NONE',
        taskStrategy: TASK_STRATEGIES.includes(ep.taskStrategy)
          ? ep.taskStrategy
          : 'WORKER_BLOCKING',
        upstreamProvider: ep.upstreamProvider || undefined,
        fixedHoldAmount:
          typeof ep.fixedHoldAmount === 'string'
            ? parseFloat(ep.fixedHoldAmount) || undefined
            : (ep.fixedHoldAmount ?? undefined),
        aiConfig,
        // AI Spec System (方案 X)。回填完整 spec（侧栏 ⚡ 徽章等沿用此字段）；
        // 「是否手动声明」由 aiSpecDetectedBy==='manual' 判别（wire/控件据它，
        // 避免把自动识别值当手动声明回发）。
        aiInterfaceSpecId: ep.aiInterfaceSpecId || null,
        aiSpecDetectedBy: ep.aiSpecDetectedBy || null,
        aiModelMappings: Array.isArray(ep.aiModelMappings)
          ? ep.aiModelMappings
          : [],
        // 非 PER_TOKEN AI 端点回显已声明的模型(从所有映射的 modelName 反推,逗号分隔)
        servedModel:
          ep.billingType !== 'PER_TOKEN' && Array.isArray(ep.aiModelMappings)
            ? ep.aiModelMappings
                .map((m) => m && m.modelName)
                .filter(Boolean)
                .join(', ')
            : '',
        // DYNAMIC: 把已存的引擎 config 反序列化回构建器 UI-state(无损回显)。
        dynamicRules:
          ep.dynamicRules && typeof ep.dynamicRules === 'object'
            ? deserializeRules(ep.dynamicRules)
            : null,
        rentalConfig,
        sandboxConfig: sandboxConfigForEndpoint,
      };
    }),
  };
};

// ============ Submit payload builders ============

// Auth on the wire is a single string. (xapi-new RegisterPage:1335 same encoding.)
const buildAuthConfigString = (authType, authConfig) => {
  if (authType === 'BEARER') return authConfig?.value || '';
  if (authType === 'HEADER' || authType === 'QUERY') {
    const k = authConfig?.key || '';
    const v = authConfig?.value || '';
    // 单头字段空 → 不发（凭 privateHeaders 派生；避免伪造 "Authorization:" 塞进 authConfig）。
    if (!k && !v) return '';
    const kk = k || (authType === 'QUERY' ? 'api_key' : 'Authorization');
    return `${kk}:${v}`;
  }
  return '';
};

// Form wsConfig → wire wsConfig：剥空串/空数组/undefined，保留已填的数值与字符串。
const buildWsConfigWire = (wsConfig) => {
  const ws = wsConfig || {};
  const out = {};
  const str = (v) =>
    v != null && String(v).trim() !== '' ? String(v).trim() : undefined;
  const num = (v) => {
    if (v == null || v === '') return undefined;
    const n = typeof v === 'string' ? parseFloat(v) : v;
    return Number.isFinite(n) ? n : undefined;
  };
  if (str(ws.adapter) !== undefined) out.adapter = str(ws.adapter);
  if (str(ws.upstreamUrl) !== undefined) out.upstreamUrl = str(ws.upstreamUrl);
  if (str(ws.clientPath) !== undefined) out.clientPath = str(ws.clientPath);
  const subs = Array.isArray(ws.subprotocols)
    ? ws.subprotocols.map((s) => String(s).trim()).filter(Boolean)
    : [];
  if (subs.length) out.subprotocols = [...new Set(subs)];
  for (const k of [
    'idleTimeoutSec',
    'maxDurationSec',
    'maxConnPerKey',
    'heartbeatSec',
    'maxFrameBytes',
  ]) {
    const n = num(ws[k]);
    if (n !== undefined) out[k] = n;
  }
  return out;
};

// Form wsBilling → wire wsBilling：按 kind 分流。
//   duration      → perMinuteUsd 必带（数字）+ holdUsd 选带。
//   realtime-token → holdUsd + 模态分档价 + 混合价，凡已填的字段透传（都选填，后端按需回退）。
// 关键：kind 必须随表单走，绝不能写死 'duration'——否则保存 realtime 端点会把 DB 打回 duration。
const buildWsBillingWire = (wsBilling) => {
  const b = wsBilling || {};
  const num = (v) => {
    if (v == null || v === '') return undefined;
    const n = typeof v === 'string' ? parseFloat(v) : v;
    return Number.isFinite(n) ? n : undefined;
  };
  const kind = b.kind === 'realtime-token' ? 'realtime-token' : 'duration';
  if (kind === 'realtime-token') {
    const out = { kind };
    for (const k of [
      'holdUsd',
      'inputTokenUsd',
      'outputTokenUsd',
      'inputTextTokenUsd',
      'inputAudioTokenUsd',
      'inputCachedTokenUsd',
      'outputTextTokenUsd',
      'outputAudioTokenUsd',
    ]) {
      const n = num(b[k]);
      if (n !== undefined) out[k] = n;
    }
    return out;
  }
  const out = { kind: 'duration', perMinuteUsd: num(b.perMinuteUsd) ?? 0 };
  const hold = num(b.holdUsd);
  if (hold !== undefined) out.holdUsd = hold;
  return out;
};

const buildRentalConfigWire = (input = {}) => {
  const cfg = { ...DEFAULT_RENTAL_CONFIG(), ...input };
  const out = {
    version: 1,
    rentalKey: String(cfg.rentalKey || '').trim(),
    role: cfg.role,
  };
  if (cfg.role === 'CREATE') {
    out.resourceIdResponsePath = String(
      cfg.resourceIdResponsePath || '',
    ).trim();
    out.pricing = {
      unit: cfg.pricing?.unit || 'HOUR',
      pricePerUnitUsd: Number(cfg.pricing?.pricePerUnitUsd),
      minimumBillableSeconds: Number(cfg.pricing?.minimumBillableSeconds),
      maxRentalSeconds: Number(cfg.pricing?.maxRentalSeconds),
    };
  }
  if (cfg.role === 'TERMINATE' || cfg.role === 'STATUS') {
    out.resourceIdInput = {
      location: cfg.resourceIdInput?.location || 'BODY',
      path: String(cfg.resourceIdInput?.path || '').trim(),
    };
  }
  if (cfg.role === 'STATUS') {
    out.stateResponsePath = String(cfg.stateResponsePath || '').trim();
    out.runningValues = (cfg.runningValues || [])
      .map(String)
      .map((v) => v.trim())
      .filter(Boolean);
    out.terminatedValues = (cfg.terminatedValues || [])
      .map(String)
      .map((v) => v.trim())
      .filter(Boolean);
    const endedPath = String(cfg.terminatedAtResponsePath || '').trim();
    if (endedPath) out.terminatedAtResponsePath = endedPath;
  }
  return out;
};

// Form endpoint → wire endpoint.
export const transformEndpointToApi = (ep, accessMode) => {
  // ── WebSocket 端点：独立组装最小 wire，不带 HTTP 的 method/billing/AI 字段 ──
  // DIRECT 模式对 WS 无意义（网关桥接需 PROXY 语义）；此处照 protocol 走 WS 分支即可。
  if (ep.protocol === 'WEBSOCKET') {
    const wire = {
      name: ep.name,
      protocol: 'WEBSOCKET',
      path:
        ep.path && String(ep.path).trim() ? String(ep.path).trim() : undefined,
      group:
        ep.group && String(ep.group).trim()
          ? String(ep.group).trim()
          : undefined,
      description: ep.description || undefined,
      hidden: !!ep.hidden,
      wsConfig: buildWsConfigWire(ep.wsConfig),
      wsBilling: buildWsBillingWire(ep.wsBilling),
    };
    if (isUuid(ep.id)) wire.id = ep.id;
    if (ep.endpointKey) wire.endpointKey = ep.endpointKey;
    Object.keys(wire).forEach((k) => wire[k] === undefined && delete wire[k]);
    return wire;
  }

  const isDirect = accessMode === 'DIRECT';
  const billingType = isDirect ? 'PER_CALL' : ep.billingType || 'PER_CALL';
  const isToken = billingType === 'PER_TOKEN';
  const isResource = billingType === 'PER_RESOURCE';
  const isDynamic = billingType === 'DYNAMIC';
  const isRental = billingType === 'TIME_RENTAL';
  const isSandbox = billingType === 'SANDBOX';

  const wire = {
    name: ep.name,
    protocol: 'HTTP',
    method: ep.method,
    path: ep.path,
    group:
      ep.group && String(ep.group).trim() ? String(ep.group).trim() : undefined,
    costPerCall:
      isDirect || isDynamic || isRental || isSandbox ? 0 : ep.costPerCall || 0,
    description: ep.description || undefined,
    billingType,
    estimatedMaxTokens: isToken ? ep.estimatedMaxTokens || 1000 : undefined,
    tokenPricing: isToken
      ? pricingArrayToRecord(ep.aiConfig?.modelPricing || [])
      : undefined,
    tokenJsonPaths: isToken ? ep.aiConfig?.tokenJsonPaths : undefined,
    costPerResource: isResource ? ep.costPerResource || 0 : undefined,
    resourceCountJsonPath: isResource
      ? ep.resourceCountJsonPath || ''
      : undefined,
    estimatedMaxResources: isResource
      ? ep.estimatedMaxResources || 10
      : undefined,
    supportsStreaming: ep.aiConfig?.supportsStreaming || false,
    streamFormat: ep.aiConfig?.supportsStreaming
      ? ep.aiConfig?.streamFormat || 'sse'
      : null,
    headers: rowsToObjectMap(ep.headers),
    params: rowsToObjectMap(ep.params),
    pathParams: rowsToObjectMap(ep.pathParams),
    bodySchema: serializeBody(ep),
    responses: ep.responses,
    privateHeaders: headersArrayToObject(ep.privateHeaders),
    hidden: !!ep.hidden,
    asyncMode: ASYNC_MODES.includes(ep.asyncMode) ? ep.asyncMode : 'NONE',
    taskStrategy: TASK_STRATEGIES.includes(ep.taskStrategy)
      ? ep.taskStrategy
      : 'WORKER_BLOCKING',
    rentalConfig: isRental ? buildRentalConfigWire(ep.rentalConfig) : undefined,
    // Sandbox 端点把整份 binding 配置随端点提交；后端物化到 binding/offering。
    sandboxConfig: isSandbox
      ? buildSandboxBindingPayload(ep.sandboxConfig || {})
      : undefined,
  };
  // UPSTREAM_POLLING: pass through the V2 fields. Backend coerces asyncMode to REQUIRED
  // server-side regardless of what we send, so we don't tamper with asyncMode here.
  if (ep.taskStrategy === 'UPSTREAM_POLLING') {
    wire.upstreamProvider = ep.upstreamProvider || undefined;
    if (ep.fixedHoldAmount != null && ep.fixedHoldAmount !== '') {
      wire.fixedHoldAmount =
        typeof ep.fixedHoldAmount === 'string'
          ? parseFloat(ep.fixedHoldAmount)
          : ep.fixedHoldAmount;
    }
  }
  if (ep.userOAuthProviderId) wire.userOAuthProviderId = ep.userOAuthProviderId;
  if (isUuid(ep.id)) wire.id = ep.id;
  // 跨修订稳定键：原样回传，让后端 copy/重建端点时沿用（改 path 仍是同一逻辑端点）。
  if (ep.endpointKey) wire.endpointKey = ep.endpointKey;

  // AI Spec：aiModelMappings 由后端自动计算，前端不发送。
  // 窄版「手动决定」仅在 detectedBy==='manual' 时回发（自动识别值不回发，后端会自动
  // 识别）。同时发 aiSpecDetectedBy='manual' 作为「我手动决定过」标记，让后端区分：
  //   - spec 非空 → 手动声明该协议；
  //   - spec 为 null → 显式「恢复为非 AI」（撤销声明）。
  if (ep.aiSpecDetectedBy === 'manual') {
    const s =
      ep.aiInterfaceSpecId && String(ep.aiInterfaceSpecId).trim()
        ? String(ep.aiInterfaceSpecId).trim()
        : null;
    wire.aiSpecDetectedBy = 'manual';
    wire.aiInterfaceSpecId = s;
  }
  // 对于非 PER_TOKEN 的 AI 端点(如图像生成),服务商只声明它服务的那个模型,
  // 服务端会自动映射到 canonical —— 用户无需处理映射这层。
  // 逗号/换行分隔的服务模型 → 去重数组(服务端各自自动映射 canonical)
  if (!isToken && ep.servedModel && String(ep.servedModel).trim()) {
    const models = String(ep.servedModel)
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (models.length) wire.servedModels = [...new Set(models)];
  }

  // DYNAMIC: compile the builder UI-state → engine DynamicRulesConfig v2. The
  // backend re-validates with the same validateRulesConfig before persisting.
  if (isDynamic && ep.dynamicRules) {
    wire.dynamicRules = compileRules(ep.dynamicRules);
  }

  // Strip undefined for cleaner payloads.
  Object.keys(wire).forEach((k) => wire[k] === undefined && delete wire[k]);
  return wire;
};

// Full payload (POST /api-services on new).
// 表单的命名环境数组 + 单 baseUrl(default) → 后端 baseUrls map { default, ...extras }。
// 无任何条目时返回 undefined（不发送，backend 不改动）。
export const buildBaseUrlsMap = (form) => {
  const map = {};
  if (form.baseUrl) map.default = form.baseUrl;
  const extras = Array.isArray(form.baseUrls) ? form.baseUrls : [];
  for (const row of extras) {
    const name = row && row.name && String(row.name).trim();
    if (name && name !== 'default') map[name] = (row.url || '').trim();
  }
  return Object.keys(map).length ? map : undefined;
};

// 纯 WS 服务（所有端点都是 WEBSOCKET）：baseUrl 是 HTTP 语义的死字段，WS 网关不读它，且后端
// baseUrl 校验只认 http/https（assertPublicUrl），填 wss:// 会被拒。→ 提交时省略 baseUrl/baseUrls，
// 由后端 resolveBaseUrlWrite 兜底为空。WS 真上游在各端点 wsConfig.upstreamUrl（走 ws 专用校验）。
export const isPureWsService = (form) => {
  const eps = Array.isArray(form.endpoints) ? form.endpoints : [];
  return eps.length > 0 && eps.every((e) => e && e.protocol === 'WEBSOCKET');
};

export const buildCreatePayload = (form) => {
  const isDirect = form.accessMode === 'DIRECT';
  const pureWs = isPureWsService(form);
  return {
    name: form.name,
    description: form.description || '',
    website: form.website || undefined,
    aboutMarkdown: form.aboutMarkdown || undefined,
    logoUrl: form.logoUrl || '',
    category: form.category,
    host: isDirect ? undefined : form.host || undefined,
    baseUrl: pureWs ? undefined : form.baseUrl || undefined,
    baseUrls: pureWs ? undefined : buildBaseUrlsMap(form),
    isPublic: !!form.isPublic,
    accessMode: form.accessMode || 'PROXY',
    rateLimitConfig: isDirect ? null : form.rateLimitConfig ?? null,
    authType: isDirect ? 'NONE' : form.authType || 'NONE',
    authConfig: isDirect
      ? ''
      : buildAuthConfigString(form.authType, form.authConfig),
    privateHeaders: isDirect ? {} : headersArrayToObject(form.privateHeaders),
    openApiSpec: form.openApiSpec,
    endpoints: (form.endpoints || []).map((ep) =>
      transformEndpointToApi(ep, form.accessMode),
    ),
  };
};

// Basic-info-only payload (PATCH /api-services/:id).
export const buildBasicPayload = (form) => {
  const isDirect = form.accessMode === 'DIRECT';
  return {
    name: form.name,
    description: form.description || '',
    // Empty string clears website / about on the backend.
    website: form.website ?? '',
    aboutMarkdown: form.aboutMarkdown ?? '',
    logoUrl: form.logoUrl || '',
    category: form.category,
    isPublic: !!form.isPublic,
    accessMode: form.accessMode || 'PROXY',
    rateLimitConfig: isDirect ? null : form.rateLimitConfig ?? null,
    host: isDirect ? undefined : form.host || undefined,
  };
};

// Version payload (PATCH /api-services/:id/versions/:versionId or POST /api-services/:id/versions).
export const buildVersionPayload = (form) => {
  const isDirect = form.accessMode === 'DIRECT';
  const pureWs = isPureWsService(form);
  return {
    baseUrl: pureWs ? undefined : form.baseUrl || undefined,
    baseUrls: pureWs ? undefined : buildBaseUrlsMap(form),
    authType: isDirect ? 'NONE' : form.authType || 'NONE',
    authConfig: isDirect
      ? ''
      : buildAuthConfigString(form.authType, form.authConfig),
    privateHeaders: isDirect ? {} : headersArrayToObject(form.privateHeaders),
    openApiSpec: form.openApiSpec,
    endpoints: (form.endpoints || []).map((ep) =>
      transformEndpointToApi(ep, form.accessMode),
    ),
  };
};

// Save payload — full body sent to PATCH /api-services/:id when state is DRAFT/SANDBOX/REJECTED/SUSPENDED.
// Backend writes everything in-place on the active version with NO version-number bump.
// Rejected by the backend if status is ACTIVE (ACTIVE must use hotfix/publish).
export const buildSavePayload = (form) => ({
  ...buildBasicPayload(form),
  ...buildVersionPayload(form),
});

// ============ The page component ============

const RegisterPage = () => {
  const { route, navigate } = XShell.useRoute();
  const { authed } = XShell.useAuth();
  const { toast } = XPrim.useToast();
  const { t } = useT();

  const editId = route.params?.editId || null;
  const isEdit = !!editId;
  const [serviceLoaded, setServiceLoaded] = React.useState(null); // raw API service when editing
  const versionId = serviceLoaded?.currentVersion?.id || null;

  const [activeSection, setActiveSection] = React.useState('overview'); // overview | origin | endpoints
  const [activeEndpointId, setActiveEndpointId] = React.useState(null);
  const [aiImportOpen, setAiImportOpen] = React.useState(false);
  // Endpoints batch-overview dialog — opened from the left sidebar tools.
  // Renders a flat table for at-a-glance review + column-header batch ops.
  // Clicking any row closes the dialog and focuses that endpoint in the
  // main master-detail editor.
  const [overviewOpen, setOverviewOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(null); // null | 'basic' | 'hotfix' | 'release' | 'create' | 'review' | 'delete' | 'parse'
  const [logoUploading, setLogoUploading] = React.useState(false);
  const [releaseNote, setReleaseNote] = React.useState('');
  // Design-system confirm dialog state: { kind: 'delete' } | { kind: 'review', form } | null
  const [confirmDialog, setConfirmDialog] = React.useState(null);
  const openapiInputRef = React.useRef(null);

  const methods = useForm({
    resolver: zodResolver(apiFormSchema),
    defaultValues: INITIAL_FORM(),
    mode: 'onBlur',
    shouldUnregister: false,
  });
  const {
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = methods;
  const formIntegrationMode = methods.watch('integrationMode');

  // Host-prefix realtime validation (advisory — backend re-checks on write).
  // Declared here (before the early `!authed` return) so the hook runs
  // unconditionally; consumed by checkAndProceed's guard + OverviewSection's inline hint.
  const formHost = methods.watch('host');
  const formAccessMode = methods.watch('accessMode');
  const isDirect = formAccessMode === 'DIRECT';
  // Grandfather: while editing, the service's own (possibly reserved) host must not
  // alarm as long as it's unchanged — mirrors the backend's validate-on-change rule.
  const originalHostPrefix =
    (serviceLoaded?.host || '')
      .replace(/\.p\.xapi\.to$/i, '')
      .replace(/^https?:\/\//i, '')
      .split('.')[0] || '';
  const hostValidation = useHostValidation(formHost, {
    excludeId: editId || undefined,
    skipValue: originalHostPrefix,
    enabled: !isDirect,
  });

  // Load existing service for edit mode.
  React.useEffect(() => {
    if (!editId || !authed) return;
    let cancelled = false;
    XApi.apiSvc
      .get(editId)
      .then((api) => {
        if (cancelled || !api) return;
        setServiceLoaded(api);
        reset(transformApiToForm(api));
        const eps = api?.currentVersion?.endpoints || api?.endpoints || [];
        if (eps[0]) setActiveEndpointId(eps[0].id || 'e1');
      })
      .catch((e) => {
        toast(e?.message || t('register.toast.loadFail'));
      });
    return () => {
      cancelled = true;
    };
  }, [editId, authed]);

  // Initial endpoint selection.
  React.useEffect(() => {
    if (activeEndpointId) return;
    const list = methods.getValues('endpoints') || [];
    if (list[0]) setActiveEndpointId(list[0].id);
  }, [activeEndpointId]);

  React.useEffect(() => {
    if (
      formIntegrationMode === 'SANDBOX_PROVIDER_V1' &&
      activeSection === 'endpoints'
    ) {
      setActiveSection('sandbox');
    }
    if (
      formIntegrationMode !== 'SANDBOX_PROVIDER_V1' &&
      activeSection === 'sandbox'
    ) {
      setActiveSection('endpoints');
    }
  }, [formIntegrationMode, activeSection]);

  // 模型→canonical 映射现在由后端自动处理,pending(canonicalId=null)由 admin
  // 在后台候选面板消化,不再在提交前拦服务商。checkAndProceed 直接提交。
  const checkAndProceed = (form, doSubmit) => {
    if (logoUploading) {
      toast({ title: t('logo.pending'), type: 'info' });
      return;
    }
    // Soft-block on a known-invalid host so the user gets an immediate reason
    // instead of a server 400 round-trip. Backend stays authoritative on write.
    if (!isDirect && hostValidation.status === 'invalid') {
      toast({ title: hostValidation.message, type: 'error' });
      setActiveSection('overview');
      return;
    }
    doSubmit();
  };

  const persistSandboxIntegration = async (apiId, form) => {
    if (form.integrationMode === 'SANDBOX_PROVIDER_V1') {
      return XApi.apiSvc.upsertSandboxBinding(
        apiId,
        buildSandboxBindingPayload(form.sandboxConfig),
      );
    }
    if (
      serviceLoaded?.integrationMode === 'SANDBOX_PROVIDER_V1' ||
      serviceLoaded?.sandboxBinding
    ) {
      return XApi.apiSvc.disableSandboxBinding(apiId);
    }
    return null;
  };

  // Submit handlers — one per branch.
  const runCreate = handleSubmit(
    async (form) => {
      checkAndProceed(form, async () => {
        setSubmitting('create');
        try {
          const created = await XApi.apiSvc.create(buildCreatePayload(form));
          if (created?.id) await persistSandboxIntegration(created.id, form);
          toast(t('register.toast.created'));
          if (created?.id)
            navigate('workspace-api', { apiId: created.id, section: 'basic' });
        } catch (e) {
          toast(e?.message || t('register.toast.create.fail'));
        } finally {
          setSubmitting(null);
        }
      });
    },
    () => toast(t('register.toast.fixFields')),
  );

  // Save — used by DRAFT / SANDBOX / REJECTED / SUSPENDED. Sends the full payload
  // (metadata + endpoints + auth + baseUrl) to PATCH /api-services/:id. Backend
  // replaces the active version in-place without bumping the version number; it
  // refuses the call if the API is ACTIVE (those must use Hotfix or Publish).
  const runSave = handleSubmit(
    async (form) => {
      if (!editId) return;
      checkAndProceed(form, async () => {
        setSubmitting('save');
        try {
          await XApi.apiSvc.updateBasic(editId, buildSavePayload(form));
          await persistSandboxIntegration(editId, form);
          toast({ title: t('register.toast.save'), type: 'success' });
          const api = await XApi.apiSvc.get(editId);
          if (api) {
            setServiceLoaded(api);
            reset(transformApiToForm(api));
          }
        } catch (e) {
          toast({
            title: e?.message || t('register.toast.save.fail'),
            type: 'error',
          });
        } finally {
          setSubmitting(null);
        }
      });
    },
    () => toast({ title: t('register.toast.fixFields'), type: 'error' }),
  );

  const runHotfix = handleSubmit(
    async (form) => {
      if (!editId || !versionId) {
        toast(t('register.toast.noVersion'));
        return;
      }
      checkAndProceed(form, async () => {
        setSubmitting('hotfix');
        try {
          await XApi.apiSvc.updateBasic(editId, buildBasicPayload(form));
          await XApi.apiSvc.updateVersion(
            editId,
            versionId,
            buildVersionPayload(form),
          );
          await persistSandboxIntegration(editId, form);
          toast(t('register.toast.hotfix'));
          navigate('workspace');
        } catch (e) {
          toast(e?.message || t('register.toast.hotfix.fail'));
        } finally {
          setSubmitting(null);
        }
      });
    },
    () => toast(t('register.toast.fixFields')),
  );

  const runRelease = handleSubmit(
    async (form) => {
      if (!editId) return;
      checkAndProceed(form, async () => {
        setSubmitting('release');
        try {
          await XApi.apiSvc.updateBasic(editId, buildBasicPayload(form));
          const body = {
            ...buildVersionPayload(form),
            description: releaseNote || 'New version release',
            makeActive: true,
          };
          await XApi.apiSvc.createVersion(editId, body);
          await persistSandboxIntegration(editId, form);
          toast(t('register.toast.release'));
          setReleaseNote('');
          navigate('workspace');
        } catch (e) {
          toast(e?.message || t('register.toast.release.fail'));
        } finally {
          setSubmitting(null);
        }
      });
    },
    () => toast(t('register.toast.fixFields')),
  );

  // Submit-for-review auto-saves the current form first, then submits.
  // The review confirmation is a design-system Dialog (was native confirm()).
  const runSubmitForReview = handleSubmit(
    (form) => {
      if (!editId || logoUploading) return;
      setConfirmDialog({ kind: 'review', form });
    },
    () => toast({ title: t('register.toast.fixFields'), type: 'error' }),
  );

  const doSubmitForReview = (form) => {
    checkAndProceed(form, async () => {
      setSubmitting('review');
      try {
        await XApi.apiSvc.updateBasic(editId, buildSavePayload(form));
        await persistSandboxIntegration(editId, form);
        await XApi.apiSvc.submitForReview(editId, '');
        toast({ title: t('register.toast.review'), type: 'success' });
        navigate('workspace');
      } catch (e) {
        toast({
          title: e?.message || t('register.toast.review.fail'),
          type: 'error',
        });
      } finally {
        setSubmitting(null);
      }
    });
  };

  const runVerifySandbox = handleSubmit(
    async (form) => {
      if (!editId || form.integrationMode !== 'SANDBOX_PROVIDER_V1') return;
      setSubmitting('verify-sandbox');
      try {
        await persistSandboxIntegration(editId, form);
        await XApi.apiSvc.verifySandboxBinding(editId);
        const api = await XApi.apiSvc.get(editId);
        if (api) {
          setServiceLoaded(api);
          reset(transformApiToForm(api));
        }
        toast({
          title: 'Adapter 已通过 Sandbox v1 Manifest 验证',
          type: 'success',
        });
      } catch (e) {
        toast({ title: e?.message || 'Adapter 验证失败', type: 'error' });
      } finally {
        setSubmitting(null);
      }
    },
    () => toast({ title: t('register.toast.fixFields'), type: 'error' }),
  );

  const runDelete = () => {
    if (!editId) return;
    setConfirmDialog({ kind: 'delete' });
  };
  const doDelete = async () => {
    setSubmitting('delete');
    try {
      await XApi.apiSvc.delete(editId);
      toast(t('register.toast.delete'));
      navigate('workspace');
    } catch (e) {
      toast(e?.message || t('register.toast.delete.fail'));
    } finally {
      setSubmitting(null);
    }
  };

  // OpenAPI file import — read .json/.yaml/.yml file, POST to /api-services/parse-openapi,
  // hydrate the form with the returned info + endpoints. Existing draft id (edit mode) is preserved
  // so the next save still goes to the same row.
  const onImportOpenApi = async (file) => {
    if (!file) return;
    setSubmitting('parse');
    try {
      const text = await file.text();
      const parsed = await XApi.apiSvc.parseOpenApi(text);
      if (!parsed) throw new Error('Empty response from parser');

      // Backend (api.service.ts:parseOpenApi) returns a FLAT shape — top-level
      // service metadata + flat endpoint rows. Older xapi-new returned
      // { info: { title/description/servers }, endpoints: [{parameters, requestBody}] }
      // which is what this code used to assume; the new shape is:
      //   { name, description, baseUrl, host, category, authType, authConfig,
      //     privateHeaders, billingType, isPublic, logoUrl,
      //     endpoints: [{ name, method, path, description, headers[], params[],
      //                   pathParams[], bodyType, bodySchema, responses[],
      //                   billingType, costPerCall, ...x-xapi-* hints }] }
      // Read both — top-level on the new backend, legacy `info.*` as fallback
      // for any older deployment / external spec passthrough.
      const incomingEps = Array.isArray(parsed.endpoints)
        ? parsed.endpoints
        : [];
      const legacyInfo = parsed.info || {};

      // Preserve current values when the OpenAPI doc doesn't fill them.
      const cur = methods.getValues();
      const parsedName = parsed.name || legacyInfo.title || '';
      const parsedDescription =
        parsed.description || legacyInfo.description || '';
      const parsedBaseUrl =
        parsed.baseUrl ||
        (legacyInfo.servers &&
          legacyInfo.servers[0] &&
          legacyInfo.servers[0].url) ||
        cur.baseUrl ||
        '';
      const parsedHost =
        parsed.host ||
        cur.host ||
        String(parsedName)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')
          .slice(0, 32);
      const baseUrl = parsedBaseUrl;
      const slug = parsedHost;

      // Endpoint 合并语义（替代旧的"全量重建"）：按 method+path 匹配已有
      // endpoint；匹配到只更新结构字段、保留 costPerCall/billingType/hidden 等
      // 业务字段；未匹配的当新 endpoint；cur 里多出来的保留不删。详见
      // workspace-sections/openapi-merge.js 注释。
      const mergedEndpoints = mergeImportedEndpoints(
        cur.endpoints,
        incomingEps,
        INITIAL_ENDPOINT,
      );

      methods.reset(
        {
          ...cur,
          name: parsedName || cur.name,
          description: parsedDescription || cur.description,
          baseUrl,
          host: slug,
          // Honor backend-detected category if present (parsed.category comes
          // from x-xapi-category or heuristic) so the user doesn't re-pick it.
          ...(parsed.category ? { category: parsed.category } : {}),
          // Service-level auth (from securitySchemes) — the parser converts it
          // into a header/query name + sample value pair.
          ...(parsed.authType && parsed.authType !== 'NONE'
            ? {
                authType: parsed.authType,
                authConfig:
                  parsed.authConfig && typeof parsed.authConfig === 'object'
                    ? {
                        key: parsed.authConfig.key || '',
                        value: parsed.authConfig.value || '',
                      }
                    : cur.authConfig,
              }
            : {}),
          endpoints: mergedEndpoints,
          openApiSpec: importedTransportSpecInput(parsed.openApiSpec, cur.openApiSpec) ?? cur.openApiSpec,
        },
        { keepDirty: true },
      );

      const newFirst = incomingEps[0] ? `e1` : null;
      if (newFirst) setActiveEndpointId(newFirst);
      setActiveSection('endpoints');
      toast(
        `Imported ${incomingEps.length} endpoint${incomingEps.length === 1 ? '' : 's'} from OpenAPI spec`,
      );
    } catch (e) {
      toast(e?.message || 'OpenAPI parse failed');
    } finally {
      setSubmitting(null);
      if (openapiInputRef.current) openapiInputRef.current.value = '';
    }
  };

  // AI import handoff — populate endpoints/meta from the analyzed session in sessionStorage.
  React.useEffect(() => {
    let stash;
    try {
      const raw = sessionStorage.getItem('xapi.aiImport.session');
      if (!raw) return;
      stash = JSON.parse(raw);
      sessionStorage.removeItem('xapi.aiImport.session');
    } catch {
      return;
    }
    const info = stash?.resp?.extractedInfo;
    if (!info) return;
    const eps = Array.isArray(info.endpoints) ? info.endpoints : [];
    const slug = String(info.name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 32);
    methods.reset({
      ...INITIAL_FORM(),
      name: info.name || '',
      description: info.description || '',
      baseUrl: info.baseUrl || '',
      openApiSpec: importedTransportSpecInput(stash?.resp?.openApiSpec),
      host: slug,
      category: info.suggestedCategory || CATEGORIES[0],
      authType: info.authType || 'NONE',
      authConfig: { key: info.authHeaderName || '', value: '' },
      endpoints: eps.length
        ? eps.map((ep, i) => ({
            ...INITIAL_ENDPOINT(`e${i + 1}`),
            method: ep.method || 'GET',
            path: ep.path || '',
            name: ep.summary || ep.name || ep.path || `Endpoint ${i + 1}`,
            description: ep.description || ep.summary || '',
          }))
        : [INITIAL_ENDPOINT('e1')],
    });
    setActiveEndpointId(eps[0] ? `e1` : null);
    toast(
      `Imported ${eps.length} endpoint${eps.length === 1 ? '' : 's'} from AI analysis`,
    );
  }, []);

  if (!authed) {
    return (
      <div
        style={{ maxWidth: 720, margin: '0 auto', padding: '40px 32px 80px' }}
      >
        <div className="micro">{t('register.signin.eyebrow')}</div>
        <h1
          style={{
            margin: '8px 0 0',
            fontSize: 28,
            letterSpacing: '-0.02em',
            fontWeight: 500,
          }}
        >
          {t('register.signin.title')}
        </h1>
        <button
          className="btn btn-primary"
          style={{ marginTop: 20 }}
          onClick={() => navigate('login')}
        >
          {t('register.signin.cta')}
        </button>
      </div>
    );
  }

  const formName = methods.watch('name');
  // Section completion signals for the left-nav progress indicator (✓).
  // Deliberately lightweight heuristics over the required fields — zod errors
  // still own the authoritative messaging; these just answer "is there
  // anything left to fill in this section?" at a glance.
  const wHost = methods.watch('host');
  const wBaseUrl = methods.watch('baseUrl');
  const wAuthType = methods.watch('authType');
  const wAuthConfig = methods.watch('authConfig');
  const wEndpoints = methods.watch('endpoints');
  const overviewDone =
    !!(formName || '').trim() && (isDirect || !!(wHost || '').trim());
  const originDone =
    isDirect ||
    (!!(wBaseUrl || '').trim() &&
      (!wAuthType ||
        wAuthType === 'NONE' ||
        !!((wAuthConfig && wAuthConfig.value) || '').trim()));
  const endpointsDone =
    Array.isArray(wEndpoints) &&
    wEndpoints.length > 0 &&
    wEndpoints.every((ep) => !!(ep && ep.method) && !!(ep.path || '').trim());

  return (
    <FormProvider {...methods}>
      <div
        className="register-shell"
        style={{
          display: 'grid',
          gridTemplateColumns: '260px 1fr',
          minHeight: 'calc(100vh - 52px)',
        }}
      >
        {/* Left nav */}
        <aside
          className="register-sidebar"
          style={{
            background: 'var(--bg-1)',
            padding: '20px 14px',
            position: 'sticky',
            top: 52,
            alignSelf: 'start',
            height: 'calc(100vh - 52px)',
            overflowY: 'auto',
          }}
        >
          <div
            className="row gap-2"
            style={{
              color: 'var(--fg-3)',
              fontSize: 12,
              marginBottom: 18,
              padding: '0 4px',
            }}
          >
            <a
              onClick={() => navigate('workspace')}
              style={{ cursor: 'pointer' }}
            >
              {t('register.crumb.workspace')}
            </a>
            <XIcons.IconChevronRight size={10} />
            <span style={{ color: 'var(--fg-1)' }}>
              {isEdit ? t('register.crumb.edit') : t('register.crumb.new')}
            </span>
          </div>

          <div style={{ padding: '0 6px', marginBottom: 14 }}>
            <div
              style={{
                fontSize: 14,
                color: 'var(--fg-1)',
                letterSpacing: '-0.01em',
              }}
            >
              {formName || t('register.untitled')}
            </div>
            <div
              className="micro-mono"
              style={{ textTransform: 'none', letterSpacing: 0, marginTop: 4 }}
            >
              {isDirect
                ? t('register.host.direct')
                : formHost
                  ? XApi.helpers.gatewayHost(formHost, formIntegrationMode)
                  : t('register.host.pending')}
            </div>
            {isEdit && serviceLoaded?.currentVersion?.version && (
              <div className="tag" style={{ marginTop: 8 }}>
                <span className="tag-dot ok live-dot" />
                <span className="mono">
                  {serviceLoaded.currentVersion.version} {t('register.live')}
                </span>
              </div>
            )}
          </div>

          <div className="micro" style={{ padding: '0 6px', marginBottom: 8 }}>
            {t('register.nav.configure')}
          </div>
          <div className="col gap-1">
            <SectionNavBtn
              k="overview"
              current={activeSection}
              setCurrent={setActiveSection}
              icon={XIcons.IconLayers}
              label={t('register.nav.overview')}
              hint={t('register.nav.overview.hint')}
              done={overviewDone}
              errorCount={countErrorsInPaths(errors, [
                'name',
                'description',
                'logoUrl',
                'category',
                'host',
                'isPublic',
                'accessMode',
                'rateLimitConfig',
              ])}
            />
            <SectionNavBtn
              k="origin"
              current={activeSection}
              setCurrent={setActiveSection}
              icon={XIcons.IconShield}
              label={t('register.nav.origin')}
              hint={
                isDirect
                  ? t('register.nav.origin.disabled')
                  : t('register.nav.origin.hint')
              }
              disabled={isDirect}
              done={originDone}
              errorCount={countErrorsInPaths(errors, [
                'baseUrl',
                'authType',
                'authConfig',
                'privateHeaders',
              ])}
            />
            <SectionNavBtn
              k="endpoints"
              current={activeSection}
              setCurrent={setActiveSection}
              icon={XIcons.IconCode}
              label={t('register.nav.endpoints')}
              hint={t('register.nav.endpoints.hint').replace(
                '{N}',
                methods.watch('endpoints')?.length || 0,
              )}
              done={endpointsDone}
              errorCount={
                errors.endpoints ? countErrorsInPaths(errors, ['endpoints']) : 0
              }
            />
          </div>

          <div
            className="micro"
            style={{ padding: '0 6px', margin: '24px 0 8px' }}
          >
            {t('register.nav.tools')}
          </div>
          {
            <button
              className="nav-item"
              title={t('register.tools.importOpenapi')}
              aria-label={t('register.tools.importOpenapi')}
              onClick={() =>
                openapiInputRef.current && openapiInputRef.current.click()
              }
              disabled={submitting != null}
            >
              <XIcons.IconDownload size={14} />{' '}
              <span>
                {submitting === 'parse'
                  ? t('register.tools.importOpenapi.busy')
                  : t('register.tools.importOpenapi')}
              </span>
            </button>
          }
          {
            <button
              className="nav-item"
              title={t('register.tools.aiImport')}
              aria-label={t('register.tools.aiImport')}
              onClick={() => setAiImportOpen(true)}
            >
              <XIcons.IconZap size={14} />{' '}
              <span>{t('register.tools.aiImport')}</span>
            </button>
          }
          {
            <button
              type="button"
              className="nav-item"
              title={t('register.tools.batchPrice')}
              aria-label={t('register.tools.batchPrice')}
              onClick={() => setOverviewOpen(true)}
            >
              <XIcons.IconCode size={14} />{' '}
              <span>{t('register.tools.batchPrice')}</span>
            </button>
          }
          {isEdit && (
            <button
              className="nav-item"
              title={t('register.tools.deleteDraft')}
              aria-label={t('register.tools.deleteDraft')}
              onClick={runDelete}
              disabled={submitting != null}
            >
              <XIcons.IconTrash size={14} />{' '}
              <span>{t('register.tools.deleteDraft')}</span>
            </button>
          )}
          <input
            ref={openapiInputRef}
            type="file"
            accept=".json,.yaml,.yml,.txt,application/json,application/x-yaml"
            style={{ display: 'none' }}
            onChange={(e) =>
              onImportOpenApi(e.target.files && e.target.files[0])
            }
          />
        </aside>

        {/* Main — borderLeft (instead of borderRight on the sticky aside) so the
            sidebar/main divider line extends the full grid-track height even when
            main content is taller than the sticky sidebar's viewport-locked height. */}
        <main
          className="register-main"
          style={{
            padding: '24px 32px 120px',
            overflowY: 'auto',
            borderLeft: '1px solid var(--line)',
          }}
        >
          <FormHeader
            isEdit={isEdit}
            isDirty={isDirty}
            submitting={submitting || (logoUploading ? 'logo' : null)}
            versionId={versionId}
            onCreate={runCreate}
            onSave={runSave}
            onHotfix={runHotfix}
            onRelease={runRelease}
            releaseNote={releaseNote}
            setReleaseNote={setReleaseNote}
            onSubmitForReview={runSubmitForReview}
            serviceStatus={serviceLoaded?.status}
          />

          {/* key={activeSection} replays the quick fade on section switch —
              same .apx-fade vocabulary as console/api-detail. */}
          <div key={activeSection} className="apx-fade">
            {activeSection === 'overview' && (
              <OverviewSection hostValidation={hostValidation} onLogoUploadingChange={setLogoUploading} disabled={submitting != null} />
            )}
            {activeSection === 'origin' && !isDirect && <OriginAuthSection />}
            {activeSection === 'origin' && isDirect && <DirectModeNotice />}
            {activeSection === 'endpoints' && (
              <EndpointsSection
                activeEndpointId={activeEndpointId}
                setActiveEndpointId={setActiveEndpointId}
                accessMode={formAccessMode}
                onImportClick={() =>
                  openapiInputRef.current && openapiInputRef.current.click()
                }
                parsing={submitting === 'parse'}
              />
            )}
          </div>

          {/* Endpoints overview dialog — opened from sidebar tools. Renders
              at page level so it works from any active section. Clicking a
              row jumps to that endpoint's detail in the editor + closes. */}
          <EndpointsOverviewDialog
            open={overviewOpen}
            onClose={() => setOverviewOpen(false)}
            accessMode={formAccessMode}
            onJumpToEndpoint={(id) => {
              setActiveEndpointId(id);
              setActiveSection('endpoints');
              setOverviewOpen(false);
            }}
          />
        </main>

        <AiImportDialog
          open={aiImportOpen}
          onClose={() => setAiImportOpen(false)}
          onAnalyzed={() => {
            /* handled by sessionStorage effect on next mount of this page */
          }}
        />

        {/* Delete-draft + submit-review confirmations (design-system Dialogs,
            replacing the old native confirm() calls). */}
        <ConfirmDialog
          open={confirmDialog?.kind === 'delete'}
          onClose={() => setConfirmDialog(null)}
          onConfirm={doDelete}
          title={t('register.confirm.delete.title')}
          body={t('register.confirm.delete')}
          confirmLabel={t('register.confirm.delete.ok')}
          danger
        />
        <ConfirmDialog
          open={confirmDialog?.kind === 'review'}
          onClose={() => setConfirmDialog(null)}
          onConfirm={() => doSubmitForReview(confirmDialog.form)}
          title={t('register.confirm.review.title')}
          body={t('register.confirm.review')}
          confirmLabel={t('register.confirm.review.ok')}
        />
      </div>
    </FormProvider>
  );
};

// ============ Page chrome ============

// Design-system confirm dialog — replaces native confirm() so destructive or
// submit actions get the same Dialog chrome as hotfix/release already use.
// Exported for the workspace sections (basic-form About tab etc.).
export const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel,
  danger,
}) => {
  const { t } = useT();
  return (
    <Dialog open={open} onClose={onClose} maxWidth={440}>
      <DialogHeader title={title} onClose={onClose} />
      <DialogBody>
        <div style={{ fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.6 }}>
          {body}
        </div>
      </DialogBody>
      <DialogFooter>
        <button type="button" className="btn btn-sm" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className={`btn btn-sm ${danger ? '' : 'btn-primary'}`}
          style={
            danger
              ? { color: 'var(--err)', borderColor: 'var(--err)' }
              : undefined
          }
          onClick={() => {
            onClose();
            onConfirm();
          }}
        >
          {confirmLabel}
        </button>
      </DialogFooter>
    </Dialog>
  );
};

const SectionNavBtn = ({
  k,
  current,
  setCurrent,
  icon: Icon,
  label,
  hint,
  errorCount,
  disabled,
  done,
}) => (
  <button
    type="button"
    className={`nav-item ${current === k ? 'active' : ''}`}
    onClick={() => !disabled && setCurrent(k)}
    disabled={disabled}
    style={{ opacity: disabled ? 0.5 : 1 }}
  >
    <Icon size={14} />
    <div className="grow col" style={{ gap: 0 }}>
      <span>{label}</span>
      <span
        className="micro-mono"
        style={{
          fontSize: 10,
          textTransform: 'none',
          letterSpacing: 0,
          marginTop: 2,
        }}
      >
        {hint}
      </span>
    </div>
    {errorCount > 0 ? (
      <span
        style={{
          background: 'var(--err)',
          color: '#000',
          borderRadius: 9,
          fontSize: 10,
          padding: '0 6px',
          minWidth: 16,
          height: 16,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
        }}
      >
        {errorCount}
      </span>
    ) : done ? (
      <XIcons.IconCheck
        size={13}
        style={{ color: 'var(--ok)', flexShrink: 0 }}
      />
    ) : null}
  </button>
);

// Per-status banner shown under the page title. Each variant matches one branch of
// the button group below — they're the same state-machine, just different surfaces.
export const StatusBanner = ({ status }) => {
  const { t } = useT();
  if (!status || status === 'DRAFT') {
    return (
      <p
        style={{
          color: 'var(--fg-3)',
          fontSize: 13,
          margin: '6px 0 0',
          lineHeight: 1.55,
        }}
      >
        {t('register.banner.draft')}
      </p>
    );
  }
  const tone = {
    SANDBOX: { color: 'var(--warn)', key: 'register.banner.sandbox' },
    PENDING_REVIEW: {
      color: 'var(--warn)',
      key: 'register.banner.pendingReview',
    },
    APPROVED: { color: 'var(--ok)', key: 'register.banner.approved' },
    ACTIVE: { color: 'var(--ok)', key: 'register.banner.active' },
    REJECTED: { color: 'var(--err)', key: 'register.banner.rejected' },
    SUSPENDED: { color: 'var(--err)', key: 'register.banner.suspended' },
  }[status] || { color: 'var(--fg-3)', key: 'register.banner.draft' };
  return (
    <p
      style={{
        color: tone.color,
        fontSize: 13,
        margin: '6px 0 0',
        lineHeight: 1.55,
      }}
    >
      {t(tone.key)}
    </p>
  );
};

// Resolve which two buttons the header shows for the current API status. Single
// source of truth — adding/renaming a state means editing only this table.
// Primary slot is the right-most button (the "ship it" action). Secondary is the
// outline button to the left (the "stage it" action).
const BUTTONS_BY_STATUS = {
  // status      secondaryAction      primaryAction
  DRAFT: ['save', 'review'],
  // SANDBOX / PENDING_REVIEW / APPROVED — admin already has this in their queue,
  // so the owner's only job here is to keep the working copy fresh. No Submit:
  // pressing Update is enough, the admin pulls the latest on next look.
  SANDBOX: [null, 'save'],
  PENDING_REVIEW: [null, 'save'],
  APPROVED: [null, 'save'],
  ACTIVE: ['hotfix', 'release'],
  REJECTED: ['save', 'resubmit'],
  // Pause is an Admin-reversible service gate. Providers may keep a working
  // copy up to date, but publishing/resubmitting must not reactivate it.
  SUSPENDED: [null, 'save'],
};

export const FormHeader = ({
  isEdit,
  isDirty,
  submitting,
  versionId,
  onCreate,
  onSave,
  onHotfix,
  onRelease,
  releaseNote,
  setReleaseNote,
  onSubmitForReview,
  serviceStatus,
}) => {
  const { t } = useT();
  const busy = submitting != null;
  const status = serviceStatus || 'DRAFT';
  const [secondary, primary] = isEdit
    ? BUTTONS_BY_STATUS[status] || BUTTONS_BY_STATUS.DRAFT
    : [null, null];
  // 更新当前版本 / 发布新版本 are the persist actions for an ACTIVE service.
  // Both go through a confirm dialog that spells out the difference + reminds
  // that staged edits/deletes only take effect here. null | 'hotfix' | 'release'.
  const [confirmKind, setConfirmKind] = React.useState(null);

  // Centralised button factory — keeps the per-status mapping above and the
  // visual treatment here, so the JSX below stays trivial.
  const renderAction = (kind, role) => {
    const isPrimary = role === 'primary';
    const cls = isPrimary ? 'btn btn-primary' : 'btn';
    const slot = (label, busyLabel, icon, onClick, extra = {}) => (
      <button
        type="button"
        className={cls}
        onClick={onClick}
        disabled={busy || extra.disabled}
      >
        {submitting === extra.flag ? (
          <>
            <div className="spinner" /> {busyLabel}
          </>
        ) : (
          <>
            {icon} <span className="form-actions-label">{label}</span>
          </>
        )}
      </button>
    );
    switch (kind) {
      case 'save':
        return slot(
          t('register.action.save'),
          t('register.action.save.busy'),
          <XIcons.IconCheck size={13} />,
          onSave,
          { flag: 'save' },
        );
      case 'review':
        return slot(
          t('register.action.review'),
          t('register.action.review.busy'),
          <XIcons.IconArrowRight size={12} />,
          onSubmitForReview,
          { flag: 'review' },
        );
      case 'submit':
        return slot(
          t('register.action.submit'),
          t('register.action.submit.busy'),
          <XIcons.IconArrowRight size={12} />,
          onSubmitForReview,
          { flag: 'review' },
        );
      case 'resubmit':
        return slot(
          t('register.action.resubmit'),
          t('register.action.resubmit.busy'),
          <XIcons.IconArrowRight size={12} />,
          onSubmitForReview,
          { flag: 'review' },
        );
      case 'hotfix':
        // Opens a confirm dialog (explains "update in place") rather than firing
        // immediately — the click no longer feels like an auto-save.
        return slot(
          t('register.action.hotfix'),
          t('register.action.hotfix.busy'),
          <XIcons.IconZap size={13} />,
          () => setConfirmKind('hotfix'),
          { flag: 'hotfix', disabled: !versionId },
        );
      case 'release':
        // Opens a confirm dialog; the release-note input now lives inside it.
        return (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setConfirmKind('release')}
            disabled={busy}
          >
            {submitting === 'release' ? (
              <>
                <div className="spinner" /> {t('register.action.release.busy')}
              </>
            ) : (
              <>
                <XIcons.IconArrowRight size={12} />{' '}
                <span className="form-actions-label">
                  {t('register.action.release')}
                </span>
              </>
            )}
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        className="row between"
        style={{ alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}
      >
        <div>
          {/* Status pill belongs next to the title — it labels WHAT you're
              editing, not an action. Previously it was rendered alongside
              the action buttons on the right, which felt visually misaligned
              ("sandbox" tag next to "Update" button). */}
          <div
            className="row gap-2"
            style={{ alignItems: 'center', flexWrap: 'wrap' }}
          >
            <h1
              style={{
                margin: 0,
                fontSize: 24,
                letterSpacing: '-0.01em',
                fontWeight: 500,
              }}
            >
              {isEdit
                ? t('register.header.title.edit')
                : t('register.header.title.new')}
            </h1>
            {isEdit && (
              <span
                className="tag form-actions-status"
                style={{ alignSelf: 'center' }}
              >
                <span
                  className="tag-dot"
                  style={{
                    background:
                      status === 'ACTIVE'
                        ? 'var(--ok)'
                        : status === 'PENDING_REVIEW' || status === 'SANDBOX'
                          ? 'var(--warn)'
                          : status === 'REJECTED' || status === 'SUSPENDED'
                            ? 'var(--err)'
                            : 'var(--fg-4)',
                  }}
                />
                <span className="mono">{status.toLowerCase()}</span>
              </span>
            )}
          </div>
          {!isEdit && (
            <p
              style={{
                color: 'var(--fg-3)',
                fontSize: 13,
                margin: '6px 0 0',
                lineHeight: 1.55,
              }}
            >
              {t('register.header.body.new')}
            </p>
          )}
          {isEdit && <StatusBanner status={status} />}
        </div>
        <div
          className="row gap-2 form-actions"
          style={{ justifyContent: 'flex-end', alignItems: 'center' }}
        >
          {!isEdit && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={onCreate}
              disabled={busy}
            >
              {submitting === 'create' ? (
                <>
                  <div className="spinner" /> {t('register.action.create.busy')}
                </>
              ) : (
                <>
                  <XIcons.IconPlus size={13} /> {t('register.action.create')}
                </>
              )}
            </button>
          )}
          {isEdit && secondary && renderAction(secondary, 'secondary')}
          {isEdit && primary && renderAction(primary, 'primary')}
        </div>
      </div>
      {isDirty && (
        <div
          style={{
            marginTop: 12,
            padding: '9px 12px',
            borderRadius: 6,
            background: 'var(--bg-2)',
            borderLeft: '3px solid var(--warn)',
            fontSize: 12.5,
            lineHeight: 1.55,
            color: 'var(--fg-2)',
          }}
        >
          {t('register.unsavedChanges')}
        </div>
      )}

      {/* Confirm dialog for 更新当前版本 / 发布新版本 — explains the difference and
          that this is where staged edits/deletes actually persist. */}
      {confirmKind && (
        <Dialog open onClose={() => setConfirmKind(null)} maxWidth={480}>
          <DialogHeader
            title={t(`register.confirm.${confirmKind}.title`)}
            onClose={() => setConfirmKind(null)}
          />
          <DialogBody>
            <div
              style={{ fontSize: 13, lineHeight: 1.75, color: 'var(--fg-2)' }}
            >
              {t(`register.confirm.${confirmKind}.body`)}
            </div>
            {confirmKind === 'release' && (
              <input
                type="text"
                value={releaseNote}
                onChange={(e) => setReleaseNote(e.target.value)}
                placeholder={t('register.action.release.placeholder')}
                className="input"
                style={{ marginTop: 14 }}
              />
            )}
          </DialogBody>
          <DialogFooter>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setConfirmKind(null)}
            >
              {t('register.confirm.cancelBtn')}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => {
                const k = confirmKind;
                setConfirmKind(null);
                if (k === 'hotfix') onHotfix();
                else if (k === 'release') onRelease();
              }}
            >
              {t(`register.confirm.${confirmKind}.ok`)}
            </button>
          </DialogFooter>
        </Dialog>
      )}
    </div>
  );
};

// ============ Overview section ============

// Inline realtime host feedback under the host-prefix input. Renders nothing when
// idle (empty / DIRECT); a muted "checking…", green "available", or red reason.
const HostValidationHint = ({ hostValidation }) => {
  if (!hostValidation || hostValidation.status === 'idle') return null;
  const { status, message } = hostValidation;
  const color =
    status === 'ok'
      ? 'var(--ok)'
      : status === 'invalid'
        ? 'var(--err)'
        : 'var(--fg-3)';
  const icon =
    status === 'ok' ? (
      <XIcons.IconCheck size={12} />
    ) : status === 'invalid' ? (
      <XIcons.IconX size={12} />
    ) : (
      <XIcons.IconInfo size={12} />
    );
  return (
    <div
      className="row gap-1"
      style={{ marginTop: 6, alignItems: 'center', color, fontSize: 11.5 }}
      role="status"
      aria-live="polite"
    >
      {icon}
      <span>{message}</span>
    </div>
  );
};

export const OverviewSection = ({ hostValidation, onLogoUploadingChange, disabled = false }) => {
  const {
    register,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();
  const { t } = useT();
  const accessMode = watch('accessMode');
  const integrationMode = watch('integrationMode');

  return (
    <div className="col gap-3">
      <Panel
        title={t('register.overview.identity')}
        subtitle={t('register.overview.identity.sub')}
      >
        <Row cols={2}>
          <Field
            label={t('register.overview.name')}
            required
            error={errors.name}
          >
            <input
              className="input"
              placeholder={t('register.overview.name.placeholder')}
              {...register('name')}
            />
          </Field>
          <Field
            label={t('register.overview.category')}
            error={errors.category}
          >
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onChange={field.onChange}
                  options={CATEGORIES.map((c) => ({ value: c, label: c }))}
                />
              )}
            />
          </Field>
        </Row>
        <Field
          label={t('register.overview.description')}
          hint={t('register.overview.description.hint')}
        >
          <textarea
            className="input"
            rows={3}
            placeholder={t('register.overview.description.placeholder')}
            {...register('description')}
          />
        </Field>
        <Field
          label={t('register.overview.website') || 'Website'}
          hint={
            t('register.overview.website.hint') ||
            'Optional homepage shown on the marketplace detail page'
          }
          error={errors.website}
        >
          <input
            className="input mono"
            placeholder="https://…"
            {...register('website')}
          />
        </Field>
        <Row cols={2}>
          <Field label={t('register.overview.logoUrl')} error={errors.logoUrl}>
            <Controller
              name="logoUrl"
              control={control}
              render={({ field }) => (
                <LogoUpload
                  value={field.value || ''}
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={(url) => setValue('logoUrl', url, { shouldDirty: true, shouldValidate: true })}
                  onUploadingChange={onLogoUploadingChange}
                  disabled={disabled}
                />
              )}
            />
          </Field>
          <Field label={t('register.overview.visibility')}>
            <Controller
              name="isPublic"
              control={control}
              render={({ field }) => (
                <div className="row gap-1">
                  <button
                    type="button"
                    className={`btn btn-sm ${field.value ? 'btn-primary' : ''}`}
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => field.onChange(true)}
                  >
                    {t('register.overview.visibility.public')}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${!field.value ? 'btn-primary' : ''}`}
                    style={{ flex: 1, justifyContent: 'center' }}
                    onClick={() => field.onChange(false)}
                  >
                    {t('register.overview.visibility.private')}
                  </button>
                </div>
              )}
            />
          </Field>
        </Row>
      </Panel>

      <Panel
        title={t('register.overview.access')}
        subtitle={t('register.overview.access.sub')}
      >
        <Controller
          name="accessMode"
          control={control}
          render={({ field }) => (
            <div
              className="row gap-2 register-row"
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}
            >
              <ModeCard
                active={field.value === 'PROXY'}
                onClick={() => field.onChange('PROXY')}
                title="PROXY"
                desc={t('register.overview.access.proxy.desc')}
                icon={<XIcons.IconShield size={16} />}
              />
              <ModeCard
                active={field.value === 'DIRECT'}
                onClick={() => {
                  if (integrationMode === 'SANDBOX_PROVIDER_V1') return;
                  field.onChange('DIRECT');
                  setValue('rateLimitConfig', null, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
                title="DIRECT"
                desc={
                  integrationMode === 'SANDBOX_PROVIDER_V1'
                    ? 'Sandbox 必须经过 sandbox-gateway，不能直接绕过状态和余额控制。'
                    : t('register.overview.access.direct.desc')
                }
                icon={<XIcons.IconLayers size={16} />}
              />
            </div>
          )}
        />
        {accessMode !== 'DIRECT' &&
          integrationMode !== 'SANDBOX_PROVIDER_V1' && (
            <Field
              label={t('register.overview.host.label')}
              hint={t('register.overview.host.hint', { suffix: XApi.helpers.gatewaySuffix(integrationMode) })}
              style={{ marginTop: 14 }}
              error={errors.host}
            >
              <div
                className="row hairline"
                style={{
                  borderRadius: 6,
                  background: 'var(--bg-2)',
                  overflow: 'hidden',
                }}
              >
                <input
                  className="input mono"
                  style={{
                    border: 'none',
                    background: 'transparent',
                    borderRadius: 0,
                    fontSize: 12,
                  }}
                  placeholder="my-api"
                  {...register('host', {
                    setValueAs: (v) =>
                      (v || '').toLowerCase().replace(/[^a-z0-9-]/g, '-'),
                  })}
                  onChange={(e) =>
                    setValue(
                      'host',
                      e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
                      { shouldDirty: true },
                    )
                  }
                />
                <span
                  className="mono"
                  style={{
                    padding: '0 12px',
                    color: 'var(--fg-3)',
                    fontSize: 11,
                    background: 'var(--bg-3)',
                    display: 'flex',
                    alignItems: 'center',
                    borderLeft: '1px solid var(--line)',
                  }}
                >
                  {XApi.helpers.gatewaySuffix(integrationMode)}
                </span>
              </div>
              <HostValidationHint hostValidation={hostValidation} />
            </Field>
          )}
        {integrationMode === 'SANDBOX_PROVIDER_V1' && (
          <div
            style={{
              marginTop: 14,
              padding: 12,
              border: '1px solid var(--line)',
              borderRadius: 7,
              color: 'var(--fg-3)',
              fontSize: 12,
              lineHeight: 1.6,
            }}
          >
            消费者统一通过 <span className="mono">sandbox.xapi.to</span>{' '}
            使用服务；这里的 Base URL 指向服务商
            Adapter，不再为每个生命周期动作配置 endpoint 标签。
          </div>
        )}
      </Panel>

      <RateLimitSection />
    </div>
  );
};

const RATE_LIMIT_PERIODS = [
  { seconds: 1, labelKey: 'register.overview.rateLimit.second' },
  { seconds: 60, labelKey: 'register.overview.rateLimit.minute' },
  { seconds: 3600, labelKey: 'register.overview.rateLimit.hour' },
];

const RateLimitSection = () => {
  const { register, control, formState: { errors }, watch } = useFormContext();
  const { t } = useT();
  const accessMode = watch('accessMode');
  const config = watch('rateLimitConfig');
  const enabled = accessMode !== 'DIRECT' && config != null;
  const requests = Number.isFinite(config?.requests) ? config.requests : 0;
  const period = RATE_LIMIT_PERIODS.find((item) => item.seconds === config?.periodSeconds);
  const periodLabel = period
    ? t(period.labelKey)
    : t('register.overview.rateLimit.custom').replace('{seconds}', String(config?.periodSeconds || '—'));

  return (
    <Panel
      title={t('register.overview.rateLimit')}
      subtitle={t('register.overview.rateLimit.sub')}
      action={(
        <Controller
          name="rateLimitConfig"
          control={control}
          render={({ field }) => (
            <button
              type="button"
              role="switch"
              aria-checked={accessMode !== 'DIRECT' && field.value != null}
              aria-label={t('register.overview.rateLimit.toggle')}
              className={`btn btn-sm rate-limit-toggle ${field.value != null ? 'btn-primary' : ''}`}
              disabled={accessMode === 'DIRECT'}
              onClick={() =>
                accessMode !== 'DIRECT' &&
                field.onChange(
                  field.value == null
                    ? { requests: 100, periodSeconds: 60 }
                    : null,
                )
              }
            >
              {field.value != null ? <XIcons.IconCheck size={12} /> : <XIcons.IconPlus size={12} />}
              {field.value != null
                ? t('register.overview.rateLimit.status.on')
                : t('register.overview.rateLimit.on')}
            </button>
          )}
        />
      )}
    >

      {enabled && (
        <div className="rate-limit-config">
          <Row cols={2}>
            <Field
              label={t('register.overview.rateLimit.requests')}
              hint={t('register.overview.rateLimit.requests.hint')}
              error={errors.rateLimitConfig?.requests}
            >
              <div className="rate-limit-input-wrap">
                <input
                  className="input mono"
                  type="number"
                  min="1"
                  max="1000000"
                  step="1"
                  {...register('rateLimitConfig.requests', { valueAsNumber: true })}
                />
                <span>{t('register.overview.rateLimit.times')}</span>
              </div>
            </Field>
            <Field label={t('register.overview.rateLimit.period')}>
              <Controller
                name="rateLimitConfig.periodSeconds"
                control={control}
                render={({ field }) => (
                  <div className="rate-limit-periods" role="group" aria-label={t('register.overview.rateLimit.period')}>
                    {RATE_LIMIT_PERIODS.map((item) => (
                      <button
                        key={item.seconds}
                        type="button"
                        className={`btn btn-sm ${field.value === item.seconds ? 'btn-primary' : ''}`}
                        onClick={() => field.onChange(item.seconds)}
                      >
                        {t(item.labelKey)}
                      </button>
                    ))}
                    {!RATE_LIMIT_PERIODS.some((item) => item.seconds === field.value) && (
                      <button type="button" className="btn btn-sm btn-primary rate-limit-custom-period">
                        {t('register.overview.rateLimit.custom').replace('{seconds}', String(field.value))}
                      </button>
                    )}
                  </div>
                )}
              />
            </Field>
          </Row>
          <div className="rate-limit-summary">
            <span className="rate-limit-summary-policy">
              <XIcons.IconGauge size={13} />
              {t('register.overview.rateLimit.preview')}
              <strong className="mono">{requests || '—'}</strong>
              {t('register.overview.rateLimit.times')} / {periodLabel}
            </span>
            <span className="rate-limit-summary-scope">
              {t('register.overview.rateLimit.scope.user')} · {t('register.overview.rateLimit.scope.keys')} · {t('register.overview.rateLimit.scope.endpoints')}
            </span>
          </div>
        </div>
      )}

      {!enabled && (
        <div className="rate-limit-off-note">
          <XIcons.IconInfo size={13} />
          <span>{t('register.overview.rateLimit.off.hint')}</span>
        </div>
      )}

      {accessMode === 'DIRECT' && (
        <div className="rate-limit-warning">
          <XIcons.IconInfo size={13} />
          <span>{t('register.overview.rateLimit.direct')}</span>
        </div>
      )}
    </Panel>
  );
};

const ModeCard = ({ active, onClick, title, desc, icon }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      textAlign: 'left',
      padding: 16,
      border: '1px solid',
      borderColor: active ? 'var(--fg-0)' : 'var(--line)',
      borderRadius: 8,
      background: active ? 'var(--bg-2)' : 'transparent',
      cursor: 'pointer',
      color: 'inherit',
    }}
  >
    <div className="row gap-2" style={{ alignItems: 'center' }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 6,
          background: 'var(--bg-3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: active ? 'var(--fg-0)' : 'var(--fg-3)',
        }}
      >
        {icon}
      </div>
      <div style={{ fontSize: 14, fontWeight: 500 }}>{title}</div>
    </div>
    <p
      style={{
        margin: '10px 0 0',
        fontSize: 12,
        color: 'var(--fg-3)',
        lineHeight: 1.6,
      }}
    >
      {desc}
    </p>
  </button>
);

// ============ Origin & Auth section ============

export const OriginAuthSection = ({ onSandboxDetected } = {}) => {
  const {
    register,
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useFormContext();
  const { t } = useT();
  const authType = watch('authType');
  const baseUrlValue = watch('baseUrl');
  // 贴上地址就认出这是不是 Sandbox Adapter —— manifest 是公开的，不需要 token，
  // 服务商因此不必事先知道「要去管理 API 里选 Sandbox 计费」这条路。
  const [probe, setProbe] = React.useState(null); // null | {detected, ...}
  const [probing, setProbing] = React.useState(false);
  const [probeDismissed, setProbeDismissed] = React.useState(false);
  React.useEffect(() => {
    const url = String(baseUrlValue || '').trim();
    // 不在这里同步清空：每按一个键就把横幅撤掉、600ms 后再放回来，页面会连跳三次。
    // 只有地址已经不成形时才撤，其余情况保留上一次结果直到新结果回来。
    if (!/^https?:\/\/.+/i.test(url)) {
      setProbe(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setProbing(true);
      try {
        const result = await XApi.apiSvc.probeSandboxAdapter(url);
        if (!cancelled) setProbe(result?.detected ? result : null);
      } catch {
        if (!cancelled) setProbe(null);
      } finally {
        if (!cancelled) setProbing(false);
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [baseUrlValue]);

  /** 一键把探测到的 manifest 变成一条待填价格的 Sandbox 端点。 */
  const applySandboxDetection = () => {
    const sku = (probe?.skus || [])[0];
    const caps = probe?.capabilities || {};
    const offering = {
      ...DEFAULT_SANDBOX_OFFERING(),
      providerSkuId: sku?.id || '',
      name: sku?.name || probe?.provider || '',
      region: sku?.region || '',
      resources: {
        ...DEFAULT_SANDBOX_OFFERING().resources,
        cpu: Number(sku?.resources?.cpu ?? 0),
        memoryGiB: Number(sku?.resources?.memoryGiB ?? 0),
        volumeGiB: Number(sku?.resources?.volumeGiB ?? 0),
        snapshotGiB: Number(sku?.resources?.snapshotGiB ?? 0),
        gpuModel: sku?.resources?.gpu?.[0]?.model || '',
        gpuCount: Number(sku?.resources?.gpu?.[0]?.count ?? 0),
      },
      capabilities: Object.fromEntries(
        Object.keys(DEFAULT_SANDBOX_OFFERING().capabilities).map((key) => [
          key,
          key === 'autoResume'
            ? caps.autoResume?.supported === true
            : caps[key] === true,
        ]),
      ),
      autoResumeMode: caps.autoResume?.supported
        ? caps.autoResume.mode
        : 'UNSUPPORTED',
      autoResumeWakeOperations: (caps.autoResume?.wakeOperations || []).join(
        ', ',
      ),
      lifecycle: caps.lifecycle || null,
      // 多机制无法替服务商猜（PAUSE 保内存 / STOP 不保），取第一个可用的让人确认。
      suspensionMechanism:
        probe?.suspension?.mechanism ||
        probe?.suspension?.supportedMechanisms?.[0] ||
        'PAUSE',
      extensionIds: (probe?.extensions || []).map((e) => e.id).join(', '),
      extensionPricing: (probe?.extensions || []).map((extension) => ({
        extensionId: extension.id,
        pricePerOperation: 0,
      })),
    };
    const next = {
      ...DEFAULT_SANDBOX_CONFIG(),
      // Adapter 与 xAPI 共用的事件签名密钥。这里生成的只是**占位**：真要用 Webhook
      // 推送，必须改成 Adapter 上配置的同一个值，否则验签必然失败。留空则后端拒绝，
      // 所以自动配置必须给一个值让流程能走完。
      webhookSecret: (
        globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
      ).replace(/-/g, ''),
      observationMode: probe?.observationMode || 'POLLING',
      maxDetectionDelaySeconds: Number(probe?.maxDetectionDelaySeconds || 30),
      manifest: {
        capabilities: caps,
        skus: probe?.skus || [],
        environments: probe?.environments || [],
      },
      offerings: [offering],
    };
    const endpoints = getValues('endpoints') || [];
    const existing = endpoints.findIndex(
      (ep) => ep?.billingType === 'SANDBOX' && ep?.sandboxConfig,
    );
    const sandboxEndpoint = {
      ...INITIAL_ENDPOINT(),
      name: '创建 Sandbox',
      method: 'POST',
      path: SANDBOX_SURFACE_PATH,
      billingType: 'SANDBOX',
      sandboxConfig: next,
    };
    if (existing >= 0) {
      setValue(`endpoints.${existing}.sandboxConfig`, next, {
        shouldDirty: true,
      });
    } else {
      setValue('endpoints', [...endpoints, sandboxEndpoint], {
        shouldDirty: true,
      });
    }
    setProbeDismissed(true);
    // 「基本信息」段按设计不保存端点（见 basic-form 的 _omitEndpoints），塞进它的
    // 表单会被静默丢掉。所以这里直接落库，再把人送到「管理 API」去填价格。
    // 直接给出接口结构（不要再经 buildSandboxBindingPayload —— 那是「表单 → 接口」
    // 的转换，这里的 next 已经是表单结构，转两次会得到非法载荷被后端 400）。
    // 第三个参数是原始探测结果：调用方要用它生成 About 文档（能力/规格都在里面）。
    if (onSandboxDetected) onSandboxDetected(sandboxEndpoint, next, probe);
  };
  const phArray = useFieldArray({ control, name: 'privateHeaders' });
  const baseUrlsArray = useFieldArray({ control, name: 'baseUrls' });
  // 纯 WS 服务：baseUrl 是 HTTP 死字段（网关不读、后端只认 http/https）→ 不必填，保存时会省略。
  const pureWs = isPureWsService({ endpoints: watch('endpoints') });

  return (
    <div className="col gap-3">
      <Panel
        title={t('register.origin.upstream')}
        subtitle={t('register.origin.upstream.sub')}
        action={
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => baseUrlsArray.append({ name: '', url: '' })}
          >
            <XIcons.IconPlus size={11} /> {t('register.origin.env.add')}
          </button>
        }
      >
        <Field
          label={t('register.origin.env.default')}
          required={!pureWs}
          hint={
            pureWs
              ? 'WebSocket 服务不使用此项（仅 HTTP 端点用）。上游地址请填在各端点的「上游 URL」；保存时本字段会被忽略。'
              : t('register.origin.baseUrl.hint')
          }
          error={errors.baseUrl}
        >
          <input
            className="input mono"
            placeholder={
              pureWs
                ? '（WS 服务留空即可，保存时忽略）'
                : 'https://api.your-domain.com'
            }
            {...register('baseUrl')}
          />
        </Field>
        {probing && !probe?.detected && (
          <div className="micro" style={{ marginTop: 8, color: 'var(--fg-4)' }}>
            正在识别上游类型…
          </div>
        )}
        {probe?.detected && !probeDismissed && (
          <div
            className="row between"
            style={{
              marginTop: 12,
              padding: '12px 14px',
              borderRadius: 8,
              gap: 12,
              border: '1px solid var(--ok, #52d3ad)',
              background: 'rgba(82,211,173,0.08)',
              alignItems: 'center',
              flexWrap: 'wrap',
              // 重新探测时只降透明度，不增删节点 —— 高度不变就不会抖。
              opacity: probing ? 0.55 : 1,
              transition: 'opacity 120ms ease',
            }}
          >
            <div
              style={{ fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.7 }}
            >
              <div>
                检测到 <strong>Sandbox Adapter</strong>
                <span
                  className="mono"
                  style={{ color: 'var(--fg-3)', marginLeft: 6 }}
                >
                  {probe.provider} · 协议 v{probe.protocolVersion}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>
                {[
                  probe.capabilities?.exec && '执行',
                  probe.capabilities?.files && '文件',
                  probe.capabilities?.ports && '端口',
                  probe.capabilities?.snapshot && '快照',
                  probe.suspension?.supported && '挂起',
                  probe.capabilities?.gpu && 'GPU',
                ]
                  .filter(Boolean)
                  .join(' / ')}
                {probe.skus?.length ? ` · ${probe.skus.length} 个可售规格` : ''}
              </div>
            </div>
            <div className="row gap-2" style={{ flexShrink: 0 }}>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={applySandboxDetection}
              >
                自动配置
              </button>
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => setProbeDismissed(true)}
              >
                忽略
              </button>
            </div>
          </div>
        )}
        {baseUrlsArray.fields.length > 0 && (
          <div className="col gap-2" style={{ marginTop: 12 }}>
            <div
              className="micro"
              style={{
                color: 'var(--fg-3, var(--fg-2))',
                lineHeight: 1.5,
                textTransform: 'none',
                letterSpacing: 0,
              }}
            >
              {t('register.origin.env.extra')}
            </div>
            {baseUrlsArray.fields.map((row, i) => (
              <div
                className="row gap-2"
                key={row.id}
                style={{ alignItems: 'center' }}
              >
                <input
                  className="input mono"
                  style={{ flex: 3 }}
                  placeholder={t('register.origin.env.name.ph')}
                  {...register(`baseUrls.${i}.name`)}
                />
                <input
                  className="input mono"
                  style={{ flex: 7 }}
                  placeholder={t('register.origin.env.url.ph')}
                  {...register(`baseUrls.${i}.url`)}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-ghost btn-icon"
                  onClick={() => baseUrlsArray.remove(i)}
                  aria-label="remove"
                >
                  <XIcons.IconTrash size={12} />
                </button>
              </div>
            ))}
            {errors.baseUrls && (
              <div
                className="micro"
                style={{
                  color: 'var(--danger, #e5484d)',
                  textTransform: 'none',
                  letterSpacing: 0,
                }}
              >
                {t('register.origin.env.error')}
              </div>
            )}
          </div>
        )}
      </Panel>

      <Panel
        title={t('register.origin.auth')}
        subtitle={t('register.origin.auth.sub')}
      >
        <Controller
          name="authType"
          control={control}
          render={({ field }) => (
            <div
              className="row gap-2"
              style={{ flexWrap: 'wrap', marginBottom: 14 }}
            >
              {AUTH_TYPES.map((authT) => (
                <button
                  type="button"
                  key={authT}
                  className={`btn btn-sm ${field.value === authT ? 'btn-primary' : ''}`}
                  onClick={() => field.onChange(authT)}
                >
                  {t(`register.origin.auth.${authT}`)}
                </button>
              ))}
            </div>
          )}
        />

        {authType !== 'NONE' && (
          <Row cols={authType === 'BEARER' ? 1 : 2}>
            {(authType === 'HEADER' || authType === 'QUERY') && (
              <Field
                label={
                  authType === 'HEADER'
                    ? t('register.origin.auth.headerName')
                    : t('register.origin.auth.queryName')
                }
              >
                <input
                  className="input mono"
                  placeholder={authType === 'HEADER' ? 'X-API-Key' : 'api_key'}
                  {...register('authConfig.key')}
                />
              </Field>
            )}
            <Field
              label={
                authType === 'BEARER'
                  ? t('register.origin.auth.bearer')
                  : t('register.origin.auth.secret')
              }
              hint={t('register.origin.auth.secret.hint')}
            >
              <input
                className="input mono"
                type="password"
                placeholder="sk_••••••"
                {...register('authConfig.value')}
              />
            </Field>
          </Row>
        )}
      </Panel>

      <Panel
        title={t('register.origin.privateHeaders')}
        subtitle={t('register.origin.privateHeaders.sub')}
        action={
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => phArray.append({ key: '', value: '' })}
          >
            <XIcons.IconPlus size={11} />{' '}
            {t('register.origin.privateHeaders.add')}
          </button>
        }
      >
        {phArray.fields.length === 0 ? (
          <EmptyState
            icon={<XIcons.IconShield size={20} />}
            text={t('register.origin.privateHeaders.empty')}
          />
        ) : (
          <div className="col gap-2">
            <div
              className="row"
              style={{
                padding: '6px 0',
                fontSize: 11,
                color: 'var(--fg-4)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                gap: 12,
              }}
            >
              <span style={{ flex: 5 }}>
                {t('register.origin.privateHeaders.col.name')}
              </span>
              <span style={{ flex: 6 }}>
                {t('register.origin.privateHeaders.col.value')}
              </span>
              <span style={{ width: 28 }} />
            </div>
            {phArray.fields.map((row, i) => (
              <div
                key={row.id}
                className="row gap-3"
                style={{ alignItems: 'center' }}
              >
                <input
                  className="input mono"
                  style={{ flex: 5 }}
                  placeholder="X-Custom-Header"
                  {...register(`privateHeaders.${i}.key`)}
                />
                <input
                  className="input mono"
                  style={{ flex: 6 }}
                  type="password"
                  placeholder="value"
                  {...register(`privateHeaders.${i}.value`)}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-ghost btn-icon"
                  onClick={() => phArray.remove(i)}
                >
                  <XIcons.IconTrash size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};

export const DirectModeNotice = () => {
  const { t } = useT();
  const parts = t('register.origin.directNotice.body').split('{PROXY}');
  return (
    <div className="card" style={{ padding: 32, textAlign: 'center' }}>
      <XIcons.IconLayers size={28} style={{ color: 'var(--fg-4)' }} />
      <h3 style={{ margin: '12px 0 6px', fontSize: 16, fontWeight: 500 }}>
        {t('register.origin.directNotice.title')}
      </h3>
      <p
        style={{
          color: 'var(--fg-3)',
          fontSize: 13,
          lineHeight: 1.7,
          maxWidth: 480,
          margin: '0 auto',
        }}
      >
        {parts[0]}
        <span className="mono">PROXY</span>
        {parts[1] || ''}
      </p>
    </div>
  );
};

// ============ Endpoints section ============

// dnd-kit render-prop 包装：把 hook 关进组件里，renderRow 仍是纯函数。
const DragHandle = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
  });
  return children({ attributes, listeners, setNodeRef, isDragging });
};
const DropZone = ({ id, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return children({ setNodeRef, isOver });
};
// 对齐统一的小图标按钮（24×24 命中区，hover 高亮）——解决「图标太小/不平齐」。
// onPointerDown 阻断冒泡：避免在可拖拽的行上点按钮时误触发拖拽。
const ActBtn = ({ title, onClick, danger, children }) => (
  <button
    type="button"
    className="ep-act-btn"
    title={title}
    onClick={onClick}
    onPointerDown={(e) => e.stopPropagation()}
    style={danger ? { color: 'var(--err)' } : undefined}
  >
    {children}
  </button>
);

export const EndpointsSection = ({
  activeEndpointId,
  setActiveEndpointId,
  accessMode,
  onImportClick,
  parsing,
  padX = 0,
  listStickyTop = 0,
}) => {
  // 派生的 Sandbox 接口里有个「跳到承载配置的那条」的快捷入口，用事件传上来。
  React.useEffect(() => {
    const jump = (event) => {
      const id = event?.detail?.id;
      if (id) setActiveEndpointId(id);
    };
    window.addEventListener('xapi:jump-endpoint', jump);
    return () => window.removeEventListener('xapi:jump-endpoint', jump);
  }, [setActiveEndpointId]);
  const { t } = useT();
  const { toast } = XPrim.useToast();
  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();
  const epArray = useFieldArray({ control, name: 'endpoints' });
  const endpoints = watch('endpoints') || [];
  const activeIdx = endpoints.findIndex((e) => e.id === activeEndpointId);
  const activeIndex = activeIdx >= 0 ? activeIdx : 0;

  // 选中某端点时(含从问题徽标/提审检查跳转过来),把它滚进泳道可视区,确保「跳到具体位置」。
  React.useEffect(() => {
    if (!activeEndpointId) return;
    const el = document.querySelector(
      `.register-ep-list [data-ep-id="${activeEndpointId}"]`,
    );
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [activeEndpointId]);
  const [filter, setFilter] = React.useState('');
  // Staged endpoint removal. Uses a base-ui Dialog (not native window.confirm)
  // and the copy makes explicit this is a DRAFT change — the endpoint is only
  // really removed after the user clicks 更新当前版本 / 发布新版本.
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const confirmDelete = () => {
    if (!deleteTarget) return;
    const { i, ep } = deleteTarget;
    epArray.remove(i);
    if (ep.id === activeEndpointId) {
      const remaining = endpoints.filter((e) => e.id !== ep.id);
      setActiveEndpointId(remaining[0]?.id || null);
    }
    setDeleteTarget(null);
  };

  const addEndpoint = () => {
    const next = INITIAL_ENDPOINT();
    epArray.append(next);
    setActiveEndpointId(next.id);
  };
  const removeEndpoint = (idx) => {
    if (endpoints.length <= 1) return;
    const remaining = endpoints.filter((_, i) => i !== idx);
    epArray.remove(idx);
    setActiveEndpointId(remaining[0]?.id || null);
  };

  const filtered = filter
    ? endpoints
        .map((ep, i) => ({ ep, i }))
        .filter(({ ep }) =>
          `${ep.protocol === 'WEBSOCKET' ? 'ws websocket ' + (ep.wsConfig?.adapter || '') : ep.method} ${ep.path} ${ep.name} ${ep.group || ''}`
            .toLowerCase()
            .includes(filter.toLowerCase()),
        )
    : endpoints.map((ep, i) => ({ ep, i }));

  // ── 选择 / 分组 / 工具 状态 ───────────────────────────────────────────────
  const [collapsedGroups, setCollapsedGroups] = React.useState({});
  const toggleGroup = (name) =>
    setCollapsedGroups((c) => ({ ...c, [name]: !c[name] }));
  const [extraGroups, setExtraGroups] = React.useState([]); // 新建但还没加端点的空分组
  const [renamingGroup, setRenamingGroup] = React.useState(null);

  // Paw 风格：未分组端点平铺在顶部（无 header），命名分组作为可折叠「文件夹」在下面。
  const grouped = (() => {
    const order = [];
    const map = new Map();
    for (const g of extraGroups) {
      if (!map.has(g)) {
        map.set(g, []);
        order.push(g);
      }
    }
    for (const item of filtered) {
      const g = (item.ep.group || '').trim();
      if (!map.has(g)) {
        map.set(g, []);
        order.push(g);
      }
      map.get(g).push(item);
    }
    return {
      ungrouped: map.get('') || [],
      named: order
        .filter((n) => n !== '')
        .map((name) => ({ name, items: map.get(name) })),
    };
  })();
  const allGroupNames = Array.from(
    new Set([
      ...extraGroups,
      ...endpoints.map((e) => (e.group || '').trim()).filter(Boolean),
    ]),
  );

  // ── 拖拽（dnd-kit）：拖端点到分组文件夹 / 顶部「未分组」区即可移动 ──────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const [draggingId, setDraggingId] = React.useState(null);
  const draggingEp = draggingId
    ? endpoints.find((e) => e.id === draggingId)
    : null;
  const onDragEnd = (e) => {
    setDraggingId(null);
    const overId = e.over && e.over.id != null ? String(e.over.id) : '';
    const m = /^group:(.*)$/.exec(overId);
    if (!m || !e.active) return;
    const target = m[1] === '__ungrouped__' ? '' : m[1];
    moveEndpointToGroup(String(e.active.id), target);
  };

  // 单行渲染（未分组 + 分组内复用）。拖拽手柄 + 删除在 hover 显形，保持干净。
  const renderRow = ({ ep, i }, inGroup) => {
    const hasError = errors.endpoints && errors.endpoints[i];
    const isWs = ep.protocol === 'WEBSOCKET';
    // AI 端点缺模型 = 上架前必须补的「警告」。草稿态 schema 宽松不报硬错,但仍要标出来,
    // 让用户提交前就知道哪几个待配模型(红=硬错阻断保存;黄=待补,不阻断草稿保存)。WS 端点无此语义。
    const aiNeedsModel =
      !isWs &&
      ((ep.aiInterfaceSpecId &&
        ep.billingType !== 'PER_TOKEN' &&
        !(ep.servedModel && String(ep.servedModel).trim())) ||
        (ep.billingType === 'PER_TOKEN' &&
          !(ep.aiConfig?.modelPricing && ep.aiConfig.modelPricing.length)));
    const warn = !hasError && aiNeedsModel;
    const aiSpec = !isWs && ep.aiInterfaceSpecId;
    // WS 端点副行人话:$X/min · <adapter>(替代 HTTP 端点的 path 行,别显示成空)。
    const wsPerMin = ep.wsBilling?.perMinuteUsd;
    const wsSubline = isWs
      ? [
          wsPerMin != null && wsPerMin !== '' ? `$${wsPerMin}/min` : null,
          ep.wsConfig?.adapter,
        ]
          .filter(Boolean)
          .join(' · ') || 'WebSocket'
      : null;
    const canRemove = endpoints.length > 1;
    const active = ep.id === activeEndpointId;
    return (
      <DragHandle id={ep.id} key={ep.id}>
        {({ listeners, setNodeRef, isDragging }) => (
          <div
            ref={setNodeRef}
            className="register-ep-row"
            data-ep-id={ep.id}
            onClick={() => setActiveEndpointId(ep.id)}
            {...listeners}
            style={{
              padding: `8px 10px 8px ${inGroup ? 22 : 10}px`,
              background: active ? 'var(--bg-3)' : 'transparent',
              borderLeft: active
                ? '2px solid var(--fg-0)'
                : '2px solid transparent',
              borderBottom: '1px solid var(--line)',
              cursor: 'grab',
              touchAction: 'none',
              opacity: isDragging ? 0.35 : ep.hidden ? 0.6 : 1,
            }}
          >
            <div className="row" style={{ alignItems: 'center', gap: 7 }}>
              {/* 拖拽提示（整行可拖） */}
              <span
                className="ep-drag-handle"
                style={{
                  color: 'var(--fg-4)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <XIcons.IconDots size={13} />
              </span>
              {isWs ? (
                <span
                  className="method"
                  style={{
                    flexShrink: 0,
                    background: 'rgba(120,80,220,0.15)',
                    color: 'rgb(150,110,240)',
                    border: '1px solid rgba(120,80,220,0.35)',
                  }}
                >
                  WS
                </span>
              ) : (
                <XPrim.MethodChip method={ep.method || 'GET'} />
              )}
              <span
                style={{
                  fontSize: 12.5,
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: ep.name ? 'var(--fg-1)' : 'var(--fg-2)',
                }}
              >
                {ep.name || t('register.endpoints.unnamed')}
              </span>
              {ep.hidden && (
                <span
                  className="micro"
                  style={{
                    flexShrink: 0,
                    padding: '1px 5px',
                    borderRadius: 3,
                    background: 'var(--bg-3)',
                    color: 'var(--fg-3)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    textTransform: 'none',
                    letterSpacing: 0,
                  }}
                >
                  <XIcons.IconEyeOff size={10} /> 隐藏
                </span>
              )}
              {hasError && (
                <span
                  title="该端点有校验错误，需修正"
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: 'var(--err)',
                    flexShrink: 0,
                  }}
                />
              )}
              {warn && (
                <span
                  title="AI 端点待配置服务模型（上架前必须）"
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: 'var(--warn, #e08e0b)',
                    flexShrink: 0,
                  }}
                />
              )}
              <div
                className="ep-row-actions"
                style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
              >
                <ActBtn
                  title={canRemove ? '删除' : '至少保留 1 个 endpoint'}
                  danger
                  onClick={(e) => {
                    e.stopPropagation();
                    if (canRemove) setDeleteTarget({ i, ep });
                  }}
                >
                  <XIcons.IconTrash size={13} />
                </ActBtn>
              </div>
            </div>
            <div
              className="mono"
              style={{
                fontSize: 11,
                color: isWs || ep.path ? 'var(--fg-3)' : 'var(--fg-2)',
                marginTop: 3,
                marginLeft: 27,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {isWs ? wsSubline : ep.path || '/—'}
            </div>
            {aiSpec && (
              <div style={{ marginTop: 5, marginLeft: 27, fontSize: 10.5 }}>
                <span
                  style={{
                    padding: '1px 5px',
                    borderRadius: 3,
                    background: 'rgba(0,150,200,0.10)',
                    color: 'rgb(0,90,140)',
                    fontWeight: 500,
                  }}
                >
                  ⚡ {aiSpec}
                </span>
              </div>
            )}
          </div>
        )}
      </DragHandle>
    );
  };

  // 写回 endpoints（统一标 dirty + 校验）
  const mutateEndpoints = (fn) => {
    const eps = getValues('endpoints') || [];
    setValue('endpoints', eps.map(fn), {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  // 分组操作
  const addEndpointToGroup = (groupName) => {
    const next = INITIAL_ENDPOINT();
    next.group = groupName || '';
    epArray.append(next);
    setActiveEndpointId(next.id);
    if (groupName) setCollapsedGroups((c) => ({ ...c, [groupName]: false }));
  };
  // 文件夹式新建：建一个唯一名的空分组并立即进入内联重命名（像 Finder 新建文件夹）
  const createGroupQuick = () => {
    let name = '新分组';
    let n = 1;
    while (allGroupNames.includes(name)) {
      n += 1;
      name = `新分组 ${n}`;
    }
    setExtraGroups((a) => [...a, name]);
    setCollapsedGroups((c) => ({ ...c, [name]: false }));
    setRenamingGroup(name);
  };
  const renameGroup = (oldName, raw) => {
    const newName = (raw || '').trim();
    setRenamingGroup(null);
    if (!newName || newName === oldName) return;
    mutateEndpoints((ep) =>
      (ep.group || '').trim() === oldName ? { ...ep, group: newName } : ep,
    );
    setExtraGroups((a) =>
      Array.from(new Set(a.map((g) => (g === oldName ? newName : g)))),
    );
  };
  const deleteGroup = (name) => {
    mutateEndpoints((ep) =>
      (ep.group || '').trim() === name ? { ...ep, group: '' } : ep,
    );
    setExtraGroups((a) => a.filter((g) => g !== name));
  };
  const moveEndpointToGroup = (epId, groupName) => {
    mutateEndpoints((ep) =>
      ep.id === epId ? { ...ep, group: groupName } : ep,
    );
  };

  return (
    // 去掉外框：master/detail 直接铺满,靠内部 hairline(toolbar 下边线 + list
    // 右边线)做区域划分 —— 有线条但不过度框住(#13)。
    <div style={{ padding: 0 }}>
      {/* Staged-removal confirm — base-ui Dialog, makes the draft semantics explicit */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth={440}
      >
        <DialogHeader title="移除端点" onClose={() => setDeleteTarget(null)} />
        <DialogBody>
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            将从草稿中移除端点{' '}
            <b>{deleteTarget?.ep?.name || deleteTarget?.ep?.path || ''}</b>。
          </div>
          <div
            className="micro"
            style={{
              marginTop: 12,
              color: 'var(--fg-3)',
              lineHeight: 1.7,
              textTransform: 'none',
              letterSpacing: 0,
            }}
          >
            这只是从当前编辑中移除，<b>还没有真正删除</b>。需要点击底部的{' '}
            <b>「更新当前版本」</b>或<b>「发布新版本」</b>保存后才会生效。
          </div>
        </DialogBody>
        <DialogFooter>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setDeleteTarget(null)}
          >
            取消
          </button>
          <button
            type="button"
            className="btn btn-sm"
            style={{ color: 'var(--err)', borderColor: 'var(--err)' }}
            onClick={confirmDelete}
          >
            移除
          </button>
        </DialogFooter>
      </Dialog>
      <div
        className="register-ep-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: '300px 1fr',
          // listStickyTop>0(工作区):把 master/detail 整块钉成一屏高,左泳道头部+搜索常驻、
          // 列表内部滚动,右侧详情独立滚动(见用户反馈"固定左、右侧滚动")。容器直接贴在工具栏
          // 下沿(无顶距),故高度只减 listStickyTop。
          ...(listStickyTop
            ? { height: `calc(100vh - ${listStickyTop}px)`, overflow: 'hidden' }
            : {}),
        }}
      >
        {/* List = 文件夹式泳道：标题 + 新建(端点/分组) + 工具，全部在左栏内处理 */}
        <div
          className="register-ep-list"
          style={{
            borderRight: '1px solid var(--line)',
            display: 'flex',
            flexDirection: 'column',
            ...(listStickyTop
              ? { height: '100%', minHeight: 0 }
              : { maxHeight: 720 }),
          }}
        >
          {/* 泳道头：接口数 + 新建▾(端点/分组) + 工具▾ */}
          <div
            className="row between"
            style={{
              padding: '10px 12px',
              borderBottom: '1px solid var(--line)',
              gap: 6,
              flexWrap: 'wrap',
            }}
          >
            <div className="micro" style={{ minWidth: 0 }}>
              {t('register.endpoints.title', { N: endpoints.length })}
            </div>
            <div className="row gap-1" style={{ flexWrap: 'wrap' }}>
              {onImportClick && (
                <button
                  type="button"
                  className="btn btn-sm btn-icon"
                  title={t('register.tools.importOpenapi')}
                  onClick={onImportClick}
                  disabled={parsing}
                >
                  {parsing ? (
                    <div className="spinner" />
                  ) : (
                    <XIcons.IconDownload size={12} />
                  )}
                </button>
              )}
              {/* 新建：端点 / 分组（像新建文件 / 文件夹）—— Base UI Menu */}
              <Menu
                triggerClassName="btn btn-sm btn-primary"
                minWidth={168}
                trigger={
                  <>
                    <XIcons.IconPlus size={11} />
                    <span>新建 ▾</span>
                  </>
                }
              >
                <MenuItem icon={XIcons.IconCode} onClick={addEndpoint}>
                  新建端点
                </MenuItem>
                <MenuItem icon={XIcons.IconLayers} onClick={createGroupQuick}>
                  新建分组
                </MenuItem>
              </Menu>
            </div>
          </div>
          <div style={{ padding: 10, borderBottom: '1px solid var(--line)' }}>
            <div
              className="row hairline gap-2"
              style={{
                padding: '0 8px',
                height: 30,
                borderRadius: 5,
                background: 'var(--bg-2)',
              }}
            >
              <XIcons.IconSearch size={12} style={{ color: 'var(--fg-4)' }} />
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={t('register.endpoints.filter')}
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  fontSize: 12,
                }}
              />
            </div>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 && (
              <div
                style={{
                  padding: 24,
                  textAlign: 'center',
                  color: 'var(--fg-4)',
                  fontSize: 12,
                }}
              >
                {t('register.endpoints.none')}
              </div>
            )}
            <DndContext
              sensors={sensors}
              onDragStart={(e) => setDraggingId(String(e.active.id))}
              onDragCancel={() => setDraggingId(null)}
              onDragEnd={onDragEnd}
            >
              {/* 未分组：平铺在顶部，本身是放置区（拖到这里 = 取消分组） */}
              <DropZone id="group:__ungrouped__">
                {({ setNodeRef, isOver }) => (
                  <div
                    ref={setNodeRef}
                    style={{
                      background: isOver
                        ? 'var(--accent-soft, rgba(245,165,36,0.10))'
                        : undefined,
                      minHeight: draggingId ? 30 : 0,
                    }}
                  >
                    {draggingId && grouped.ungrouped.length === 0 && (
                      <div
                        className="micro"
                        style={{
                          padding: '8px 12px',
                          color: 'var(--fg-4)',
                          textAlign: 'center',
                        }}
                      >
                        拖到此处 = 移出分组
                      </div>
                    )}
                    {grouped.ungrouped.map((it) => renderRow(it, false))}
                  </div>
                )}
              </DropZone>

              {/* 命名分组：可折叠文件夹，整块是放置区 */}
              {grouped.named.map((grp) => {
                const collapsed = collapsedGroups[grp.name];
                return (
                  <DropZone id={`group:${grp.name}`} key={grp.name}>
                    {({ setNodeRef, isOver }) => (
                      <div
                        ref={setNodeRef}
                        style={{
                          background: isOver
                            ? 'var(--accent-soft, rgba(245,165,36,0.10))'
                            : undefined,
                          outline: isOver
                            ? '1px dashed var(--accent, #f5a524)'
                            : 'none',
                          outlineOffset: -2,
                        }}
                      >
                        <div
                          className="register-ep-grouphdr"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 7,
                            padding: '8px 10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--line)',
                          }}
                          onClick={() => toggleGroup(grp.name)}
                        >
                          {collapsed ? (
                            <XIcons.IconChevronRight
                              size={14}
                              style={{ color: 'var(--fg-4)', flexShrink: 0 }}
                            />
                          ) : (
                            <XIcons.IconChevronDown
                              size={14}
                              style={{ color: 'var(--fg-4)', flexShrink: 0 }}
                            />
                          )}
                          <XIcons.IconLayers
                            size={15}
                            style={{ color: 'var(--fg-2)', flexShrink: 0 }}
                          />
                          {renamingGroup === grp.name ? (
                            <input
                              autoFocus
                              defaultValue={grp.name}
                              onClick={(e) => e.stopPropagation()}
                              onBlur={(e) =>
                                renameGroup(grp.name, e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter')
                                  renameGroup(grp.name, e.target.value);
                                if (e.key === 'Escape') setRenamingGroup(null);
                              }}
                              className="input"
                              style={{
                                flex: 1,
                                fontSize: 13,
                                padding: '3px 8px',
                              }}
                            />
                          ) : (
                            <span
                              style={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: 13,
                                fontWeight: 600,
                                color: 'var(--fg-1)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {grp.name}
                            </span>
                          )}
                          {/* hover 操作在左、数量常驻在右——不 hover 时数字稳定靠右 */}
                          <div
                            className="ep-row-actions"
                            style={{ display: 'flex', gap: 1, flexShrink: 0 }}
                          >
                            <ActBtn
                              title="新增端点到本组"
                              onClick={(e) => {
                                e.stopPropagation();
                                addEndpointToGroup(grp.name);
                              }}
                            >
                              <XIcons.IconPlus size={14} />
                            </ActBtn>
                            <ActBtn
                              title="重命名分组"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRenamingGroup(grp.name);
                              }}
                            >
                              <XIcons.IconEdit size={13} />
                            </ActBtn>
                            <ActBtn
                              title="删除分组（端点移到未分组）"
                              danger
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteGroup(grp.name);
                              }}
                            >
                              <XIcons.IconTrash size={13} />
                            </ActBtn>
                          </div>
                          <span
                            className="num"
                            style={{
                              flexShrink: 0,
                              minWidth: 18,
                              textAlign: 'right',
                            }}
                          >
                            {grp.items.length}
                          </span>
                        </div>
                        {!collapsed && grp.items.length === 0 && (
                          <div
                            className="micro"
                            style={{
                              padding: '8px 12px 8px 28px',
                              color: 'var(--fg-4)',
                            }}
                          >
                            空分组 · 拖端点进来或点 + 新增
                          </div>
                        )}
                        {!collapsed &&
                          grp.items.map((it) => renderRow(it, true))}
                      </div>
                    )}
                  </DropZone>
                );
              })}
              <DragOverlay dropAnimation={null}>
                {draggingEp ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 7,
                      padding: '8px 12px',
                      background: 'var(--bg-1)',
                      border: '1px solid var(--accent, #f5a524)',
                      borderRadius: 6,
                      boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                      fontSize: 12.5,
                      maxWidth: 280,
                    }}
                  >
                    <XPrim.MethodChip method={draggingEp.method || 'GET'} />
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {draggingEp.name || t('register.endpoints.unnamed')}
                    </span>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        </div>

        {/* Detail —— 工作区(listStickyTop)下:列固定一屏高、不滚;编辑器内部 path+tabs 固定、内容滚动 */}
        <div
          style={{
            paddingRight: padX,
            minWidth: 0,
            ...(listStickyTop
              ? {
                  minHeight: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }
              : {}),
          }}
        >
          {endpoints.length > 0 ? (
            <EndpointEditor
              key={activeEndpointId || activeIndex}
              index={activeIndex}
              accessMode={accessMode}
              onRemove={() => removeEndpoint(activeIndex)}
              canRemove={endpoints.length > 1}
              fillHeight={!!listStickyTop}
            />
          ) : (
            <div
              style={{
                flex: 1,
                display: 'grid',
                placeItems: 'center',
                minHeight: 240,
                color: 'var(--fg-4)',
                fontSize: 13,
              }}
            >
              暂无端点，请点击左侧“新建”添加端点
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ 批量改价（工具）独立弹窗 ============
// 展示完整端点（含分组），在弹窗内勾选适配的接口/分组，批量设单价。
// 暂时仅支持 PER_CALL（其余计费类型展示但不可选，标注原因）。Base UI 组件。
export const BatchPriceDialog = ({ open, onClose }) => {
  const { watch, setValue, getValues } = useFormContext();
  const { toast } = XPrim.useToast();
  const endpoints = watch('endpoints') || [];
  const [selected, setSelected] = React.useState(() => new Set());
  const [price, setPrice] = React.useState('');
  React.useEffect(() => {
    if (open) {
      setSelected(new Set());
      setPrice('');
    }
  }, [open]);

  // WS 端点按连接时长计费，不参与 PER_CALL 批量改价（即便 form 里残留 billingType='PER_CALL'）。
  const isPerCall = (ep) =>
    ep.protocol !== 'WEBSOCKET' &&
    (ep.billingType || 'PER_CALL') === 'PER_CALL';
  // 分组：未分组在前，命名分组在后
  const grouped = (() => {
    const order = [];
    const map = new Map();
    endpoints.forEach((ep, i) => {
      const g = (ep.group || '').trim();
      if (!map.has(g)) {
        map.set(g, []);
        order.push(g);
      }
      map.get(g).push({ ep, i });
    });
    return {
      ungrouped: map.get('') || [],
      named: order
        .filter((n) => n !== '')
        .map((name) => ({ name, items: map.get(name) })),
    };
  })();
  const perCallIds = endpoints.filter(isPerCall).map((e) => e.id);
  const allSelected =
    perCallIds.length > 0 && perCallIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0;

  const toggle = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const toggleAll = (on) => setSelected(on ? new Set(perCallIds) : new Set());
  const toggleGroup = (items, on) =>
    setSelected((s) => {
      const n = new Set(s);
      items
        .filter(({ ep }) => isPerCall(ep))
        .forEach(({ ep }) => (on ? n.add(ep.id) : n.delete(ep.id)));
      return n;
    });
  const groupState = (items) => {
    const ids = items.filter(({ ep }) => isPerCall(ep)).map(({ ep }) => ep.id);
    const sel = ids.filter((id) => selected.has(id));
    return {
      all: ids.length > 0 && sel.length === ids.length,
      some: sel.length > 0 && sel.length < ids.length,
      hasPerCall: ids.length > 0,
    };
  };

  const apply = () => {
    const num = parseFloat(price);
    if (price === '' || isNaN(num) || num < 0) {
      toast('请输入有效单价');
      return;
    }
    if (!someSelected) {
      toast('请先勾选接口');
      return;
    }
    const eps = getValues('endpoints') || [];
    let cnt = 0;
    const next = eps.map((ep) => {
      if (selected.has(ep.id) && isPerCall(ep)) {
        cnt += 1;
        return { ...ep, costPerCall: num };
      }
      return ep;
    });
    setValue('endpoints', next, { shouldDirty: true, shouldValidate: true });
    toast(`已为 ${cnt} 个接口设为 $${num} / 次`);
    onClose();
  };

  const EpRow = ({ ep, i, indent }) => {
    const per = isPerCall(ep);
    const isWs = ep.protocol === 'WEBSOCKET';
    return (
      <div
        onClick={() => per && toggle(ep.id)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: `8px 14px 8px ${indent ? 30 : 14}px`,
          borderBottom: '1px solid var(--line)',
          opacity: per ? 1 : 0.5,
          cursor: per ? 'pointer' : 'default',
        }}
      >
        <Checkbox
          checked={selected.has(ep.id)}
          disabled={!per}
          onCheckedChange={() => per && toggle(ep.id)}
        />
        {isWs ? (
          <span
            className="method"
            style={{
              flexShrink: 0,
              background: 'rgba(120,80,220,0.15)',
              color: 'rgb(150,110,240)',
              border: '1px solid rgba(120,80,220,0.35)',
            }}
          >
            WS
          </span>
        ) : (
          <XPrim.MethodChip method={ep.method || 'GET'} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12.5,
              color: ep.name ? 'var(--fg-1)' : 'var(--fg-2)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {ep.name || '(未命名)'}
          </div>
          <div
            className="mono"
            style={{
              fontSize: 11,
              color: isWs || ep.path ? 'var(--fg-3)' : 'var(--fg-2)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {isWs ? ep.wsConfig?.adapter || 'WebSocket' : ep.path || '/—'}
          </div>
        </div>
        {per ? (
          <span
            className="mono"
            style={{ fontSize: 12, color: 'var(--fg-2)', flexShrink: 0 }}
          >
            {isFreeEndpoint(ep) ? 'free' : `$${Number(ep.costPerCall || 0)}`}
          </span>
        ) : (
          <span
            className="micro"
            style={{ color: 'var(--fg-4)', flexShrink: 0 }}
          >
            {isWs ? 'WebSocket · 按时长计费' : `${ep.billingType} · 暂不支持`}
          </span>
        )}
      </div>
    );
  };

  if (!open) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth={620} maxHeight="84vh">
      <DialogHeader
        title="批量改价 · 按调用计费 (PER_CALL)"
        onClose={onClose}
      />
      <DialogBody padding={0}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            borderBottom: '1px solid var(--line)',
            background: 'var(--bg-2)',
          }}
        >
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onCheckedChange={toggleAll}
            disabled={perCallIds.length === 0}
          />
          <span style={{ flex: 1, fontSize: 12 }}>
            全选 PER_CALL 接口（共 {perCallIds.length} 个）
          </span>
          <span
            className="micro"
            style={{
              color: someSelected ? 'var(--accent, #f5a524)' : 'var(--fg-4)',
            }}
          >
            已选 {selected.size}
          </span>
        </div>
        <div>
          {endpoints.length === 0 && (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                color: 'var(--fg-4)',
                fontSize: 12,
              }}
            >
              暂无端点
            </div>
          )}
          {grouped.ungrouped.map(({ ep, i }) => (
            <EpRow key={ep.id} ep={ep} i={i} />
          ))}
          {grouped.named.map((grp) => {
            const gs = groupState(grp.items);
            return (
              <div key={grp.name}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 14px',
                    background: 'var(--bg-2)',
                    borderBottom: '1px solid var(--line)',
                  }}
                >
                  <Checkbox
                    checked={gs.all}
                    indeterminate={gs.some}
                    disabled={!gs.hasPerCall}
                    onCheckedChange={(on) => toggleGroup(grp.items, on)}
                  />
                  <XIcons.IconLayers
                    size={15}
                    style={{ color: 'var(--fg-2)', flexShrink: 0 }}
                  />
                  <span
                    style={{
                      flex: 1,
                      fontSize: 12.5,
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {grp.name}
                  </span>
                  <span className="num">{grp.items.length}</span>
                </div>
                {grp.items.map(({ ep, i }) => (
                  <EpRow key={ep.id} ep={ep} i={i} indent />
                ))}
              </div>
            );
          })}
        </div>
      </DialogBody>
      <DialogFooter>
        <div
          className="row gap-1"
          style={{ alignItems: 'center', marginRight: 'auto' }}
        >
          <span className="mono" style={{ color: 'var(--fg-3)' }}>
            $
          </span>
          <input
            type="number"
            min="0"
            step="0.001"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="单价 USD"
            className="input mono"
            style={{ width: 130, padding: '5px 10px' }}
          />
          <span className="micro" style={{ color: 'var(--fg-4)' }}>
            / 次调用
          </span>
        </div>
        <button type="button" className="btn btn-sm" onClick={onClose}>
          取消
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={apply}
          disabled={!someSelected || price === ''}
        >
          应用到 {selected.size} 个
        </button>
      </DialogFooter>
    </Dialog>
  );
};

// ============ Endpoints overview dialog (table view + batch pricing) ============
//
// Opened from the left sidebar's "批量价格设置" tool. Shows all endpoints in a
// flat table — path / display name / billing type / input price / output price.
// Column headers for Billing and Price expose a popover with bulk-apply ops:
//   • Billing column header   → "Set all to PER_CALL / PER_TOKEN / PER_RESOURCE"
//   • Price column header     → batch-set PER_CALL price + PER_TOKEN default model
// Clicking any row jumps the editor to that endpoint (and closes the dialog).
// Mirrors xapi-new's RegisterPage column-header popovers, adapted to our
// master-detail layout (no in-table inline editing — that lives in the card view).

export const EndpointsOverviewDialog = ({
  open,
  onClose,
  accessMode,
  onJumpToEndpoint,
}) => {
  const { watch, setValue, getValues } = useFormContext();
  const { toast } = XPrim.useToast();
  const endpoints = watch('endpoints') || [];

  const [batchPerCallPrice, setBatchPerCallPrice] = React.useState('');
  const [batchGroup, setBatchGroup] = React.useState('');

  // ── 行勾选：批量操作作用于「选中项」；一个都没勾时退回「全部」 ──────────────
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  // 关闭时清空选择，下次打开是干净状态
  React.useEffect(() => {
    if (!open) setSelectedIds(new Set());
  }, [open]);
  const someSelected = selectedIds.size > 0;
  const allSelected =
    endpoints.length > 0 && endpoints.every((e) => selectedIds.has(e.id));
  const toggleOne = (id) =>
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const toggleAll = () =>
    setSelectedIds(
      allSelected ? new Set() : new Set(endpoints.map((e) => e.id)),
    );
  // 批量作用域：选中项优先，否则全部。返回 id 集合 + 中文范围描述（给 toast）。
  const batchScope = () => {
    const list = someSelected
      ? endpoints.filter((e) => selectedIds.has(e.id))
      : endpoints;
    const ids = new Set(list.map((e) => e.id));
    const label = someSelected ? `选中 ${ids.size} 个` : `全部 ${ids.size} 个`;
    return { ids, label };
  };

  // Only PER_CALL is batch-settable. PER_TOKEN has multi-model rows; PER_RESOURCE
  // needs a Resource-count JSON path alongside the price — both need full-fidelity
  // editing in the per-endpoint editor (row click → jump).
  const [billingMenuOpen, setBillingMenuOpen] = React.useState(false);
  const [priceMenuOpen, setPriceMenuOpen] = React.useState(false);
  // Per-row inline state: only one row can be editing/menu-open at a time.
  // editingPriceId tracks which endpoint's price cell is in input mode.
  // rowBillingMenuId tracks which row's billing pill has its dropdown open.
  const [editingPriceId, setEditingPriceId] = React.useState(null);
  const [draftPrice, setDraftPrice] = React.useState('');
  const [rowBillingMenuId, setRowBillingMenuId] = React.useState(null);
  // Refs for click-away handling — click anywhere outside the open popover
  // closes it (matches how every popover in the rest of the app behaves).
  const billingMenuRef = React.useRef(null);
  const priceMenuRef = React.useRef(null);
  const rowMenuRef = React.useRef(null);
  React.useEffect(() => {
    if (!billingMenuOpen && !priceMenuOpen && !rowBillingMenuId) return;
    const onDown = (e) => {
      if (
        billingMenuOpen &&
        billingMenuRef.current &&
        !billingMenuRef.current.contains(e.target)
      )
        setBillingMenuOpen(false);
      if (
        priceMenuOpen &&
        priceMenuRef.current &&
        !priceMenuRef.current.contains(e.target)
      )
        setPriceMenuOpen(false);
      if (
        rowBillingMenuId &&
        rowMenuRef.current &&
        !rowMenuRef.current.contains(e.target)
      )
        setRowBillingMenuId(null);
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        setBillingMenuOpen(false);
        setPriceMenuOpen(false);
        setRowBillingMenuId(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [billingMenuOpen, priceMenuOpen, rowBillingMenuId]);

  // ----- Single-row mutations (preserves the rest of the endpoint config) -----
  const setRowBillingType = (epId, billingType) => {
    const eps = getValues('endpoints') || [];
    const next = eps.map((ep) => {
      if (ep.id !== epId) return ep;
      if (billingType === 'PER_TOKEN') {
        return {
          ...ep,
          billingType,
          aiConfig: ep.aiConfig || DEFAULT_AI_CONFIG(),
        };
      }
      return { ...ep, billingType };
    });
    setValue('endpoints', next, { shouldDirty: true, shouldValidate: true });
    setRowBillingMenuId(null);
  };
  const commitRowPrice = (epId) => {
    const num = parseFloat(draftPrice);
    if (draftPrice === '' || isNaN(num) || num < 0) {
      toast('Enter a valid non-negative price');
      setEditingPriceId(null);
      setDraftPrice('');
      return;
    }
    // Only PER_CALL reaches this path; PER_TOKEN / PER_RESOURCE rows jump to
    // the editor instead of opening an inline input.
    const eps = getValues('endpoints') || [];
    const next = eps.map((ep) =>
      ep.id === epId && (ep.billingType || 'PER_CALL') === 'PER_CALL'
        ? { ...ep, costPerCall: num }
        : ep,
    );
    setValue('endpoints', next, { shouldDirty: true, shouldValidate: true });
    setEditingPriceId(null);
    setDraftPrice('');
  };
  const startEditPrice = (ep) => {
    const bt = ep.billingType || 'PER_CALL';
    // Only PER_CALL is inline-editable. PER_TOKEN (multi-model rows) and
    // PER_RESOURCE (price + JSON path) both need the full editor.
    if (bt !== 'PER_CALL') return;
    setDraftPrice(String(ep.costPerCall ?? ''));
    setEditingPriceId(ep.id);
  };

  // ----- Batch operations（作用于选中项；未勾选则全部）-----

  const applyBillingToAll = (billingType) => {
    const { ids, label } = batchScope();
    if (!ids.size) return;
    const eps = getValues('endpoints') || [];
    const next = eps.map((ep) => {
      if (!ids.has(ep.id)) return ep;
      if (billingType === 'PER_TOKEN') {
        return {
          ...ep,
          billingType,
          aiConfig: ep.aiConfig || DEFAULT_AI_CONFIG(),
        };
      }
      return { ...ep, billingType };
    });
    setValue('endpoints', next, { shouldDirty: true, shouldValidate: true });
    toast(`${label} → ${billingType}`);
    setBillingMenuOpen(false);
  };

  const applyPerCallPriceToAll = () => {
    const num = parseFloat(batchPerCallPrice);
    if (batchPerCallPrice === '' || isNaN(num) || num < 0) {
      toast('Enter a valid non-negative price');
      return;
    }
    const { ids } = batchScope();
    const eps = getValues('endpoints') || [];
    const matching = eps.filter(
      (ep) => ids.has(ep.id) && (ep.billingType || 'PER_CALL') === 'PER_CALL',
    );
    if (!matching.length) {
      toast('选中范围内没有 PER_CALL 端点');
      return;
    }
    const next = eps.map((ep) =>
      ids.has(ep.id) && (ep.billingType || 'PER_CALL') === 'PER_CALL'
        ? { ...ep, costPerCall: num }
        : ep,
    );
    setValue('endpoints', next, { shouldDirty: true, shouldValidate: true });
    toast(`已为 ${matching.length} 个 PER_CALL 端点设为 $${num}`);
    setBatchPerCallPrice('');
  };

  // 批量分组：把作用域内端点归入一个 group（留空 = 清除分组）
  const applyGroupToAll = () => {
    const { ids, label } = batchScope();
    if (!ids.size) return;
    const eps = getValues('endpoints') || [];
    const g = batchGroup.trim();
    setValue(
      'endpoints',
      eps.map((ep) => (ids.has(ep.id) ? { ...ep, group: g } : ep)),
      { shouldDirty: true, shouldValidate: true },
    );
    toast(g ? `${label}归入分组「${g}」` : `${label}已清除分组`);
  };

  // 批量隐藏/显示：把作用域内端点的 hidden 设为 true/false
  const applyHiddenToAll = (hidden) => {
    const { ids, label } = batchScope();
    if (!ids.size) return;
    const eps = getValues('endpoints') || [];
    setValue(
      'endpoints',
      eps.map((ep) => (ids.has(ep.id) ? { ...ep, hidden } : ep)),
      { shouldDirty: true, shouldValidate: true },
    );
    toast(hidden ? `已隐藏${label}` : `已显示${label}`);
  };

  // ----- Row price display -----
  const priceCell = (ep) => {
    if (isFreeEndpoint(ep)) return <span style={{ color: 'var(--ok)' }}>free</span>;
    const bt = ep.billingType || 'PER_CALL';
    if (bt === 'PER_CALL') {
      const v = Number(ep.costPerCall || 0);
      return v > 0 ? `$${v}` : <span style={{ color: 'var(--fg-4)' }}>—</span>;
    }
    if (bt === 'PER_TOKEN') {
      const ac = ep.aiConfig || {};
      const def = (ac.modelPricing || []).find(
        (r) => r.modelName === 'default',
      );
      if (!def) return <span style={{ color: 'var(--fg-4)' }}>—</span>;
      const inp = Number(def.inputPricePerToken || 0);
      const out = Number(def.outputPricePerToken || 0);
      return (
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-2)' }}>
          ${inp}
          <span style={{ color: 'var(--fg-4)' }}>/M in</span> · ${out}
          <span style={{ color: 'var(--fg-4)' }}>/M out</span>
        </span>
      );
    }
    if (bt === 'PER_RESOURCE') {
      const v = Number(ep.costPerResource || 0);
      return v > 0 ? (
        <span className="mono" style={{ fontSize: 11 }}>
          ${v}
          <span style={{ color: 'var(--fg-4)' }}>/each</span>
        </span>
      ) : (
        <span style={{ color: 'var(--fg-4)' }}>—</span>
      );
    }
    return <span style={{ color: 'var(--fg-4)' }}>—</span>;
  };

  const billingPillStyle = (bt) => {
    if (bt === 'PER_TOKEN')
      return {
        color: 'var(--ok)',
        background: 'rgba(46,204,113,0.08)',
        border: '1px solid rgba(46,204,113,0.28)',
      };
    if (bt === 'PER_RESOURCE')
      return {
        color: 'var(--warn)',
        background: 'rgba(245,158,11,0.08)',
        border: '1px solid rgba(245,158,11,0.28)',
      };
    return {
      color: 'var(--fg-2)',
      background: 'var(--bg-2)',
      border: '1px solid var(--line)',
    };
  };
  // Centralized short label so pill + dropdown + future surfaces all agree.
  // "per call / per token / per resource" — more readable than the bare token.
  const billingShortLabel = (bt) => {
    if (bt === 'PER_TOKEN') return 'per token';
    if (bt === 'PER_RESOURCE') return 'per resource';
    if (bt === 'DYNAMIC') return 'dynamic';
    return 'per call';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={900} responsive>
      <DialogHeader
        title={`端点总览 · ${endpoints.length} 个${someSelected ? ` · 已选 ${selectedIds.size}` : ''}`}
        onClose={onClose}
      />
      <DialogBody padding={16}>
        <div
          style={{
            color: 'var(--fg-3)',
            fontSize: 12,
            marginBottom: 12,
            lineHeight: 1.55,
          }}
        >
          点击任一行跳到该端点详情编辑器；点列头的 ⚙ 批量为所有端点设置 billing
          类型或价格。
        </div>

        {/* 批量工具条：分组 + 隐藏/显示（应用到全部端点）。改价/计费类型见列头下拉。 */}
        <div
          className="row gap-2"
          style={{
            flexWrap: 'wrap',
            alignItems: 'center',
            marginBottom: 12,
            padding: '10px 12px',
            background: 'var(--bg-2)',
            border: '1px solid var(--line)',
            borderRadius: 8,
          }}
        >
          <span className="micro" style={{ color: 'var(--fg-3)' }}>
            批量：
            <span
              style={{
                color: someSelected ? 'var(--accent, #f5a524)' : 'var(--fg-4)',
              }}
            >
              {someSelected
                ? `作用于选中 ${selectedIds.size} 个`
                : '作用于全部（勾选可只改部分）'}
            </span>
          </span>
          <input
            className="input"
            value={batchGroup}
            onChange={(e) => setBatchGroup(e.target.value)}
            placeholder="分组名（留空=清除）"
            style={{ width: 180, padding: '5px 10px', fontSize: 12 }}
          />
          <button
            type="button"
            className="btn btn-sm"
            onClick={applyGroupToAll}
          >
            全部归组
          </button>
          <div
            style={{
              width: 1,
              height: 18,
              background: 'var(--line)',
              margin: '0 4px',
            }}
          />
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => applyHiddenToAll(true)}
          >
            <XIcons.IconEyeOff size={11} /> 全部隐藏
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => applyHiddenToAll(false)}
          >
            全部显示
          </button>
        </div>

        {/* Outer card holds the rounded corner; header keeps `overflow:visible`
            so popovers can extend below it without being clipped, and the
            body has its own `overflow:auto` for row scrolling. */}
        <div
          className="hairline"
          style={{ borderRadius: 8, background: 'var(--bg-2)' }}
        >
          {/* Mobile-only top batch bar — column-header popovers are hidden on
              mobile, so surface the most common batch op (PER_CALL price)
              here in a friendly full-width control. */}
          {accessMode !== 'DIRECT' && (
            <div
              className="ep-batch-mobile-batchbtn"
              style={{
                padding: '10px 12px',
                borderBottom: '1px solid var(--line)',
                flexDirection: 'column',
                gap: 8,
                alignItems: 'stretch',
              }}
            >
              <div className="micro" style={{ color: 'var(--fg-3)' }}>
                批量设置 PER_CALL 价格（应用到所有 PER_CALL 端点）
              </div>
              <div className="row gap-1" style={{ alignItems: 'center' }}>
                <span
                  className="mono"
                  style={{ color: 'var(--fg-3)', fontSize: 12, width: 14 }}
                >
                  $
                </span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={batchPerCallPrice}
                  onChange={(e) => setBatchPerCallPrice(e.target.value)}
                  placeholder="0.001"
                  className="input mono"
                  style={{ flex: 1, padding: '6px 10px', fontSize: 13 }}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={applyPerCallPriceToAll}
                >
                  应用
                </button>
              </div>
              <div
                className="micro-mono"
                style={{
                  color: 'var(--fg-4)',
                  textTransform: 'none',
                  letterSpacing: 0,
                  fontSize: 10,
                  lineHeight: 1.4,
                }}
              >
                PER_TOKEN / PER_RESOURCE 端点点击对应行进入详情配置。
              </div>
            </div>
          )}

          {/* Desktop column-header row (popovers for Billing & Price columns).
              Hidden on mobile via .ep-batch-header media query. */}
          <div
            className="row ep-batch-header"
            style={{
              padding: '8px 12px',
              borderBottom: '1px solid var(--line)',
              fontSize: 10,
              color: 'var(--fg-4)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              alignItems: 'center',
              gap: 12,
              background: 'var(--bg-3)',
              borderRadius: '8px 8px 0 0',
              position: 'relative',
              overflow: 'visible',
            }}
          >
            <div className="col-select">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected && !allSelected;
                }}
                onChange={toggleAll}
                title={allSelected ? '取消全选' : '全选'}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <div className="col-method">方法</div>
            <div className="col-path">路径</div>
            <div className="col-name">名称</div>
            {accessMode !== 'DIRECT' && (
              <div className="col-billing" style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => {
                    setBillingMenuOpen((v) => !v);
                    setPriceMenuOpen(false);
                  }}
                  className="row gap-1"
                  style={{
                    alignItems: 'center',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    color: 'inherit',
                    cursor: 'pointer',
                    textTransform: 'inherit',
                    letterSpacing: 'inherit',
                    fontSize: 'inherit',
                  }}
                >
                  计费 <XIcons.IconChevronDown size={10} />
                </button>
                {billingMenuOpen && (
                  <div
                    ref={billingMenuRef}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: 4,
                      background: 'var(--bg-1)',
                      border: '1px solid var(--line)',
                      borderRadius: 6,
                      padding: 6,
                      zIndex: 20,
                      minWidth: 180,
                      boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
                    }}
                  >
                    <div
                      className="micro"
                      style={{ padding: '4px 8px', color: 'var(--fg-4)' }}
                    >
                      {someSelected
                        ? `批量设置选中 ${selectedIds.size} 个`
                        : '批量设置全部'}
                    </div>
                    <button
                      type="button"
                      onClick={() => applyBillingToAll('PER_CALL')}
                      className="btn btn-sm"
                      style={{
                        width: '100%',
                        justifyContent: 'flex-start',
                        marginTop: 2,
                      }}
                    >
                      Per call
                    </button>
                    <button
                      type="button"
                      onClick={() => applyBillingToAll('PER_TOKEN')}
                      className="btn btn-sm"
                      style={{
                        width: '100%',
                        justifyContent: 'flex-start',
                        marginTop: 2,
                      }}
                    >
                      Per token
                    </button>
                    <button
                      type="button"
                      onClick={() => applyBillingToAll('PER_RESOURCE')}
                      className="btn btn-sm"
                      style={{
                        width: '100%',
                        justifyContent: 'flex-start',
                        marginTop: 2,
                      }}
                    >
                      Per resource
                    </button>
                  </div>
                )}
              </div>
            )}
            {accessMode !== 'DIRECT' && (
              <div className="col-price" style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => {
                    setPriceMenuOpen((v) => !v);
                    setBillingMenuOpen(false);
                  }}
                  className="row gap-1"
                  style={{
                    alignItems: 'center',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    color: 'inherit',
                    cursor: 'pointer',
                    textTransform: 'inherit',
                    letterSpacing: 'inherit',
                    fontSize: 'inherit',
                  }}
                >
                  价格 <XIcons.IconChevronDown size={10} />
                </button>
                {priceMenuOpen && (
                  <div
                    ref={priceMenuRef}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: 4,
                      background: 'var(--bg-1)',
                      border: '1px solid var(--line)',
                      borderRadius: 6,
                      padding: 12,
                      zIndex: 25,
                      width: 280,
                      boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
                    }}
                  >
                    {/* PER_CALL bulk — only billing type that's safely batchable */}
                    <div
                      className="micro"
                      style={{ color: 'var(--fg-3)', marginBottom: 6 }}
                    >
                      {someSelected
                        ? '选中的 PER_CALL 端点'
                        : '所有 PER_CALL 端点'}
                    </div>
                    <div className="row gap-1" style={{ alignItems: 'center' }}>
                      <span
                        className="mono"
                        style={{
                          color: 'var(--fg-3)',
                          fontSize: 12,
                          width: 14,
                        }}
                      >
                        $
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={batchPerCallPrice}
                        onChange={(e) => setBatchPerCallPrice(e.target.value)}
                        placeholder="0.001"
                        className="input mono"
                        style={{ flex: 1, padding: '4px 8px', fontSize: 11 }}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={applyPerCallPriceToAll}
                      >
                        应用
                      </button>
                    </div>
                    {/* PER_TOKEN / PER_RESOURCE intentionally not batchable here —
                        PER_TOKEN has multi-model rows; PER_RESOURCE needs a JSON path
                        alongside the price. Both require the full per-endpoint editor. */}
                    <div
                      className="micro-mono"
                      style={{
                        marginTop: 14,
                        color: 'var(--fg-4)',
                        textTransform: 'none',
                        letterSpacing: 0,
                        lineHeight: 1.5,
                        fontSize: 10,
                      }}
                    >
                      PER_TOKEN / PER_RESOURCE
                      端点需在端点详情中单独配置（PER_TOKEN
                      各模型输入/输出单价；PER_RESOURCE 单价 + Resource count
                      JSON path）。点击对应行进入。
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="col-jump"></div>
          </div>

          {/* Rows */}
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {endpoints.length === 0 && (
              <div
                style={{
                  padding: 24,
                  textAlign: 'center',
                  color: 'var(--fg-4)',
                  fontSize: 12,
                }}
              >
                暂无端点
              </div>
            )}
            {endpoints.map((ep) => {
              const bt = ep.billingType || 'PER_CALL';
              const editingThis = editingPriceId === ep.id;
              const menuOpenThis = rowBillingMenuId === ep.id;
              // Click-away from the row jumps to detail; clicks on the
              // billing pill button / price cell button stopPropagation so
              // inline interactions don't trigger the jump.
              const jump = (e) => {
                e.stopPropagation();
                onJumpToEndpoint(ep.id);
              };
              return (
                <div
                  key={ep.id}
                  className="ep-batch-row"
                  // Hidden endpoints (= not exposed to marketplace, or
                  // soft-deleted) — dim the whole row so they're tellable
                  // at a glance from active ones. Matches the left-rail
                  // chip treatment (opacity 0.55 + eye-off icon).
                  style={ep.hidden ? { opacity: 0.55 } : undefined}
                  title={ep.hidden ? '已隐藏，不对外暴露' : undefined}
                >
                  {/* 行勾选：批量操作作用域 */}
                  <div className="col-select">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(ep.id)}
                      onChange={() => toggleOne(ep.id)}
                      onClick={(e) => e.stopPropagation()}
                      title="选中以批量操作"
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                  {/* Method / path / name → jump to detail */}
                  <button
                    type="button"
                    onClick={jump}
                    className="col-method"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                    }}
                  >
                    <XPrim.MethodChip method={ep.method} />
                  </button>
                  <button
                    type="button"
                    onClick={jump}
                    title={ep.path}
                    className="mono col-path"
                    style={{
                      fontSize: 12,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: 'var(--fg-1)',
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    {ep.path || (
                      <span style={{ color: 'var(--fg-2)' }}>/—</span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={jump}
                    title={ep.name}
                    className="col-name"
                    style={{
                      fontSize: 12,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      textAlign: 'left',
                      color: 'var(--fg-2)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {ep.hidden && (
                      <XIcons.IconEyeOff
                        size={11}
                        style={{ color: 'var(--fg-4)', flexShrink: 0 }}
                      />
                    )}
                    {ep.name || (
                      <span style={{ color: 'var(--fg-2)' }}>(unnamed)</span>
                    )}
                  </button>

                  {/* Billing pill → click opens per-row dropdown to switch type */}
                  {accessMode !== 'DIRECT' && (
                    <div
                      className="col-billing"
                      style={{ position: 'relative' }}
                      ref={menuOpenThis ? rowMenuRef : null}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRowBillingMenuId(menuOpenThis ? null : ep.id);
                        }}
                        className="mono"
                        style={{
                          fontSize: 10,
                          padding: '2px 6px',
                          borderRadius: 4,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          ...billingPillStyle(bt),
                        }}
                      >
                        {billingShortLabel(bt)}
                        <XIcons.IconChevronDown size={9} />
                      </button>
                      {menuOpenThis && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            marginTop: 4,
                            background: 'var(--bg-1)',
                            border: '1px solid var(--line)',
                            borderRadius: 6,
                            padding: 4,
                            zIndex: 25,
                            minWidth: 150,
                            boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
                          }}
                        >
                          {['PER_CALL', 'PER_TOKEN', 'PER_RESOURCE'].map(
                            (bv) => (
                              <button
                                key={bv}
                                type="button"
                                onClick={() => setRowBillingType(ep.id, bv)}
                                className="btn btn-sm"
                                style={{
                                  width: '100%',
                                  justifyContent: 'flex-start',
                                  marginTop: 2,
                                  background:
                                    bv === bt ? 'var(--bg-3)' : 'transparent',
                                  fontSize: 11,
                                }}
                              >
                                {billingShortLabel(bv)}
                              </button>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Price cell — varies by billing type.
                      • PER_CALL: inline editable (single $X value)
                      • PER_TOKEN: multi-model rows → jump to detail
                      • PER_RESOURCE: price + Resource-count JSON path → jump to detail */}
                  {accessMode !== 'DIRECT' && (
                    <div className="col-price" style={{ fontSize: 12 }}>
                      {bt === 'PER_TOKEN' ? (
                        <button
                          type="button"
                          onClick={jump}
                          title="点击进入端点配置 model 价格"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                            color: 'inherit',
                            fontSize: 12,
                          }}
                        >
                          {(() => {
                            const ac = ep.aiConfig || {};
                            const def = (ac.modelPricing || []).find(
                              (r) => r.modelName === 'default',
                            );
                            if (!def)
                              return (
                                <span
                                  className="mono"
                                  style={{ color: 'var(--fg-4)' }}
                                >
                                  — <XIcons.IconExternal size={10} />
                                </span>
                              );
                            return (
                              <span
                                className="mono"
                                style={{ fontSize: 11, color: 'var(--fg-2)' }}
                              >
                                ${Number(def.inputPricePerToken || 0)}
                                <span style={{ color: 'var(--fg-4)' }}>
                                  /M in
                                </span>{' '}
                                · ${Number(def.outputPricePerToken || 0)}
                                <span style={{ color: 'var(--fg-4)' }}>
                                  /M out
                                </span>
                                <XIcons.IconExternal
                                  size={10}
                                  style={{
                                    marginLeft: 4,
                                    color: 'var(--fg-4)',
                                  }}
                                />
                              </span>
                            );
                          })()}
                        </button>
                      ) : bt === 'PER_RESOURCE' ? (
                        // Resource-count JSON path is required alongside the price.
                        // Don't inline-edit — jump to detail so the user sees both fields.
                        <button
                          type="button"
                          onClick={jump}
                          title="进入端点详情配置（价格 + Resource count JSON path）"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                            color: 'inherit',
                            fontSize: 12,
                          }}
                        >
                          {(() => {
                            if (isFreeEndpoint(ep)) return 'free';
                            const v = Number(ep.costPerResource || 0);
                            const path = ep.resourceCountJsonPath || '';
                            return (
                              <span
                                className="mono"
                                style={{ fontSize: 11, color: 'var(--fg-2)' }}
                              >
                                {v > 0 ? (
                                  <>
                                    ${v}
                                    <span style={{ color: 'var(--fg-4)' }}>
                                      /each
                                    </span>
                                  </>
                                ) : (
                                  <span style={{ color: 'var(--fg-4)' }}>
                                    未设置
                                  </span>
                                )}
                                {path && (
                                  <span style={{ color: 'var(--fg-4)' }}>
                                    {' '}
                                    · {path}
                                  </span>
                                )}
                                <XIcons.IconExternal
                                  size={10}
                                  style={{
                                    marginLeft: 4,
                                    color: 'var(--fg-4)',
                                  }}
                                />
                              </span>
                            );
                          })()}
                        </button>
                      ) : editingThis ? (
                        // PER_CALL → inline numeric edit
                        <div
                          onClick={(e) => e.stopPropagation()}
                          className="row gap-1"
                          style={{ alignItems: 'center' }}
                        >
                          <span
                            className="mono"
                            style={{ color: 'var(--fg-3)', fontSize: 12 }}
                          >
                            $
                          </span>
                          <input
                            autoFocus
                            type="number"
                            min="0"
                            step="0.01"
                            value={draftPrice}
                            onChange={(e) => setDraftPrice(e.target.value)}
                            onBlur={() => commitRowPrice(ep.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitRowPrice(ep.id);
                              if (e.key === 'Escape') {
                                setEditingPriceId(null);
                                setDraftPrice('');
                              }
                            }}
                            className="input mono"
                            style={{
                              flex: 1,
                              padding: '3px 6px',
                              fontSize: 12,
                            }}
                          />
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditPrice(ep);
                          }}
                          title="点击编辑"
                          className="mono"
                          style={{
                            background: 'transparent',
                            border: '1px dashed transparent',
                            padding: '2px 6px',
                            borderRadius: 4,
                            cursor: 'pointer',
                            color: 'var(--fg-1)',
                            fontSize: 12,
                            textAlign: 'left',
                            transition: 'border-color .1s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = 'var(--line-2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = 'transparent';
                          }}
                        >
                          {(() => {
                            if (isFreeEndpoint(ep)) return 'free';
                            const v = Number(ep.costPerCall || 0);
                            return v > 0 ? (
                              `$${v}`
                            ) : (
                              <span style={{ color: 'var(--fg-4)' }}>
                                未设置
                              </span>
                            );
                          })()}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Jump-to-detail chevron */}
                  <button
                    type="button"
                    onClick={jump}
                    title="进入端点详情编辑"
                    className="col-jump"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                    }}
                  >
                    <XIcons.IconChevronRight size={11} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
};

// 端点 path 输入框:左侧一个「上游环境」下拉(default + 命名 Base URL)作为该端点走哪个上游的
// 唯一选择来源;右侧只填 path 本体(不含 {{env}} 前缀)。用户不需要手敲 {{。
// 存储时:default → 直接存 path;命名环境 → 存成 {{name}}/path(网关按名解析,见 resolveUpstreamBase)。
const EndpointPathInput = ({ index }) => {
  const { t } = useT();
  const { watch, setValue } = useFormContext();
  const fieldName = `endpoints.${index}.path`;
  const raw = watch(fieldName) || '';
  // 把存储值拆成 { env, rest }:有 {{name}} 前缀 → 命名环境;否则 → default(走默认 Base URL)。
  const m = raw.match(/^\{\{([a-zA-Z0-9_-]+)\}\}([\s\S]*)$/);
  const env = m ? m[1] : 'default';
  const rest = m ? m[2] : raw;
  const extraEnvs = watch('baseUrls') || [];
  const envNames = [
    'default',
    ...extraEnvs.map((e) => e && e.name).filter(Boolean),
  ];
  const envActive = env !== 'default';

  // default 不写前缀(保持 path 干净);命名环境写成 {{name}} 前缀。
  const compose = (envName, pathRest) =>
    envName === 'default' ? pathRest : `{{${envName}}}${pathRest}`;
  const setEnv = (name) =>
    setValue(fieldName, compose(name, rest), {
      shouldDirty: true,
      shouldValidate: true,
    });
  const onRestChange = (e) =>
    setValue(fieldName, compose(env, e.target.value), {
      shouldDirty: true,
      shouldValidate: true,
    });

  return (
    <div
      className="row hairline"
      style={{
        flex: 1,
        minWidth: 0,
        borderRadius: 6,
        background: 'var(--bg-2)',
        overflow: 'hidden',
        alignItems: 'stretch',
      }}
    >
      {/* 上游环境下拉 —— 选择该端点走哪个 Base URL(default 或命名环境),唯一来源 */}
      <Menu
        minWidth={210}
        title={t('register.endpoint.path.envBtn')}
        triggerStyle={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '0 8px',
          border: 'none',
          borderRight: '1px solid var(--line)',
          borderRadius: 0,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          background: envActive
            ? 'var(--accent-soft, rgba(245,165,36,0.14))'
            : 'var(--bg-3)',
          color: envActive ? 'var(--accent, #f5a524)' : 'var(--fg-3)',
          fontSize: 11,
          cursor: 'pointer',
        }}
        trigger={
          <>
            <XIcons.IconGlobe size={11} />
            <span className="mono">{`{{${env}}}`}</span>
            <XIcons.IconChevronDown size={10} />
          </>
        }
      >
        {envNames.map((n) => (
          <MenuItem key={n} onClick={() => setEnv(n)}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
              }}
            >
              <code className="mono">{`{{${n}}}`}</code>
              {n === 'default' && (
                <span
                  className="micro"
                  style={{
                    color: 'var(--fg-4)',
                    textTransform: 'none',
                    letterSpacing: 0,
                  }}
                >
                  {t('register.endpoint.path.defaultHint')}
                </span>
              )}
              {n === env && (
                <XIcons.IconCheck
                  size={12}
                  style={{
                    marginLeft: 'auto',
                    color: 'var(--accent, #f5a524)',
                  }}
                />
              )}
            </span>
          </MenuItem>
        ))}
      </Menu>
      <input
        className="input mono"
        style={{
          flex: 1,
          minWidth: 0,
          border: 'none',
          background: 'transparent',
          borderRadius: 0,
        }}
        placeholder={t('register.endpoint.path.placeholder')}
        value={rest}
        onChange={onRestChange}
      />
    </div>
  );
};

// ============ Endpoint editor (5 tabs: General / Request / Body / Responses / Billing) ============

const EndpointEditor = ({
  index,
  accessMode,
  onRemove,
  canRemove,
  fillHeight = false,
}) => {
  const { t } = useT();
  const [tab, setTab] = React.useState('general');
  const test = useEndpointTest(index); // 测试块状态/逻辑(testMode + 发送),供顶部按钮与右侧面板共享
  const { toast } = XPrim.useToast();
  const {
    register,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();
  const path = watch(`endpoints.${index}.path`) || '';
  const method = watch(`endpoints.${index}.method`) || 'GET';
  const billingType = watch(`endpoints.${index}.billingType`) || 'PER_CALL';
  const isSandboxEndpoint = billingType === 'SANDBOX';
  // 一个 Sandbox 服务只有一条「承载配置」的端点（POST /v1/sandboxes，带 sandboxConfig）；
  // 其余标准接口是 xAPI 按能力自动生成的派生行，不可编辑、也不能被改写成同一个 path。
  const isDerivedSandboxRoute =
    isSandboxEndpoint && !watch(`endpoints.${index}.sandboxConfig`);
  // 传输协议：缺省 HTTP（存量端点无 protocol）。WS 时隐藏 method/HTTP tabs，改渲染 WsEndpointFields。
  const protocol = watch(`endpoints.${index}.protocol`) || 'HTTP';
  const isWs = protocol === 'WEBSOCKET';

  // 模型 tab gating:实时探测端点是否为 AI 接口(path 嗅探 + billing 旁证)。后端
  // detect-endpoint 带 EntityAccountGuard;探测失败 / 非实体账号 → 当作非 AI(不出 tab)。
  const [aiDetect, setAiDetect] = React.useState({
    specId: null,
    detectedBy: null,
    loading: false,
  });
  React.useEffect(() => {
    // WS 端点不做 AI 接口探测（无 HTTP method/billing 语义）。
    if (isWs || !path || !path.trim()) {
      setAiDetect({ specId: null, detectedBy: null, loading: false });
      return;
    }
    let alive = true;
    setAiDetect((s) => ({ ...s, loading: true }));
    const tm = setTimeout(async () => {
      try {
        // billingType is only an optional L2 hint and the DTO rejects DYNAMIC → omit it
        // unless it's one of the three the detector accepts. Path sniff (L1) is the signal.
        const bt = ['PER_CALL', 'PER_TOKEN', 'PER_RESOURCE'].includes(
          billingType,
        )
          ? billingType
          : undefined;
        const res = await XApi.apiSvc.detectAiEndpoint(
          bt ? { path, billingType: bt } : { path },
        );
        if (alive)
          setAiDetect({
            specId: res?.suggested?.specId ?? null,
            detectedBy: res?.suggested?.detectedBy ?? null,
            loading: false,
          });
      } catch {
        if (alive)
          setAiDetect({ specId: null, detectedBy: null, loading: false });
      }
    }, 400);
    return () => {
      alive = false;
      clearTimeout(tm);
    };
  }, [path, billingType, isWs]);
  // 识别为 AI 接口 + 非 PER_TOKEN(PER_TOKEN 的模型来自定价表)+ 非 DIRECT → 出「模型」tab。
  // 手动声明（窄版）：仅 detectedBy==='manual' 的表单值算手动声明（区别于自动识别
  // 回填的值）。effectiveSpec = 自动值 ?? 手动声明。
  const formSpec = watch(`endpoints.${index}.aiInterfaceSpecId`) || null;
  const formDetectedBy = watch(`endpoints.${index}.aiSpecDetectedBy`) || null;
  const manualSpec = formDetectedBy === 'manual' ? formSpec : null;
  // 手动决定覆盖自动识别(含手动关闭=manual+spec空 → effectiveSpec=null,使计费/模型 tab
  // 也随之不当作 AI)。非 manual 时跟随实时 aiDetect。
  const effectiveSpec =
    formDetectedBy === 'manual' ? formSpec : aiDetect.specId;
  const effectiveDetectedBy =
    formDetectedBy === 'manual'
      ? 'manual'
      : aiDetect.specId
        ? aiDetect.detectedBy
        : null;
  // 识别为 AI 接口（含手动声明的 'other'）→ 出「模型」tab。'other' 不进路由、无 canonical
  // 可映射，但仍允许声明 servedModels：modelName 会被保留（canonicalId=null），用于在服务
  // 详情页对外展示「该接口支持的模型」。PER_TOKEN 模型来自定价表、DIRECT 不经网关，均不出此 tab。
  const showModelTab =
    !isWs &&
    !!effectiveSpec &&
    billingType !== 'PER_TOKEN' &&
    accessMode !== 'DIRECT';
  // 当前 tab 若因条件变化被隐藏,回落到 general。WS 端点只有 general(承载 WsEndpointFields)。
  React.useEffect(() => {
    if (tab === 'model' && !showModelTab) setTab('general');
    if (isWs && tab !== 'general') setTab('general');
  }, [showModelTab, isWs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-extract path params from `:foo` and `{foo}` placeholders.
  const pathParamsArray = useFieldArray({
    control,
    name: `endpoints.${index}.pathParams`,
  });
  React.useEffect(() => {
    const matches = [...path.matchAll(/[:{]([a-zA-Z_][a-zA-Z0-9_]*)[}]?/g)].map(
      (m) => m[1],
    );
    const existing = (pathParamsArray.fields || []).map((f) => f.name);
    const missing = matches.filter((m) => !existing.includes(m));
    const stale = existing.filter((e) => !matches.includes(e));
    if (missing.length > 0) {
      missing.forEach((name) =>
        pathParamsArray.append({
          name,
          type: 'string',
          required: true,
          description: '',
          value: '',
        }),
      );
    }
    if (stale.length > 0) {
      stale.forEach((name) => {
        const idx = (pathParamsArray.fields || []).findIndex(
          (f) => f.name === name,
        );
        if (idx >= 0) pathParamsArray.remove(idx);
      });
    }
  }, [path]);

  if (index < 0) {
    return (
      <div
        style={{
          padding: 32,
          color: 'var(--fg-4)',
          fontSize: 13,
          textAlign: 'center',
        }}
      >
        {t('register.endpoint.selectHint')}
      </div>
    );
  }

  const epErr = errors.endpoints && errors.endpoints[index];

  return (
    <div
      style={
        fillHeight
          ? {
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              padding: 18,
              minWidth: 0,
            }
          : { padding: 18, minWidth: 0 }
      }
    >
      {/* ── 固定头部:protocol switch + method+path + tabs(不跟随内容滚动)── */}
      <div style={fillHeight ? { flexShrink: 0 } : undefined}>
        {/* Protocol 开关:HTTP | WebSocket。DIRECT 模式不支持 WS(网关桥接需 PROXY),故 DIRECT 隐藏。 */}
        {accessMode !== 'DIRECT' && (
          <div
            className="row gap-2"
            style={{ marginBottom: 12, alignItems: 'center' }}
          >
            <Controller
              name={`endpoints.${index}.protocol`}
              control={control}
              render={({ field }) => (
                <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                  {PROTOCOLS.map((p) => (
                    <button
                      type="button"
                      key={p}
                      className={`btn btn-sm ${(field.value || 'HTTP') === p ? 'btn-primary' : ''}`}
                      onClick={() => field.onChange(p)}
                      title={
                        p === 'WEBSOCKET'
                          ? '双向 WebSocket 端点(按连接时长计费)'
                          : 'HTTP / REST 端点'
                      }
                    >
                      {p === 'WEBSOCKET' ? 'WebSocket' : 'HTTP'}
                    </button>
                  ))}
                </div>
              )}
            />
          </div>
        )}
        {/* Top row: (HTTP) method + path + test  /  (WS) name + path */}
        {isWs ? (
          <>
            <div
              className="row gap-2"
              style={{ marginBottom: 14, alignItems: 'center' }}
            >
              <span
                className="method"
                style={{
                  flexShrink: 0,
                  background: 'rgba(120,80,220,0.15)',
                  color: 'rgb(150,110,240)',
                  border: '1px solid rgba(120,80,220,0.35)',
                }}
              >
                WS
              </span>
              <input
                className="input"
                style={{ flex: 1, minWidth: 0 }}
                placeholder={t(
                  'register.endpoint.field.displayName.placeholder',
                )}
                {...register(`endpoints.${index}.name`)}
              />
              <button
                type="button"
                className="btn btn-sm btn-ghost btn-icon"
                onClick={onRemove}
                disabled={!canRemove}
                title={t('register.endpoint.removeTitle')}
              >
                <XIcons.IconTrash size={12} />
              </button>
            </div>
            {epErr?.name?.message && <ErrorText text={epErr.name.message} />}
          </>
        ) : (
          <>
            <div className="row gap-2" style={{ marginBottom: 14 }}>
              {isSandboxEndpoint ? (
                /* Sandbox 面由协议固定：展示但不可编辑。改成自由填就没有「一份代码
                   通吃所有服务商」了，聚合网关也无法跨家路由。派生行必须显示自己
                   真实的 method/path —— 一律显示成 POST /v1/sandboxes 会让列表里
                   十几条接口看起来完全一样。 */
                <div
                  className="row gap-2"
                  style={{ flex: 1, minWidth: 0, alignItems: 'center' }}
                  title={
                    isDerivedSandboxRoute
                      ? 'xAPI 按该服务声明的能力自动生成，无需配置'
                      : 'Sandbox v1 协议固定的创建接口，其余标准接口由 xAPI 自动生成'
                  }
                >
                  <span className="badge mono" style={{ flexShrink: 0 }}>
                    {method}
                  </span>
                  {/* .input 是给真正的 <input> 用的：表单控件由浏览器把文字垂直居中，
                      而 <code> 是普通块级元素，19.5px 的行高塞进 32px 的盒子会贴顶，
                      看起来就比左边的 POST 高一截。这里自己居中。 */}
                  <code
                    className="input mono"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      color: 'var(--fg-2)',
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {path || SANDBOX_SURFACE_PATH}
                  </code>
                  <span
                    style={{
                      fontSize: 11,
                      color: 'var(--fg-3)',
                      flexShrink: 0,
                    }}
                  >
                    {isDerivedSandboxRoute ? '自动生成' : '协议固定'}
                  </span>
                </div>
              ) : (
                <>
                  <Controller
                    name={`endpoints.${index}.method`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onChange={field.onChange}
                        width={110}
                        options={METHODS.map((m) => ({ value: m, label: m }))}
                      />
                    )}
                  />
                  <EndpointPathInput index={index} />
                </>
              )}
              {/* 测试:首次点开右侧测试块;testMode 下按钮变「发送」直接打一次上游 */}
              <button
                type="button"
                className={`btn btn-sm ${test.testMode ? 'btn-primary' : ''}`}
                onClick={() =>
                  test.testMode ? test.run() : test.setTestMode(true)
                }
                disabled={test.testMode && test.running}
                title={
                  test.testMode
                    ? '用当前配置真打一次上游'
                    : '打开测试块,用当前配置真打一次上游验证'
                }
                style={{ flexShrink: 0 }}
              >
                {test.testMode && test.running ? (
                  <>
                    <div className="spinner" /> 发送…
                  </>
                ) : (
                  <>
                    <XIcons.IconPlay size={11} />{' '}
                    {test.testMode ? '发送' : '测试'}
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-ghost btn-icon"
                onClick={onRemove}
                disabled={!canRemove}
                title={t('register.endpoint.removeTitle')}
              >
                <XIcons.IconTrash size={12} />
              </button>
            </div>
            {epErr?.path?.message && <ErrorText text={epErr.path.message} />}
          </>
        )}

        {/* Tab strip —— 与 path 同属固定头部。WS 端点无 HTTP tabs(全部字段在 WsEndpointFields)。 */}
        {!isWs && (
          <div className="tabs" style={{ marginBottom: fillHeight ? 0 : 18 }}>
            {[
              { k: 'general', l: t('register.endpoint.tab.general') },
              { k: 'request', l: t('register.endpoint.tab.request') },
              {
                k: 'body',
                l: t('register.endpoint.tab.body'),
                hide: method === 'GET' || method === 'DELETE',
              },
              { k: 'responses', l: t('register.endpoint.tab.responses') },
              // 模型 tab:仅在识别为 AI 接口 + 非 PER_TOKEN 时出现(servedModels 声明)。
              {
                k: 'model',
                l: t('register.endpoint.tab.model'),
                hide: !showModelTab,
              },
              {
                k: 'billing',
                l: t('register.endpoint.tab.billing'),
                hide: accessMode === 'DIRECT',
              },
            ]
              .filter((it) => !it.hide)
              .map((it) => (
                <button
                  type="button"
                  key={it.k}
                  className={`tab ${tab === it.k ? 'active' : ''}`}
                  onClick={() => setTab(it.k)}
                >
                  {it.l}
                </button>
              ))}
          </div>
        )}
      </div>

      {/* ── 内容区:内部滚动(testMode 下右侧测试块同区)── */}
      <div
        style={{
          ...(fillHeight
            ? {
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                paddingTop: 18,
                paddingRight: 6,
              }
            : {}),
          ...(!isWs && test.testMode
            ? {
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(300px, 400px)',
                gap: 20,
                alignItems: 'start',
              }
            : {}),
        }}
      >
        <div style={{ minWidth: 0 }}>
          {isWs ? (
            <WsEndpointFields index={index} />
          ) : (
            <>
              {tab === 'general' && (
                <EndpointGeneralTab
                  index={index}
                  accessMode={accessMode}
                  billingType={billingType}
                  aiDetect={aiDetect}
                />
              )}
              {tab === 'request' && (
                <EndpointRequestTab
                  index={index}
                  pathParamsArray={pathParamsArray}
                />
              )}
              {tab === 'body' && <EndpointBodyTab index={index} />}
              {tab === 'responses' && <EndpointResponsesTab index={index} />}
              {tab === 'model' && (
                <EndpointModelTab
                  index={index}
                  specId={effectiveSpec}
                  detectedBy={effectiveDetectedBy}
                  path={path}
                />
              )}
              {tab === 'billing' && (
                <EndpointBillingTab
                  index={index}
                  billingType={billingType}
                  specId={effectiveSpec}
                />
              )}
            </>
          )}
        </div>

        {!isWs && test.testMode && <EndpointTestPanel test={test} />}
      </div>
    </div>
  );
};

// ── 测试调用 ─────────────────────────────────────────────────────────────
// useEndpointTest 封装测试状态/逻辑:testMode(右侧测试块是否展开)+ 请求构建 +
// SSRF 加固的后端 /agent/test-config 调用(沙箱、不计费、不落库)。顶部「测试」
// 按钮切 testMode 并变「发送」;EndpointTestPanel 渲染右侧输入/输出块。无需先保存。
function useEndpointTest(index) {
  const { watch } = useFormContext();
  const baseUrl = (watch('baseUrl') || '').trim();
  const method = watch(`endpoints.${index}.method`) || 'GET';
  const path = watch(`endpoints.${index}.path`) || '';
  const authType = watch('authType') || 'NONE';

  const [testMode, setTestMode] = React.useState(false);
  const [params, setParams] = React.useState('');
  const [body, setBody] = React.useState('');
  const [running, setRunning] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [leftTab, setLeftTab] = React.useState('params');
  const [rightTab, setRightTab] = React.useState('info');

  const hasBody = ['POST', 'PUT', 'PATCH'].includes(method);

  // Assemble the upstream credentials from the service-level auth config +
  // extra private headers — same shape the gateway injects. Sent over HTTPS,
  // never persisted/logged server-side.
  const buildPrivateHeaders = () => {
    const ph = {};
    const ak = watch('authConfig.key');
    const av = watch('authConfig.value');
    if (authType === 'BEARER' && av) ph['Authorization'] = av;
    else if ((authType === 'HEADER' || authType === 'QUERY') && ak && av)
      ph[ak] = av;
    (watch('privateHeaders') || []).forEach((h) => {
      if (h && h.key) ph[h.key] = h.value;
    });
    return ph;
  };

  const parsedParams = () => {
    try {
      return params.trim() ? JSON.parse(params) : {};
    } catch {
      return null;
    }
  };
  const parsedBody = () => {
    if (!hasBody || !body.trim()) return undefined;
    try {
      return JSON.parse(body);
    } catch {
      return body;
    }
  };

  // Resolved URL preview: substitute path params, the rest become query.
  const buildUrl = () => {
    const pp = parsedParams() || {};
    let p = path;
    const names = new Set(
      [...p.matchAll(/[:{]([a-zA-Z0-9_]+)\}?/g)].map((m) => m[1]),
    );
    const q = [];
    Object.entries(pp).forEach(([k, v]) => {
      if (names.has(k)) {
        p = p.replace(
          new RegExp(`\\{${k}\\}|:${k}`, 'g'),
          encodeURIComponent(v),
        );
      } else {
        q.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
      }
    });
    return `${baseUrl}${p}${q.length ? '?' + q.join('&') : ''}`;
  };

  // cURL preview — credential values masked so secrets aren't rendered on screen.
  const curlPreview = () => {
    const url = buildUrl() || '…';
    const headers = buildPrivateHeaders();
    const tb = parsedBody();
    const parts = [`curl --request ${method} \\`, `  --url '${url}'`];
    Object.keys(headers).forEach((k) => {
      parts[parts.length - 1] += ' \\';
      parts.push(`  --header '${k}: ••••••'`);
    });
    if (tb !== undefined) {
      parts[parts.length - 1] += ' \\';
      parts.push(
        `  --data '${typeof tb === 'string' ? tb : JSON.stringify(tb)}'`,
      );
    }
    return parts.join('\n');
  };

  const run = async () => {
    setErr(null);
    setResult(null);
    if (!baseUrl) {
      setErr('请先到「基本信息 › 上游接入」填写上游 Base URL。');
      return;
    }
    let testParams;
    try {
      testParams = params.trim() ? JSON.parse(params) : undefined;
    } catch {
      setErr('参数需要是合法 JSON,例如 {"id":"123"}');
      return;
    }
    let testBody;
    if (hasBody && body.trim()) {
      try {
        testBody = JSON.parse(body);
      } catch {
        testBody = body; // 允许非 JSON 原文
      }
    }
    setRunning(true);
    try {
      const res = await XApi.agentService.testConfig({
        baseUrl,
        method,
        path,
        authType,
        privateHeaders: buildPrivateHeaders(),
        testParams,
        testBody,
      });
      setResult(res);
      setRightTab('info');
    } catch (e) {
      setErr(e?.message || '测试请求失败');
    } finally {
      setRunning(false);
    }
  };

  return {
    baseUrl,
    method,
    path,
    authType,
    testMode,
    setTestMode,
    params,
    setParams,
    body,
    setBody,
    hasBody,
    running,
    result,
    err,
    leftTab,
    setLeftTab,
    rightTab,
    setRightTab,
    buildPrivateHeaders,
    buildUrl,
    curlPreview,
    run,
  };
}

// EndpointTestPanel —— 右侧测试块(输入 + 输出竖排)。发送由 EndpointEditor 顶部的
// 「发送」按钮触发(testMode 下),所以这里不再有 method/URL/Send 行;只渲染参数/
// 请求体/鉴权输入、cURL 预览(凭证隐藏)、状态徽章 + 信息/响应输出。窄列竖排。
function EndpointTestPanel({ test }) {
  const {
    method,
    path,
    baseUrl,
    authType,
    params,
    setParams,
    body,
    setBody,
    hasBody,
    running,
    result,
    err,
    leftTab,
    setLeftTab,
    rightTab,
    setRightTab,
    buildPrivateHeaders,
    buildUrl,
    curlPreview,
    setTestMode,
  } = test;

  const ok = result && result.success;
  const bodyStr = result
    ? result.error
      ? result.error
      : typeof result.responseBody === 'string'
        ? result.responseBody
        : JSON.stringify(result.responseBody, null, 2)
    : '';
  const bodySize =
    result && result.responseBody != null ? new Blob([bodyStr]).size : 0;
  const fmtSize = (b) =>
    b >= 1024 ? `${(b / 1024).toFixed(2)} KiB` : `${b} B`;

  const subTab = (active) => ({
    padding: '6px 2px',
    marginRight: 16,
    background: 'transparent',
    border: 'none',
    borderBottom: active ? '2px solid var(--fg-0)' : '2px solid transparent',
    color: active ? 'var(--fg-0)' : 'var(--fg-3)',
    fontSize: 12,
    fontWeight: active ? 500 : 400,
    cursor: 'pointer',
  });

  return (
    <div
      style={{
        border: '1px solid var(--bd-1)',
        borderRadius: 8,
        background: 'var(--bg-0)',
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        minWidth: 0,
        position: 'sticky',
        top: 12,
      }}
    >
      {/* 头部 + 关闭 */}
      <div className="row between" style={{ alignItems: 'center' }}>
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--fg-1)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <XIcons.IconPlay size={11} /> 测试调用
        </span>
        <button
          type="button"
          onClick={() => setTestMode(false)}
          title="关闭测试"
          style={{
            width: 22,
            height: 22,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            background: 'transparent',
            color: 'var(--fg-3)',
            fontSize: 13,
            cursor: 'pointer',
            borderRadius: 4,
          }}
        >
          ✕
        </button>
      </div>

      {/* 解析后的请求行(只读预览;发送由顶部按钮触发) */}
      <div
        className="mono"
        style={{
          fontSize: 11,
          color: 'var(--fg-3)',
          wordBreak: 'break-all',
          lineHeight: 1.5,
        }}
        title="method / path 由本端点定义;Base URL 取自「上游接入」"
      >
        <span style={{ color: 'var(--c-mint)' }}>{method}</span>{' '}
        {baseUrl ? buildUrl() : `(未填 Base URL)${path}`}
      </div>

      {/* ── 输入 ── */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--bd-1)' }}>
        {[
          { k: 'params', l: '参数' },
          ...(hasBody ? [{ k: 'body', l: '请求体' }] : []),
          { k: 'auth', l: '鉴权' },
        ].map((it) => (
          <button
            type="button"
            key={it.k}
            style={subTab(leftTab === it.k)}
            onClick={() => setLeftTab(it.k)}
          >
            {it.l}
          </button>
        ))}
      </div>

      {leftTab === 'params' && (
        <textarea
          className="input mono"
          rows={4}
          value={params}
          onChange={(e) => setParams(e.target.value)}
          placeholder='path + query (JSON)，如 {"id":"123","limit":"10"}'
        />
      )}
      {leftTab === 'body' && hasBody && (
        <textarea
          className="input mono"
          rows={6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder='请求体 (JSON)，如 {"key":"value"}'
        />
      )}
      {leftTab === 'auth' && (
        <div style={{ fontSize: 12, color: 'var(--fg-3)', lineHeight: 1.7 }}>
          鉴权类型{' '}
          <span className="mono" style={{ color: 'var(--fg-1)' }}>
            {authType}
          </span>{' '}
          —— 凭证取自「上游接入」,值已隐藏:
          <div
            className="mono"
            style={{
              marginTop: 6,
              padding: '8px 10px',
              background: 'var(--bg-2)',
              border: '1px solid var(--bd-1)',
              borderRadius: 6,
              color: 'var(--fg-2)',
            }}
          >
            {Object.keys(buildPrivateHeaders()).length
              ? Object.keys(buildPrivateHeaders()).map((k) => (
                  <div key={k}>{k}: ••••••</div>
                ))
              : '(无)'}
          </div>
        </div>
      )}

      {err && <div style={{ fontSize: 12, color: 'var(--err)' }}>{err}</div>}

      {/* cURL 预览 */}
      <div
        style={{
          border: '1px solid var(--bd-1)',
          borderRadius: 6,
          overflow: 'hidden',
        }}
      >
        <div
          className="row between"
          style={{
            padding: '6px 10px',
            borderBottom: '1px solid var(--bd-1)',
            background: 'var(--bg-2)',
          }}
        >
          <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
            预览 · cURL
          </span>
          <span style={{ fontSize: 10, color: 'var(--fg-4)' }}>凭证已隐藏</span>
        </div>
        <pre
          className="mono"
          style={{
            margin: 0,
            padding: 10,
            fontSize: 11,
            color: 'var(--fg-2)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            maxHeight: 120,
            overflow: 'auto',
          }}
        >
          {curlPreview()}
        </pre>
      </div>

      {/* ── 输出 ── */}
      <div
        style={{
          borderTop: '1px solid var(--bd-1)',
          paddingTop: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div
          style={{
            height: 32,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '0 12px',
            borderRadius: 6,
            border: `1px solid ${result ? (ok ? 'var(--ok)' : 'var(--err)') : 'var(--bd-1)'}`,
          }}
        >
          {running ? (
            <>
              <div className="spinner" />
              <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>
                发送中…
              </span>
            </>
          ) : result ? (
            <>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: ok ? 'var(--ok)' : 'var(--err)',
                }}
              />
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: ok ? 'var(--ok)' : 'var(--err)',
                }}
              >
                {result.statusCode || '网络错误'}
              </span>
              <span
                className="mono"
                style={{
                  marginLeft: 'auto',
                  fontSize: 11,
                  color: 'var(--fg-3)',
                }}
              >
                {result.latencyMs} ms
              </span>
            </>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--fg-4)' }}>
              等待发送…
            </span>
          )}
        </div>

        <div style={{ display: 'flex', borderBottom: '1px solid var(--bd-1)' }}>
          {[
            { k: 'info', l: '信息' },
            { k: 'response', l: '响应' },
          ].map((it) => (
            <button
              type="button"
              key={it.k}
              style={subTab(rightTab === it.k)}
              onClick={() => setRightTab(it.k)}
            >
              {it.l}
            </button>
          ))}
        </div>

        {!result ? (
          <div
            style={{
              padding: '20px 4px',
              textAlign: 'center',
              color: 'var(--fg-4)',
              fontSize: 12,
            }}
          >
            点击顶部「发送」查看响应
          </div>
        ) : rightTab === 'info' ? (
          <div
            style={{
              border: '1px solid var(--bd-1)',
              borderRadius: 6,
              overflow: 'hidden',
            }}
          >
            {[
              [
                '状态',
                ok
                  ? `成功 · ${result.statusCode}`
                  : `失败 · ${result.statusCode || '网络错误'}`,
              ],
              ['延迟', `${result.latencyMs} ms`],
              ['响应大小', fmtSize(bodySize)],
              ['URL', buildUrl()],
            ].map(([k, v], i) => (
              <div
                key={k}
                className="row between"
                style={{
                  padding: '8px 10px',
                  borderTop: i ? '1px solid var(--bd-1)' : 'none',
                  gap: 12,
                }}
              >
                <span
                  style={{ fontSize: 12, color: 'var(--fg-3)', flexShrink: 0 }}
                >
                  {k}
                </span>
                <span
                  className="mono"
                  style={{
                    fontSize: 11,
                    color: 'var(--fg-1)',
                    textAlign: 'right',
                    wordBreak: 'break-all',
                  }}
                >
                  {v}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <pre
            className="mono"
            style={{
              margin: 0,
              padding: 10,
              fontSize: 11,
              border: '1px solid var(--bd-1)',
              borderRadius: 6,
              color: 'var(--fg-2)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              maxHeight: 320,
              overflow: 'auto',
            }}
          >
            {bodyStr}
          </pre>
        )}
      </div>
    </div>
  );
}

// 「AI 接口」开关块（基础 tab）。一个开关表达「这个端点是不是 AI」：
//  - 自动识别命中 → 开关默认为开,但 provider 仍可【关闭】（路径恰好相似的普通接口被误判时）→
//    写 sentinel（spec=null + detectedBy='manual'），后端【优先于自动识别】撤销 AI 标记
//    （associate() 把 explicitNotAi 置于 auto 命中之前）。再打开则清手动覆盖、回到自动识别。
//  - 自动落空 → provider 可手动打开/关闭：
//      · 打开 = 声明为通用 'other'（是 AI 但非标准协议，待平台核验），写
//        aiInterfaceSpecId='other' + aiSpecDetectedBy='manual'；
//      · 关闭 = sentinel（spec=null + detectedBy='manual'），后端据此撤销声明。
//    手动声明默认不进共享模型路由池（detectedBy='manual' 被后端闸门排除，需平台核验）。
//  - 具体协议（openai/claude/…）不在此选择——那是平台核验/admin 专属；若端点已被
//    admin 指定为具体协议，开关显示为开并原样展示该协议（不被本控件改写）。
const EndpointAiDeclareBlock = ({ index, aiDetect }) => {
  const { watch, setValue } = useFormContext();
  // 只有 detectedBy==='manual' 的表单值才算「手动声明」（区别于自动识别回填值）。
  const formSpec = watch(`endpoints.${index}.aiInterfaceSpecId`) || null;
  const formDetectedBy = watch(`endpoints.${index}.aiSpecDetectedBy`) || null;

  const autoHit = !!aiDetect.specId;
  // 手动决定(detectedBy==='manual')**覆盖**自动识别 —— 自动识别只是「默认帮你打开」,
  // provider 永远能关掉(普通接口路径恰好撞上 AI 模式被误判时)。
  //   manual + spec 非空 = 手动声明开;manual + spec 空 = 手动关闭(撤销自动命中);
  //   非 manual = 跟随自动识别。
  const manualDecision = formDetectedBy === 'manual';
  const on = manualDecision ? !!formSpec : autoHit;
  const isAuto = on && !manualDecision; // 当前开 + 来自自动识别
  const shownSpec = on ? (manualDecision ? formSpec : aiDetect.specId) : null;

  const setDecl = (spec, by) => {
    setValue(`endpoints.${index}.aiInterfaceSpecId`, spec, {
      shouldDirty: true,
    });
    setValue(`endpoints.${index}.aiSpecDetectedBy`, by, { shouldDirty: true });
  };
  const toggle = () => {
    if (on)
      setDecl(null, 'manual'); // 关:手动撤销(覆盖误判的自动命中)
    else if (autoHit)
      setDecl(null, null); // 重开且本就自动命中:清手动覆盖,回到自动识别
    else setDecl('other', 'manual'); // 重开且无自动命中:手动声明为通用 AI 接口
  };

  const hint = on
    ? isAuto
      ? '已按路径自动识别为 AI 接口。若这其实是普通接口(路径恰好相似),可关掉。'
      : '已手动声明为 AI 接口;保存后计量(待平台核验)。'
    : '非 AI 端点保持关闭;疑似 AI 但未被自动识别时打开。';

  return (
    <Field label="AI 接口" hint={hint}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          role="switch"
          aria-checked={on}
          onClick={toggle}
          style={{
            flexShrink: 0,
            width: 40,
            height: 22,
            borderRadius: 999,
            border: '1px solid ' + (on ? 'var(--c-mint)' : 'var(--line-2)'),
            background: on ? 'var(--c-mint)' : 'var(--bg-2)',
            position: 'relative',
            cursor: 'pointer',
            transition: 'background .15s ease, border-color .15s ease',
            padding: 0,
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 2,
              left: on ? 20 : 2,
              width: 16,
              height: 16,
              borderRadius: '50%',
              background: on ? 'var(--bg-0, #000)' : 'var(--fg-3)',
              transition: 'left .15s ease, background .15s ease',
            }}
          />
        </button>
        {on && shownSpec ? (
          isAuto ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
              }}
            >
              <span style={{ fontWeight: 700, color: 'var(--ok,#52d3ad)' }}>
                ✓
              </span>
              <span style={{ color: 'var(--fg-3)' }}>识别为</span>
              <span className="mono" style={{ fontWeight: 600 }}>
                {shownSpec}
              </span>
              <span style={{ color: 'var(--fg-4)' }}>· 自动</span>
            </span>
          ) : (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
              }}
            >
              <span style={{ fontWeight: 600, color: 'rgb(90,150,230)' }}>
                手动声明
              </span>
              <span className="mono" style={{ fontWeight: 600 }}>
                {shownSpec}
              </span>
              <span style={{ color: 'var(--fg-4)' }}>· 待平台核验</span>
            </span>
          )
        ) : (
          <span style={{ color: 'var(--fg-3)', fontSize: 13 }}>
            {on ? '已声明为 AI 接口' : '非 AI 接口'}
          </span>
        )}
      </div>
    </Field>
  );
};

const EndpointGeneralTab = ({ index, accessMode, billingType, aiDetect }) => {
  const { t } = useT();
  const {
    register,
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useFormContext();
  const epErr = errors.endpoints && errors.endpoints[index];
  const asyncMode = watch(`endpoints.${index}.asyncMode`) || 'NONE';
  const oauthProviderId = watch(`endpoints.${index}.userOAuthProviderId`);
  const supportsStreaming = watch(
    `endpoints.${index}.aiConfig.supportsStreaming`,
  );
  // Available OAuth providers from /oauth/providers — load once per mount.
  // Free-text was too easy to typo (users had to paste a UUID); a Select
  // off the registered providers list removes that footgun entirely.
  const [oauthProviders, setOauthProviders] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    XApi.oauthService
      .listProviders()
      .then((list) => {
        if (!cancelled) setOauthProviders(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  // V2 strategy state — drives the Execution strategy Panel + async lock.
  const taskStrategy =
    watch(`endpoints.${index}.taskStrategy`) || 'WORKER_BLOCKING';
  const upstreamProvider = watch(`endpoints.${index}.upstreamProvider`);
  const fixedHoldAmount = watch(`endpoints.${index}.fixedHoldAmount`);
  const isPolling = taskStrategy === 'UPSTREAM_POLLING';
  const isRental = billingType === 'TIME_RENTAL';
  const isSandboxEndpoint = billingType === 'SANDBOX';
  // Sandbox 端点：路径由协议固定（不可自由填），且必须是非流式同步 HTTP。
  React.useEffect(() => {
    if (!isSandboxEndpoint) return;
    if (asyncMode !== 'NONE')
      setValue(`endpoints.${index}.asyncMode`, 'NONE', {
        shouldValidate: true,
      });
    if (supportsStreaming)
      setValue(`endpoints.${index}.aiConfig.supportsStreaming`, false, {
        shouldValidate: true,
      });
    if (oauthProviderId)
      setValue(`endpoints.${index}.userOAuthProviderId`, undefined, {
        shouldValidate: true,
      });
    // 派生行（xAPI 自动生成的 /terminate、/commands…）各有自己的 method/path，
    // 绝不能被改写成创建接口的固定值 —— 否则列表里十几条接口会显示成同一条。
    const derived =
      !getValues(`endpoints.${index}.sandboxConfig`) &&
      !!String(getValues(`endpoints.${index}.path`) || '').trim();
    if (derived) return;
    if (getValues(`endpoints.${index}.method`) !== 'POST') {
      setValue(`endpoints.${index}.method`, 'POST', { shouldValidate: true });
    }
    if (getValues(`endpoints.${index}.path`) !== SANDBOX_SURFACE_PATH) {
      setValue(`endpoints.${index}.path`, SANDBOX_SURFACE_PATH, {
        shouldValidate: true,
      });
    }
    if (!getValues(`endpoints.${index}.sandboxConfig`)) {
      setValue(`endpoints.${index}.sandboxConfig`, DEFAULT_SANDBOX_CONFIG(), {
        shouldValidate: true,
      });
    }
    if (!String(getValues(`endpoints.${index}.name`) || '').trim()) {
      setValue(`endpoints.${index}.name`, '创建 Sandbox', {
        shouldValidate: true,
      });
    }
  }, [
    isSandboxEndpoint,
    asyncMode,
    supportsStreaming,
    oauthProviderId,
    index,
    setValue,
    getValues,
  ]);
  // Side effect: when polling, billingType can't be PER_RESOURCE and asyncMode is forced to REQUIRED.
  React.useEffect(() => {
    if (!isPolling) return;
    if (billingType === 'PER_RESOURCE' || billingType === 'TIME_RENTAL') {
      setValue(`endpoints.${index}.billingType`, 'PER_TOKEN', {
        shouldValidate: true,
      });
    }
    if (asyncMode !== 'REQUIRED') {
      setValue(`endpoints.${index}.asyncMode`, 'REQUIRED', {
        shouldValidate: true,
      });
    }
  }, [isPolling, billingType, asyncMode, index, setValue]);
  React.useEffect(() => {
    if (!isRental) return;
    if (asyncMode !== 'NONE')
      setValue(`endpoints.${index}.asyncMode`, 'NONE', {
        shouldValidate: true,
      });
    if (supportsStreaming)
      setValue(`endpoints.${index}.aiConfig.supportsStreaming`, false, {
        shouldValidate: true,
      });
    if (oauthProviderId)
      setValue(`endpoints.${index}.userOAuthProviderId`, undefined, {
        shouldValidate: true,
      });
    const endpointId = getValues(`endpoints.${index}.id`);
    const rentalConfig = getValues(`endpoints.${index}.rentalConfig`);
    if (!rentalConfig) {
      setValue(
        `endpoints.${index}.rentalConfig`,
        {
          ...DEFAULT_RENTAL_CONFIG(),
          rentalKey: buildInternalRentalKey(endpointId),
        },
        { shouldValidate: true },
      );
    } else if (rentalConfig.role === 'CREATE' && !rentalConfig.rentalKey) {
      setValue(
        `endpoints.${index}.rentalConfig.rentalKey`,
        buildInternalRentalKey(endpointId),
        { shouldValidate: true },
      );
    }
  }, [
    isRental,
    asyncMode,
    supportsStreaming,
    oauthProviderId,
    index,
    setValue,
    getValues,
  ]);

  return (
    <div className="col gap-3">
      {/* 基本信息小节头(与下方小节同款:左侧彩条 + 加粗标题) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            width: 3,
            height: 13,
            borderRadius: 2,
            background: 'var(--c-mint, #5fd0a8)',
            flexShrink: 0,
            display: 'inline-block',
          }}
        />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>
          {t('register.endpoint.panel.basics')}
        </span>
      </div>
      <Row cols={2}>
        <Field
          label={t('register.endpoint.field.displayName')}
          required
          error={epErr?.name}
        >
          <input
            className="input"
            placeholder={t('register.endpoint.field.displayName.placeholder')}
            {...register(`endpoints.${index}.name`)}
          />
        </Field>
        <Field
          label={t('register.endpoint.field.billingType')}
          hint={
            accessMode === 'DIRECT'
              ? t('register.endpoint.field.billingType.directHint')
              : isPolling
                ? t('register.endpoint.field.billingType.pollingHint')
                : null
          }
        >
          <Controller
            name={`endpoints.${index}.billingType`}
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={field.onChange}
                disabled={accessMode === 'DIRECT'}
                options={[
                  {
                    value: 'PER_CALL',
                    label: t('register.endpoint.field.billingType.opt.perCall'),
                  },
                  {
                    value: 'PER_TOKEN',
                    label: t(
                      'register.endpoint.field.billingType.opt.perToken',
                    ),
                  },
                  // DYNAMIC 在 polling 下允许（异步 cost-passthrough）；PER_RESOURCE 仍不支持
                  // polling（后端 R1 + 副作用会把它转回 PER_TOKEN），故仅同步时才列 PER_RESOURCE。
                  {
                    value: 'DYNAMIC',
                    label: t('register.endpoint.field.billingType.opt.dynamic'),
                  },
                  ...(isPolling
                    ? []
                    : [
                        {
                          value: 'PER_RESOURCE',
                          label: t(
                            'register.endpoint.field.billingType.opt.perResource',
                          ),
                        },
                        {
                          value: 'TIME_RENTAL',
                          label: '按时间租赁（5 分钟滚动预留）',
                        },
                        {
                          value: 'SANDBOX',
                          label: 'Sandbox（标准计量：状态 / 资源 / 操作）',
                        },
                      ]),
                ]}
              />
            )}
          />
        </Field>
      </Row>
      <Field
        label={t('register.endpoint.field.description')}
        hint={t('register.endpoint.field.description.hint')}
      >
        <textarea
          className="input"
          rows={3}
          placeholder={t('register.endpoint.field.description.placeholder')}
          {...register(`endpoints.${index}.description`)}
        />
      </Field>
      {/* 分组:编目元数据(端点归到哪个文件夹)—— 属于基本信息,不是可见性/访问。 */}
      <Field
        label={t('register.endpoint.field.group')}
        hint={t('register.endpoint.field.group.hint')}
      >
        <input
          className="input"
          list={`ep-groups-${index}`}
          placeholder={t('register.endpoint.field.group.placeholder')}
          {...register(`endpoints.${index}.group`)}
        />
        <datalist id={`ep-groups-${index}`}>
          {Array.from(
            new Set(
              (getValues('endpoints') || [])
                .map((e) => e && e.group)
                .filter(Boolean),
            ),
          ).map((g) => (
            <option key={g} value={g} />
          ))}
        </datalist>
      </Field>
      {/* AI 接口:次要的自动分类项 —— 降到描述下方,紧凑展示(见 EndpointAiDeclareBlock)。 */}
      {accessMode !== 'DIRECT' && (
        <EndpointAiDeclareBlock index={index} aiDetect={aiDetect} />
      )}

      {/* ── 执行行为：异步(常用，提上来) + 流式。两个小开关并排，警告整行放下方。 ── */}
      <Panel title={t('register.endpoint.panel.behavior')} section compact>
        <Row cols={2}>
          <Field
            label={t('register.endpoint.async.label')}
            hint={
              isPolling
                ? null
                : asyncMode === 'REQUIRED'
                  ? t('register.endpoint.async.toggleHintOn')
                  : asyncMode === 'OPTIONAL'
                    ? t('register.endpoint.async.hint.optional')
                    : t('register.endpoint.async.toggleHintOff')
            }
          >
            {isPolling ? (
              <div
                className="row gap-2"
                style={{
                  padding: '7px 10px',
                  background: 'var(--bg-2)',
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  fontSize: 12,
                  color: 'var(--fg-3)',
                }}
              >
                <XIcons.IconInfo size={12} />
                <span>{t('register.endpoint.async.lockedByPolling')}</span>
              </div>
            ) : (
              // 三态(对齐后端 AsyncMode 枚举):NONE 仅同步 / OPTIONAL 同步+异步(调用方带
              // X-XAPI-Async:true 才异步)/ REQUIRED 仅异步(同步请求 400)。
              <Controller
                name={`endpoints.${index}.asyncMode`}
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value || 'NONE'}
                    onChange={field.onChange}
                    options={[
                      {
                        value: 'NONE',
                        label: t('register.endpoint.async.opt.none'),
                      },
                      {
                        value: 'OPTIONAL',
                        label: t('register.endpoint.async.opt.optional'),
                      },
                      {
                        value: 'REQUIRED',
                        label: t('register.endpoint.async.opt.required'),
                      },
                    ]}
                  />
                )}
              />
            )}
          </Field>
          <Field
            label={t('register.endpoint.field.supportsStreaming')}
            hint={t('register.endpoint.panel.streaming.sub')}
          >
            <div className="row gap-2" style={{ alignItems: 'center' }}>
              <Controller
                name={`endpoints.${index}.aiConfig.supportsStreaming`}
                control={control}
                render={({ field }) => (
                  <button
                    type="button"
                    className={`btn btn-sm ${field.value ? 'btn-primary' : ''}`}
                    onClick={() => field.onChange(!field.value)}
                  >
                    {field.value
                      ? t('register.endpoint.streaming.on')
                      : t('register.endpoint.streaming.off')}
                  </button>
                )}
              />
              {supportsStreaming && (
                <Controller
                  name={`endpoints.${index}.aiConfig.streamFormat`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value || 'sse'}
                      onChange={field.onChange}
                      options={STREAM_FORMATS.map((s) => ({
                        value: s,
                        label: s,
                      }))}
                    />
                  )}
                />
              )}
            </div>
          </Field>
        </Row>
        {asyncMode !== 'NONE' && oauthProviderId && (
          <div
            className="row gap-2"
            style={{
              marginTop: 10,
              padding: '8px 10px',
              background: 'rgba(245,158,11,0.08)',
              border: '1px solid rgba(245,158,11,0.3)',
              borderRadius: 6,
              fontSize: 12,
              color: 'var(--warn, #f59e0b)',
            }}
          >
            <XIcons.IconInfo size={12} />
            <span>{t('register.endpoint.async.warn.oauth')}</span>
          </div>
        )}
        {asyncMode !== 'NONE' && supportsStreaming && (
          <div
            className="row gap-2"
            style={{
              marginTop: 10,
              padding: '8px 10px',
              background: 'rgba(245,158,11,0.08)',
              border: '1px solid rgba(245,158,11,0.3)',
              borderRadius: 6,
              fontSize: 12,
              color: 'var(--warn, #f59e0b)',
            }}
          >
            <XIcons.IconInfo size={12} />
            <span>{t('register.endpoint.async.warn.streaming')}</span>
          </div>
        )}
      </Panel>

      {/* ── 访问与展示：分组 / 隐藏 / OAuth ── */}
      {/* 可见性与访问:可见性(从公开隐藏)+ 访问鉴权(调用方 OAuth)。分组已移到基本信息。 */}
      <Panel title={t('register.endpoint.panel.visibility')} section compact>
        <Row cols={2}>
          <Field
            label={t('register.endpoint.field.hidden')}
            hint={t('register.endpoint.field.hidden.hint')}
          >
            <Controller
              name={`endpoints.${index}.hidden`}
              control={control}
              render={({ field }) => (
                <button
                  type="button"
                  className={`btn btn-sm ${field.value ? 'btn-primary' : ''}`}
                  onClick={() => field.onChange(!field.value)}
                  style={{ justifyContent: 'center' }}
                >
                  {field.value ? (
                    <>
                      <XIcons.IconEyeOff size={12} />{' '}
                      {t('register.endpoint.field.hidden.on')}
                    </>
                  ) : (
                    <>
                      <XIcons.IconEye size={12} />{' '}
                      {t('register.endpoint.field.hidden.off')}
                    </>
                  )}
                </button>
              )}
            />
          </Field>
          <Field
            label={t('register.endpoint.field.oauth')}
            hint={t('register.endpoint.field.oauth.hint')}
          >
            <Controller
              name={`endpoints.${index}.userOAuthProviderId`}
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value || ''}
                  onChange={(v) => field.onChange(v || undefined)}
                  disabled={isRental}
                  options={[
                    {
                      value: '',
                      label: t('register.endpoint.field.oauth.none'),
                    },
                    ...oauthProviders.map((p) => ({
                      value: p.id,
                      label: p.name
                        ? `${p.name}${p.type ? ` (${p.type})` : ''}`
                        : p.id,
                    })),
                  ]}
                />
              )}
            />
          </Field>
        </Row>
      </Panel>

      {/* V2 — Execution strategy. Picks between V1 worker-blocking (default) and
          V2 upstream-polling (for video-gen-style long jobs that need to poll the
          upstream provider). Only relevant in PROXY mode; DIRECT mode never proxies
          server-side so this whole concept doesn't apply. */}
      {accessMode !== 'DIRECT' && !isRental && (
        <Panel
          title={t('register.endpoint.taskStrategy.label')}
          subtitle={t('register.endpoint.taskStrategy.hint')}
          section
          compact
        >
          <Controller
            name={`endpoints.${index}.taskStrategy`}
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || 'WORKER_BLOCKING'}
                onChange={field.onChange}
                options={[
                  {
                    value: 'WORKER_BLOCKING',
                    label: t(
                      'register.endpoint.taskStrategy.opt.workerBlocking',
                    ),
                  },
                  {
                    value: 'UPSTREAM_POLLING',
                    label: t(
                      'register.endpoint.taskStrategy.opt.upstreamPolling',
                    ),
                  },
                ]}
              />
            )}
          />
          {isPolling && (
            <div className="col gap-3" style={{ marginTop: 12 }}>
              <Field
                label={t('register.endpoint.taskStrategy.upstreamProvider')}
                required
                error={epErr?.upstreamProvider}
              >
                <Controller
                  name={`endpoints.${index}.upstreamProvider`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value || ''}
                      onChange={field.onChange}
                      options={[
                        {
                          value: '',
                          label: t(
                            'register.endpoint.taskStrategy.upstreamProvider.placeholder',
                          ),
                        },
                        ...KNOWN_UPSTREAM_PROVIDERS.map((p) => ({
                          value: p,
                          label: p,
                        })),
                      ]}
                    />
                  )}
                />
              </Field>
              <Field
                label={t('register.endpoint.taskStrategy.fixedHold')}
                hint={t(
                  'register.endpoint.taskStrategy.fixedHold.hint',
                ).replace('{MAX}', String(FIXED_HOLD_MAX_USD))}
                error={epErr?.fixedHoldAmount}
              >
                <div className="row gap-2" style={{ alignItems: 'center' }}>
                  <span className="mono" style={{ color: 'var(--fg-3)' }}>
                    $
                  </span>
                  <input
                    type="number"
                    className="input mono"
                    step="0.01"
                    min="0.01"
                    max={FIXED_HOLD_MAX_USD}
                    placeholder={
                      billingType === 'PER_CALL'
                        ? t(
                            'register.endpoint.taskStrategy.fixedHold.autoPerCall',
                          )
                        : '5.00'
                    }
                    {...register(`endpoints.${index}.fixedHoldAmount`, {
                      setValueAs: (v) =>
                        v === '' || v == null ? undefined : parseFloat(v),
                    })}
                  />
                </div>
              </Field>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
};

// ============ Request tab (3 mini-tables: headers, path, query) ============

const EndpointRequestTab = ({ index, pathParamsArray }) => {
  const { t } = useT();
  const { control } = useFormContext();
  const headersArray = useFieldArray({
    control,
    name: `endpoints.${index}.headers`,
  });
  const queryArray = useFieldArray({
    control,
    name: `endpoints.${index}.params`,
  });

  return (
    <div className="col gap-3">
      <Panel
        title={t('register.endpoint.request.pathParams.title')}
        subtitle={t('register.endpoint.request.pathParams.subtitle')}
        compact
      >
        {pathParamsArray.fields.length === 0 ? (
          <EmptyState
            icon={<XIcons.IconCode size={18} />}
            text={t('register.endpoint.request.pathParams.empty')}
            sub={t('register.endpoint.request.pathParams.emptySub')}
          />
        ) : (
          <ParamsTable
            namePrefix={`endpoints.${index}.pathParams`}
            arr={pathParamsArray}
            lockRequired
            lockName
          />
        )}
      </Panel>

      <Panel
        title={t('register.endpoint.request.headers.title')}
        compact
        action={
          <button
            type="button"
            className="btn btn-sm"
            onClick={() =>
              headersArray.append({
                name: '',
                type: 'string',
                required: false,
                description: '',
                value: '',
              })
            }
          >
            <XIcons.IconPlus size={11} />{' '}
            {t('register.endpoint.request.headers.add')}
          </button>
        }
      >
        {headersArray.fields.length === 0 ? (
          <EmptyState
            icon={<XIcons.IconCode size={18} />}
            text={t('register.endpoint.request.headers.empty')}
          />
        ) : (
          <ParamsTable
            namePrefix={`endpoints.${index}.headers`}
            arr={headersArray}
          />
        )}
      </Panel>

      <Panel
        title={t('register.endpoint.request.query.title')}
        compact
        action={
          <button
            type="button"
            className="btn btn-sm"
            onClick={() =>
              queryArray.append({
                name: '',
                type: 'string',
                required: false,
                description: '',
                value: '',
              })
            }
          >
            <XIcons.IconPlus size={11} />{' '}
            {t('register.endpoint.request.query.add')}
          </button>
        }
      >
        {queryArray.fields.length === 0 ? (
          <EmptyState
            icon={<XIcons.IconCode size={18} />}
            text={t('register.endpoint.request.query.empty')}
          />
        ) : (
          <ParamsTable
            namePrefix={`endpoints.${index}.params`}
            arr={queryArray}
          />
        )}
      </Panel>

      <Panel
        title={t('register.endpoint.request.privateHeaders.title')}
        compact
        subtitle={t('register.endpoint.request.privateHeaders.subtitle')}
        action={<PrivateHeaderAdd index={index} />}
      >
        <PrivateHeadersList index={index} />
      </Panel>
    </div>
  );
};

const ParamsTable = ({ namePrefix, arr, lockRequired, lockName }) => {
  const { t } = useT();
  const { register, control } = useFormContext();
  return (
    <div className="col gap-2">
      <div
        className="row"
        style={{
          padding: '6px 0',
          fontSize: 11,
          color: 'var(--fg-4)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          gap: 8,
        }}
      >
        <span style={{ flex: 3 }}>{t('register.endpoint.col.name')}</span>
        <span style={{ flex: 3 }}>{t('register.endpoint.col.example')}</span>
        <span style={{ width: 110 }}>{t('register.endpoint.col.type')}</span>
        <span style={{ flex: 3 }}>
          {t('register.endpoint.col.description')}
        </span>
        <span style={{ width: 80 }}>{t('register.endpoint.col.required')}</span>
        <span style={{ width: 28 }} />
      </div>
      {arr.fields.map((row, i) => (
        <div
          key={row.id}
          className="row gap-2"
          style={{ alignItems: 'center' }}
        >
          <input
            className="input mono"
            style={{ flex: 3 }}
            disabled={lockName}
            placeholder="name"
            {...register(`${namePrefix}.${i}.name`)}
          />
          <input
            className="input mono"
            style={{ flex: 3 }}
            placeholder="example"
            {...register(`${namePrefix}.${i}.value`)}
          />
          <Controller
            control={control}
            name={`${namePrefix}.${i}.type`}
            render={({ field }) => (
              <Select
                value={field.value || 'string'}
                onChange={field.onChange}
                width={110}
                options={PARAM_TYPES.map((t) => ({ value: t, label: t }))}
              />
            )}
          />
          <input
            className="input"
            style={{ flex: 3 }}
            placeholder="description"
            {...register(`${namePrefix}.${i}.description`)}
          />
          <div style={{ width: 80, display: 'flex', justifyContent: 'center' }}>
            <Controller
              control={control}
              name={`${namePrefix}.${i}.required`}
              render={({ field }) => (
                <input
                  type="checkbox"
                  checked={!!field.value}
                  disabled={lockRequired}
                  onChange={(e) => field.onChange(e.target.checked)}
                  style={{ accentColor: '#fff' }}
                />
              )}
            />
          </div>
          <button
            type="button"
            className="btn btn-sm btn-ghost btn-icon"
            onClick={() => arr.remove(i)}
            disabled={lockName}
          >
            <XIcons.IconTrash size={11} />
          </button>
        </div>
      ))}
    </div>
  );
};

const PrivateHeaderAdd = ({ index }) => {
  const { t } = useT();
  const { control } = useFormContext();
  const arr = useFieldArray({
    control,
    name: `endpoints.${index}.privateHeaders`,
  });
  return (
    <button
      type="button"
      className="btn btn-sm"
      onClick={() => arr.append({ key: '', value: '' })}
    >
      <XIcons.IconPlus size={11} />{' '}
      {t('register.endpoint.request.privateHeaders.add')}
    </button>
  );
};

const PrivateHeadersList = ({ index }) => {
  const { t } = useT();
  const { control, register } = useFormContext();
  const arr = useFieldArray({
    control,
    name: `endpoints.${index}.privateHeaders`,
  });
  if (arr.fields.length === 0)
    return (
      <EmptyState
        icon={<XIcons.IconShield size={18} />}
        text={t('register.endpoint.request.privateHeaders.empty')}
      />
    );
  return (
    <div className="col gap-2">
      {arr.fields.map((row, i) => (
        <div
          key={row.id}
          className="row gap-2"
          style={{ alignItems: 'center' }}
        >
          <input
            className="input mono"
            style={{ flex: 5 }}
            placeholder="X-Custom-Header"
            {...register(`endpoints.${index}.privateHeaders.${i}.key`)}
          />
          <input
            className="input mono"
            style={{ flex: 6 }}
            type="password"
            placeholder="value"
            {...register(`endpoints.${index}.privateHeaders.${i}.value`)}
          />
          <button
            type="button"
            className="btn btn-sm btn-ghost btn-icon"
            onClick={() => arr.remove(i)}
          >
            <XIcons.IconTrash size={11} />
          </button>
        </div>
      ))}
    </div>
  );
};

// ============ Body tab ============

// 结构化表单字段表格（form-data / x-www-form-urlencoded）。withFileType=true 时
// 每行可选 Text/File；File 行的值在测试台上传，编辑器里只声明。
const BodyFieldsTable = ({ index, withFileType }) => {
  const { t } = useT();
  const { register, control, watch } = useFormContext();
  const arr = useFieldArray({ control, name: `endpoints.${index}.bodyParams` });
  return (
    <Field
      label={t('register.endpoint.body.formFields')}
      hint={
        withFileType
          ? t('register.endpoint.body.formFields.hintFile')
          : t('register.endpoint.body.formFields.hint')
      }
    >
      <div className="col gap-2">
        <div
          className="row"
          style={{
            padding: '6px 0',
            fontSize: 11,
            color: 'var(--fg-4)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            gap: 8,
          }}
        >
          <span style={{ flex: 3 }}>{t('register.endpoint.col.key')}</span>
          {withFileType && (
            <span style={{ width: 96 }}>{t('register.endpoint.col.type')}</span>
          )}
          <span style={{ flex: 3 }}>{t('register.endpoint.col.value')}</span>
          <span style={{ flex: 3 }}>
            {t('register.endpoint.col.description')}
          </span>
          <span style={{ width: 28 }} />
        </div>
        {arr.fields.length === 0 && (
          <div
            style={{ fontSize: 12, color: 'var(--fg-4)', padding: '2px 2px' }}
          >
            {t('register.endpoint.body.noFields')}
          </div>
        )}
        {arr.fields.map((row, i) => {
          const ftype =
            watch(`endpoints.${index}.bodyParams.${i}.type`) || 'text';
          return (
            <div
              key={row.id}
              className="row gap-2"
              style={{ alignItems: 'center' }}
            >
              <input
                className="input mono"
                style={{ flex: 3 }}
                placeholder="key"
                {...register(`endpoints.${index}.bodyParams.${i}.key`)}
              />
              {withFileType && (
                <Controller
                  control={control}
                  name={`endpoints.${index}.bodyParams.${i}.type`}
                  render={({ field }) => (
                    <Select
                      value={field.value || 'text'}
                      onChange={field.onChange}
                      width={96}
                      options={BODY_FIELD_TYPES.map((t) => ({
                        value: t,
                        label: t,
                      }))}
                    />
                  )}
                />
              )}
              {withFileType && ftype === 'file' ? (
                <input
                  className="input mono"
                  style={{ flex: 3 }}
                  disabled
                  placeholder={t('register.endpoint.body.fileUpload')}
                />
              ) : (
                <input
                  className="input mono"
                  style={{ flex: 3 }}
                  placeholder="value"
                  {...register(`endpoints.${index}.bodyParams.${i}.value`)}
                />
              )}
              <input
                className="input"
                style={{ flex: 3 }}
                placeholder="description"
                {...register(`endpoints.${index}.bodyParams.${i}.description`)}
              />
              <button
                type="button"
                className="btn btn-sm btn-ghost btn-icon"
                onClick={() => arr.remove(i)}
              >
                <XIcons.IconTrash size={11} />
              </button>
            </div>
          );
        })}
        <div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() =>
              arr.append({ key: '', type: 'text', value: '', description: '' })
            }
          >
            <XIcons.IconPlus size={11} /> {t('register.endpoint.body.addField')}
          </button>
        </div>
      </div>
    </Field>
  );
};

const EndpointBodyTab = ({ index }) => {
  const { t } = useT();
  const { register, control, watch } = useFormContext();
  const bodyType = watch(`endpoints.${index}.bodyType`) || 'JSON';
  return (
    <div className="col gap-3">
      <Field label={t('register.endpoint.body.type')}>
        <Controller
          name={`endpoints.${index}.bodyType`}
          control={control}
          render={({ field }) => (
            <div className="row gap-1">
              {BODY_TYPES.map((t) => (
                <button
                  type="button"
                  key={t}
                  className={`btn btn-sm ${field.value === t ? 'btn-primary' : ''}`}
                  onClick={() => field.onChange(t)}
                >
                  {BODY_TYPE_LABELS[t] || t}
                </button>
              ))}
            </div>
          )}
        />
      </Field>
      {bodyType === 'NONE' ? (
        <EmptyState
          icon={<XIcons.IconCode size={20} />}
          text={t('register.endpoint.body.empty')}
          sub={t('register.endpoint.body.emptySub')}
        />
      ) : bodyType === 'FORM_DATA' ? (
        <BodyFieldsTable index={index} withFileType />
      ) : bodyType === 'FORM_URLENCODED' ? (
        <BodyFieldsTable index={index} />
      ) : (
        <Field
          label={t('register.endpoint.body.jsonExample')}
          hint={t('register.endpoint.body.jsonExample.hint')}
        >
          <textarea
            className="input mono"
            rows={12}
            placeholder={
              '{\n  "model": "claude-haiku-4.5",\n  "messages": []\n}'
            }
            {...register(`endpoints.${index}.bodySchema`)}
          />
        </Field>
      )}
    </div>
  );
};

// ============ Responses tab ============

const EndpointResponsesTab = ({ index }) => {
  const { t } = useT();
  const { control, register, watch, setValue } = useFormContext();
  const arr = useFieldArray({ control, name: `endpoints.${index}.responses` });
  const responses = watch(`endpoints.${index}.responses`) || [];
  const [activeRespIdx, setActiveRespIdx] = React.useState(0);
  const active = responses[activeRespIdx];

  const addResponse = () => {
    arr.append({ status_code: 200, label: 'success', type: 'json', body: {} });
    setActiveRespIdx(responses.length);
  };

  const updateBody = (text) => {
    if (!active) return;
    if (active.type === 'json') {
      try {
        setValue(
          `endpoints.${index}.responses.${activeRespIdx}.body`,
          JSON.parse(text),
          { shouldDirty: true },
        );
      } catch {
        setValue(`endpoints.${index}.responses.${activeRespIdx}.body`, text, {
          shouldDirty: true,
        });
      }
    } else {
      setValue(`endpoints.${index}.responses.${activeRespIdx}.body`, text, {
        shouldDirty: true,
      });
    }
  };

  return (
    <div
      className="register-resp-grid"
      style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 16 }}
    >
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div
          style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)' }}
          className="micro"
        >
          Examples · {arr.fields.length}
        </div>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {arr.fields.map((r, i) => {
            const resp = responses[i] || {};
            const status = resp.status_code ?? 200;
            const ok = status >= 200 && status < 300;
            return (
              <button
                type="button"
                key={r.id}
                onClick={() => setActiveRespIdx(i)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  background:
                    i === activeRespIdx ? 'var(--bg-3)' : 'transparent',
                  borderLeft:
                    i === activeRespIdx
                      ? '2px solid var(--fg-0)'
                      : '2px solid transparent',
                  borderBottom: '1px solid var(--line)',
                  cursor: 'pointer',
                  color: 'inherit',
                }}
              >
                <div className="row between" style={{ alignItems: 'center' }}>
                  <span
                    className="mono"
                    style={{
                      fontSize: 12,
                      color: ok ? 'var(--ok)' : 'var(--err)',
                    }}
                  >
                    {status}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost btn-icon"
                    style={{ width: 22, height: 22 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      arr.remove(i);
                      if (i === activeRespIdx) setActiveRespIdx(0);
                    }}
                  >
                    <XIcons.IconX size={10} />
                  </button>
                </div>
                <div
                  style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 2 }}
                >
                  {resp.label || '(unlabeled)'}
                </div>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          style={{
            margin: 8,
            width: 'calc(100% - 16px)',
            justifyContent: 'center',
          }}
          onClick={addResponse}
        >
          <XIcons.IconPlus size={11} /> {t('register.endpoint.responses.add')}
        </button>
      </div>

      {active ? (
        <div className="col gap-2">
          <Row cols={2}>
            <Field label={t('register.endpoint.responses.statusCode')}>
              <input
                className="input mono"
                type="number"
                {...register(
                  `endpoints.${index}.responses.${activeRespIdx}.status_code`,
                  { valueAsNumber: true },
                )}
              />
            </Field>
            <Field label={t('register.endpoint.responses.label')}>
              <input
                className="input"
                {...register(
                  `endpoints.${index}.responses.${activeRespIdx}.label`,
                )}
              />
            </Field>
          </Row>
          <Field label={t('register.endpoint.responses.contentType')}>
            <Controller
              control={control}
              name={`endpoints.${index}.responses.${activeRespIdx}.type`}
              render={({ field }) => (
                <Select
                  value={field.value || 'json'}
                  onChange={field.onChange}
                  options={[
                    { value: 'json', label: 'application/json' },
                    { value: 'text', label: 'text/plain' },
                  ]}
                />
              )}
            />
          </Field>
          <Field label={t('register.endpoint.responses.body')}>
            {/* key remounts the textarea on example switch so the uncontrolled
                defaultValue re-applies with the selected example's body. Without
                it the DOM keeps the previous example's text and the next onBlur
                writes that stale text into the newly-selected example (clobber).
                Kept uncontrolled on purpose: updateBody JSON.parses on commit, so
                a controlled value+onChange would reformat mid-type and jump the caret. */}
            <textarea
              key={`resp-body-${activeRespIdx}`}
              className="input mono"
              rows={12}
              defaultValue={
                typeof active.body === 'string'
                  ? active.body
                  : JSON.stringify(active.body, null, 2)
              }
              onBlur={(e) => updateBody(e.target.value)}
            />
          </Field>
        </div>
      ) : (
        <EmptyState
          icon={<XIcons.IconCode size={20} />}
          text={t('register.endpoint.responses.empty')}
          sub={t('register.endpoint.responses.emptySub')}
        />
      )}
    </div>
  );
};

// ============ Model tab (AI endpoints, non-PER_TOKEN) ============
// Declares servedModels — the upstream models this endpoint serves. Backend auto-maps
// each to a canonical id; the DYNAMIC `model =` dropdown reads these. Model identity is
// decoupled from billing (only appears when the path is detected as an AI interface).
const EndpointModelTab = ({ index, specId, detectedBy, path }) => {
  const { watch, setValue } = useFormContext();
  // 'other'（手动声明的 AI 接口、不走网关）：servedModels 仅用于在服务详情页对外展示「该接口
  // 支持的模型」，不进路由、不参与 /v1/models 聚合（modelName 原样保留，不做 canonical 映射）。
  const isOther = specId === 'other';
  const raw = watch(`endpoints.${index}.servedModel`) || '';
  const models = String(raw)
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const [draft, setDraft] = React.useState('');

  const commit = (next) =>
    setValue(`endpoints.${index}.servedModel`, next.join(', '), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

  // 自动从计费配置提取模型:DYNAMIC 取规则里 `model =` 条件的值(套用模板或手写都算),
  // 自动补进 servedModels —— 用户不必再手抄一遍。PER_TOKEN 的模型来自定价行,且本 tab
  // 对 PER_TOKEN 隐藏,无需在此提取。提取是「补充」语义:只新增缺失项,不删用户额外加的。
  const billingType = watch(`endpoints.${index}.billingType`);
  const dyn = watch(`endpoints.${index}.dynamicRules`);
  const autoModels = React.useMemo(() => {
    if (billingType !== 'DYNAMIC' || !dyn || !Array.isArray(dyn.rules))
      return [];
    const out = [];
    for (const r of dyn.rules) {
      for (const c of r.conds || []) {
        if (
          c &&
          c.field === 'model' &&
          (!c.op || c.op === '=') &&
          c.value &&
          String(c.value).trim()
        ) {
          out.push(String(c.value).trim());
        }
      }
    }
    return [...new Set(out)];
  }, [billingType, JSON.stringify(dyn)]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!autoModels.length) return;
    const have = new Set(models);
    const missing = autoModels.filter((m) => !have.has(m));
    if (missing.length) commit([...models, ...missing]);
  }, [autoModels.join('')]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = () => {
    const names = draft
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!names.length) return;
    commit([...new Set([...models, ...names])]);
    setDraft('');
  };
  const remove = (name) => commit(models.filter((m) => m !== name));

  return (
    <div className="col gap-3">
      <Field
        label="接口识别"
        hint="由端点 path 自动匹配 interface spec;命中 AI 接口才出现本 tab。非 AI 接口无此 tab,计费规则也不涉及 model。"
      >
        {detectedBy === 'manual' ? (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 12px',
              borderRadius: 8,
              background: 'rgba(90,150,230,.1)',
              border: '1px solid rgba(90,150,230,.3)',
              fontSize: 13,
              color: 'rgb(90,150,230)',
            }}
          >
            <span style={{ fontWeight: 600 }}>手动声明</span>{' '}
            <span className="mono" style={{ fontWeight: 600 }}>
              {specId}
            </span>
            <span style={{ color: 'var(--fg-4)' }}>
              · 自动识别未命中此 path
            </span>
          </div>
        ) : (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 12px',
              borderRadius: 8,
              background: 'rgba(82,211,173,.1)',
              border: '1px solid rgba(82,211,173,.3)',
              fontSize: 13,
              color: 'var(--ok, #52d3ad)',
            }}
          >
            <span style={{ fontWeight: 700 }}>✓</span> 识别为{' '}
            <span className="mono" style={{ fontWeight: 600 }}>
              {specId}
            </span>
            <span style={{ color: 'var(--fg-4)' }}>
              ← 自动 · <span className="mono">{path}</span>
            </span>
          </div>
        )}
      </Field>
      <Field
        label={
          isOther
            ? '支持的模型 · servedModels（仅展示）'
            : '服务的模型 · servedModels'
        }
        hint={
          isOther
            ? "声明该接口支持哪些上游模型,仅用于在【服务详情页】对外展示。'other' 为手动声明的 AI 接口,不进 AI 网关路由、不参与 /v1/models 聚合,因此这些模型名仅作信息展示(原样保留,不做 canonical 映射;canonical 列表里没有的模型也照常展示)。一次可粘贴多个,用逗号或换行分隔。"
            : '声明该接口对外提供哪些上游模型 → 后端自动映射 canonical → 决定 /v1/models 列表与路由校验(不在列表里 → ModelNotFound)。计费 tab 里 DYNAMIC 规则的「model =」下拉即取自这里;反过来,规则里写过的 model 值会自动补进来。一次可粘贴多个,用逗号或换行分隔。'
        }
      >
        {/* 标签输入框:chips + 输入同处一框,支持回车/逗号/粘贴多个、Backspace 删末项。 */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 7,
            alignItems: 'center',
            padding: '7px 9px',
            minHeight: 42,
            border: '1px solid var(--line)',
            borderRadius: 9,
            background: 'var(--bg-2)',
            cursor: 'text',
          }}
          onClick={(e) => {
            const inp = e.currentTarget.querySelector('input');
            if (inp) inp.focus();
          }}
        >
          {models.map((m) => (
            <span
              key={m}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 7,
                background: 'var(--bg-1)',
                border: '1px solid var(--line)',
                fontSize: 12.5,
                fontFamily: 'ui-monospace, monospace',
                color: 'var(--fg-1)',
              }}
            >
              {m}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(m);
                }}
                title="移除"
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--fg-3)',
                  cursor: 'pointer',
                  fontSize: 13,
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
          <input
            className="mono"
            style={{
              flex: 1,
              minWidth: 170,
              border: 'none',
              background: 'transparent',
              color: 'var(--fg-0)',
              fontSize: 12.5,
              outline: 'none',
              padding: '2px 0',
            }}
            placeholder={
              models.length
                ? '继续添加…'
                : '输入或粘贴模型名,回车/逗号分隔多个…'
            }
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                add();
              } else if (e.key === 'Backspace' && !draft && models.length)
                remove(models[models.length - 1]);
            }}
            onBlur={add}
          />
        </div>
      </Field>
    </div>
  );
};

// ============ Billing tab (per-endpoint) ============

const RentalCsvInput = ({ value, onChange, placeholder }) => {
  const [draft, setDraft] = React.useState((value || []).join(', '));
  React.useEffect(() => {
    setDraft((value || []).join(', '));
  }, [JSON.stringify(value || [])]); // eslint-disable-line react-hooks/exhaustive-deps
  const commit = () =>
    onChange([
      ...new Set(
        draft
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
      ),
    ]);
  return (
    <input
      className="input mono"
      placeholder={placeholder}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
    />
  );
};

const EndpointBillingTab = ({ index, billingType, specId }) => {
  const { t } = useT();
  const {
    register,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();
  const endpoints = watch('endpoints') || [];
  const currentEndpoint = endpoints[index] || {};
  const rentalRole = watch(`endpoints.${index}.rentalConfig.role`) || 'CREATE';
  const rentalUnit =
    watch(`endpoints.${index}.rentalConfig.pricing.unit`) || 'HOUR';
  const rentalPrice = Number(
    watch(`endpoints.${index}.rentalConfig.pricing.pricePerUnitUsd`) || 0,
  );
  const rentalDivisor =
    rentalUnit === 'HOUR' ? 3600 : rentalUnit === 'MINUTE' ? 60 : 1;
  const rentalPerSecond = rentalPrice / rentalDivisor;
  const rentalErr = errors?.endpoints?.[index]?.rentalConfig || {};
  const createRentalGroups = endpoints
    .map((endpoint, endpointIndex) => ({ endpoint, endpointIndex }))
    .filter(
      ({ endpoint, endpointIndex }) =>
        endpointIndex !== index &&
        endpoint?.billingType === 'TIME_RENTAL' &&
        endpoint?.rentalConfig?.role === 'CREATE' &&
        endpoint?.rentalConfig?.rentalKey,
    )
    .map(({ endpoint, endpointIndex }) => ({
      value: endpoint.rentalConfig.rentalKey,
      label: rentalGroupLabel(endpoint, endpointIndex),
    }));

  React.useEffect(() => {
    if (billingType !== 'TIME_RENTAL' || rentalRole === 'CREATE') return;
    if (createRentalGroups.length !== 1) return;
    const currentKey = getValues(`endpoints.${index}.rentalConfig.rentalKey`);
    if (currentKey !== createRentalGroups[0].value) {
      setValue(
        `endpoints.${index}.rentalConfig.rentalKey`,
        createRentalGroups[0].value,
        { shouldDirty: true, shouldValidate: true },
      );
    }
  }, [
    billingType,
    rentalRole,
    index,
    setValue,
    getValues,
    createRentalGroups.length,
    createRentalGroups[0]?.value,
  ]);

  const changeRentalRole = (nextRole, onChange) => {
    const keyPath = `endpoints.${index}.rentalConfig.rentalKey`;
    onChange(nextRole);
    if (nextRole === 'CREATE') {
      setValue(keyPath, buildInternalRentalKey(currentEndpoint.id), {
        shouldDirty: true,
        shouldValidate: true,
      });
      return;
    }
    const currentKey = getValues(keyPath);
    const stillValid = createRentalGroups.some(
      (group) => group.value === currentKey,
    );
    const nextKey =
      createRentalGroups.length === 1
        ? createRentalGroups[0].value
        : stillValid
          ? currentKey
          : '';
    setValue(keyPath, nextKey, { shouldDirty: true, shouldValidate: true });
  };
  if (billingType === 'PER_CALL') {
    return (
      <div className="col gap-3">
        <Row cols={2}>
          <Field
            label={t('register.endpoint.billing.perCall.cost')}
            hint={t('register.endpoint.billing.perCall.cost.hint')}
          >
            <input
              className="input mono"
              type="number"
              step="0.0001"
              {...register(`endpoints.${index}.costPerCall`, {
                valueAsNumber: true,
              })}
            />
          </Field>
          <Field
            label={t('register.endpoint.billing.perCall.share')}
            hint={t('register.endpoint.billing.perCall.share.hint')}
          >
            <input className="input mono" disabled value="80% (platform 20%)" />
          </Field>
        </Row>
        {/* servedModels 现归独立「模型」tab(识别为 AI 接口时出现);此处不再重复。 */}
      </div>
    );
  }
  if (billingType === 'PER_RESOURCE') {
    return (
      <div className="col gap-3">
        <Row cols={2}>
          <Field label={t('register.endpoint.billing.perResource.cost')}>
            <input
              className="input mono"
              type="number"
              step="0.0001"
              {...register(`endpoints.${index}.costPerResource`, {
                valueAsNumber: true,
              })}
            />
          </Field>
          <Field
            label={t('register.endpoint.billing.perResource.maxResources')}
            hint={t('register.endpoint.billing.perResource.maxResources.hint')}
          >
            <input
              className="input mono"
              type="number"
              {...register(`endpoints.${index}.estimatedMaxResources`, {
                valueAsNumber: true,
              })}
            />
          </Field>
        </Row>
        <Field
          label={t('register.endpoint.billing.perResource.countPath')}
          hint={t('register.endpoint.billing.perResource.countPath.hint')}
        >
          <input
            className="input mono"
            placeholder="data.length"
            {...register(`endpoints.${index}.resourceCountJsonPath`)}
          />
        </Field>
        {/* servedModels 现归独立「模型」tab(识别为 AI 接口时出现);此处不再重复。 */}
      </div>
    );
  }
  if (billingType === 'SANDBOX') {
    // 整个服务只有一条端点承载 Sandbox 配置，其余是 xAPI 按能力自动生成的派生接口，
    // 共用同一份规格与价格。给派生行也渲染一遍编辑器是有害的：在上面动一下就会给它
    // 长出 sandboxConfig，于是它不再被识别为派生行，method/path 被改写成
    // POST /v1/sandboxes，和真正的创建接口撞成同一条。
    const authoredIndex = endpoints.findIndex(
      (endpoint) =>
        endpoint?.billingType === 'SANDBOX' &&
        endpoint?.sandboxConfig &&
        typeof endpoint.sandboxConfig === 'object',
    );
    const isDerivedRow = authoredIndex >= 0 && authoredIndex !== index;
    if (isDerivedRow) {
      const authored = endpoints[authoredIndex];
      return (
        <div
          className="card"
          style={{
            padding: '12px 14px',
            color: 'var(--fg-2)',
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          这条接口由 xAPI 按你声明的能力自动生成，
          <strong>不需要单独配置</strong>。 规格与价格全服务共用一份，配置在
          <button
            type="button"
            className="btn btn-sm"
            style={{
              margin: '0 6px',
              padding: '2px 8px',
              fontFamily: 'var(--font-mono, monospace)',
            }}
            title="跳到承载配置的那条接口"
            onClick={() => {
              // 跨三层组件传 setActiveEndpointId 太绕（EndpointsSection → EndpointEditor →
              // 计费面板），这里用一个自定义事件，由 EndpointsSection 接住。
              if (authored?.id) {
                window.dispatchEvent(
                  new CustomEvent('xapi:jump-endpoint', {
                    detail: { id: authored.id },
                  }),
                );
              }
            }}
          >
            {authored?.method} {authored?.path} →
          </button>
          上；改那一条，这里会自动跟随。
        </div>
      );
    }
    return (
      <div className="col gap-3">
        <div
          className="card"
          style={{
            padding: '12px 14px',
            color: 'var(--fg-2)',
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          服务商把自家 Sandbox 用 Adapter 标准化后，在这里注册一次即可。接口面由
          xAPI Sandbox v1
          协议固定并自动生成（消费者对任意服务商用同一份代码），你只需要配置
          Adapter、可售规格与价格。 注册后同时具备两个入口：
          <code>{XApi.helpers.gatewayHost('<host>', 'SANDBOX_PROVIDER_V1')}/v1/sandboxes</code> 直接用这一家，
          <code>{XApi.config.sandboxDomain}</code>{' '}
          参与跨服务商自动选型；两者共用同一套预留与结算。
        </div>
        {/* 接入状态与核验失败原因来自服务级 binding（顶层 sandboxConfig 是它的只读回显），
            端点级 basePath 下没有这两个字段，所以显式从顶层取。 */}
        <SandboxProviderSection
          basePath={`endpoints.${index}.sandboxConfig`}
          bindingStatus={watch('sandboxConfig.status')}
          bindingError={watch('sandboxConfig.lastError')}
        />
      </div>
    );
  }
  if (billingType === 'TIME_RENTAL') {
    return (
      <div className="col gap-3">
        <div
          className="card"
          style={{
            padding: '12px 14px',
            color: 'var(--fg-2)',
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          同一租赁组必须包含且只能包含一个创建、销毁和状态接口。平台按秒结算，滚动预留未来
          5 分钟费用，并单独预留最多 120
          秒的关停缓冲；续费失败时自动调用销毁接口。
        </div>
        <Row cols={2}>
          <Field label="接口角色" required error={rentalErr?.role}>
            <Controller
              name={`endpoints.${index}.rentalConfig.role`}
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value || 'CREATE'}
                  onChange={(value) => changeRentalRole(value, field.onChange)}
                  options={[
                    { value: 'CREATE', label: '创建资源（CREATE）' },
                    { value: 'TERMINATE', label: '销毁资源（TERMINATE）' },
                    { value: 'STATUS', label: '查询状态（STATUS）' },
                  ]}
                />
              )}
            />
          </Field>
          {rentalRole === 'CREATE' ? (
            <Field
              label="租赁计费组"
              hint="平台自动建立内部关联，无需填写标识。"
            >
              <div
                className="input"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: 'var(--fg-2)',
                }}
              >
                <span style={{ color: 'var(--ok, #52d3ad)', fontWeight: 700 }}>
                  ✓
                </span>
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {rentalGroupLabel(currentEndpoint, index)}
                </span>
              </div>
            </Field>
          ) : (
            <Field
              label="所属租赁计费组"
              required
              hint={
                createRentalGroups.length
                  ? createRentalGroups.length === 1
                    ? '已自动关联唯一的创建接口。'
                    : '请选择该接口负责的租赁计费组。'
                  : '请先将一个端点配置为“创建资源”。'
              }
              error={rentalErr?.rentalKey}
            >
              {createRentalGroups.length ? (
                <Controller
                  name={`endpoints.${index}.rentalConfig.rentalKey`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value || ''}
                      onChange={(value) => field.onChange(value)}
                      options={createRentalGroups}
                      placeholder="选择创建接口"
                      width="100%"
                    />
                  )}
                />
              ) : (
                <div className="input" style={{ color: 'var(--fg-4)' }}>
                  暂无可关联的创建接口
                </div>
              )}
            </Field>
          )}
        </Row>

        {rentalRole === 'CREATE' && (
          <Panel
            title="资源识别与定价"
            subtitle="资源 ID 会从创建接口的成功响应中提取，并作为整段租赁的唯一标识。"
            compact
          >
            <Field
              label="资源 ID 响应路径"
              required
              hint="JSON 点路径，例如 data.sandbox_id"
              error={rentalErr?.resourceIdResponsePath}
            >
              <input
                className="input mono"
                placeholder="data.id"
                {...register(
                  `endpoints.${index}.rentalConfig.resourceIdResponsePath`,
                )}
              />
            </Field>
            <Row cols={2}>
              <Field label="计价单位" required>
                <Controller
                  name={`endpoints.${index}.rentalConfig.pricing.unit`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value || 'HOUR'}
                      onChange={field.onChange}
                      options={[
                        { value: 'SECOND', label: '每秒' },
                        { value: 'MINUTE', label: '每分钟' },
                        { value: 'HOUR', label: '每小时' },
                      ]}
                    />
                  )}
                />
              </Field>
              <Field
                label="单位价格（USD）"
                required
                error={rentalErr?.pricing?.pricePerUnitUsd}
              >
                <input
                  className="input mono"
                  type="number"
                  min="0"
                  step="0.000001"
                  {...register(
                    `endpoints.${index}.rentalConfig.pricing.pricePerUnitUsd`,
                    { valueAsNumber: true },
                  )}
                />
              </Field>
            </Row>
            <Row cols={2}>
              <Field
                label="最低计费秒数"
                required
                hint="最多 300 秒，与首个预留窗口一致"
                error={rentalErr?.pricing?.minimumBillableSeconds}
              >
                <input
                  className="input mono"
                  type="number"
                  min="1"
                  max="300"
                  step="1"
                  {...register(
                    `endpoints.${index}.rentalConfig.pricing.minimumBillableSeconds`,
                    { valueAsNumber: true },
                  )}
                />
              </Field>
              <Field
                label="单次最长租赁秒数"
                required
                hint="平台上限 604800 秒（7 天）"
                error={rentalErr?.pricing?.maxRentalSeconds}
              >
                <input
                  className="input mono"
                  type="number"
                  min="1"
                  max="604800"
                  step="1"
                  {...register(
                    `endpoints.${index}.rentalConfig.pricing.maxRentalSeconds`,
                    { valueAsNumber: true },
                  )}
                />
              </Field>
            </Row>
            <div
              className="micro"
              style={{ marginTop: 10, color: 'var(--fg-3)' }}
            >
              折算 $
              {Number.isFinite(rentalPerSecond)
                ? rentalPerSecond.toFixed(8)
                : '0.00000000'}
              /秒；每个滚动窗口预留约 ${(rentalPerSecond * 300).toFixed(8)}{' '}
              USD，关停缓冲预留约 ${(rentalPerSecond * 120).toFixed(8)} USD。
            </div>
          </Panel>
        )}

        {(rentalRole === 'TERMINATE' || rentalRole === 'STATUS') && (
          <Panel
            title="资源 ID 注入"
            subtitle="平台调用该接口时，把 CREATE 提取到的资源 ID 写入指定位置。"
            compact
          >
            <Row cols={2}>
              <Field label="注入位置" required>
                <Controller
                  name={`endpoints.${index}.rentalConfig.resourceIdInput.location`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value || 'BODY'}
                      onChange={field.onChange}
                      options={[
                        { value: 'PATH', label: '路径参数（PATH）' },
                        { value: 'QUERY', label: '查询参数（QUERY）' },
                        { value: 'BODY', label: '请求体（BODY）' },
                      ]}
                    />
                  )}
                />
              </Field>
              <Field
                label="字段路径"
                required
                hint="PATH 填占位符名称；BODY 可填 data.resourceId"
                error={rentalErr?.resourceIdInput?.path}
              >
                <input
                  className="input mono"
                  placeholder="resourceId"
                  {...register(
                    `endpoints.${index}.rentalConfig.resourceIdInput.path`,
                  )}
                />
              </Field>
            </Row>
          </Panel>
        )}

        {rentalRole === 'STATUS' && (
          <Panel
            title="状态映射"
            subtitle="控制面强制 kill 或资源自然回收时，平台据此停止续费并按最后确认时间结算。"
            compact
          >
            <Field
              label="状态响应路径"
              required
              hint="JSON 点路径，例如 data.status"
              error={rentalErr?.stateResponsePath}
            >
              <input
                className="input mono"
                placeholder="status"
                {...register(
                  `endpoints.${index}.rentalConfig.stateResponsePath`,
                )}
              />
            </Field>
            <Row cols={2}>
              <Field
                label="运行中状态值"
                required
                hint="逗号分隔，不区分大小写"
                error={rentalErr?.runningValues}
              >
                <Controller
                  name={`endpoints.${index}.rentalConfig.runningValues`}
                  control={control}
                  render={({ field }) => (
                    <RentalCsvInput
                      placeholder="running, ready"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Field>
              <Field
                label="已终止状态值"
                required
                hint="逗号分隔，不区分大小写"
                error={rentalErr?.terminatedValues}
              >
                <Controller
                  name={`endpoints.${index}.rentalConfig.terminatedValues`}
                  control={control}
                  render={({ field }) => (
                    <RentalCsvInput
                      placeholder="terminated, stopped"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Field>
            </Row>
            <Field
              label="终止时间响应路径（可选）"
              hint="服务商能返回真实终止时间时填写；否则平台使用最后存活确认时间。"
            >
              <input
                className="input mono"
                placeholder="terminated_at"
                {...register(
                  `endpoints.${index}.rentalConfig.terminatedAtResponsePath`,
                )}
              />
            </Field>
          </Panel>
        )}
      </div>
    );
  }
  if (billingType === 'DYNAMIC') {
    return <DynamicRulesEditor index={index} />;
  }
  // PER_TOKEN
  return <PerTokenEditor index={index} specId={specId} />;
};

// ============ Per-token (advanced) editor ============

// 「input 计数是否已含缓存」由 interface spec 决定，已知协议后端会强制按真相算，
// 前端据此锁定+说明：OpenAI 系=已包含(需扣减)；Anthropic=不含(另计)；其它=用户自配。
const SPEC_CACHE_AUTO = {
  openai: true,
  openai_responses: true,
  openai_image: true,
  gemini: true, // promptTokenCount 含缓存 → 已包含(需扣减),与后端 CACHE_INCLUDED_IN_INPUT_SPECS 同步
  claude: false,
};
// 有内置 token 路径预设的协议（与后端 TOKEN_JSON_PRESETS 同源,必须保持一致）。识别到即
// 自动配置,前端把「Token JSON paths」面板折叠,用户只需配模型价格。
const SPEC_HAS_PRESET = new Set([
  'openai',
  'openai_responses',
  'claude',
  'openai_image',
  'gemini',
]);

const PerTokenEditor = ({ index, specId }) => {
  const { t } = useT();
  const {
    control,
    register,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useFormContext();
  const tjpErr = errors?.endpoints?.[index]?.aiConfig?.tokenJsonPaths || {};
  const supportsStreaming = watch(
    `endpoints.${index}.aiConfig.supportsStreaming`,
  );
  // 注意:aiInterfaceSpecId 是 endpoint 顶层字段(与 aiConfig 同级),不在 aiConfig 下 ——
  // 写成 aiConfig.aiInterfaceSpecId 会恒为 undefined,锁定/折叠都不触发(见 2832/3294 同款路径)。
  // specId = EndpointEditor 的 effectiveSpec(实时 aiDetect 优先):新端点没保存、表单字段
  // 还是 null 时,也能据实时识别折叠面板,与上方「AI 接口」状态同步。
  const aiSpecId = specId || watch(`endpoints.${index}.aiInterfaceSpecId`);
  const autoCache = SPEC_CACHE_AUTO[aiSpecId]; // true / false / undefined(未知协议)
  const specHasPreset = SPEC_HAS_PRESET.has(aiSpecId);
  // 识别到协议(有 preset)时,清掉 INITIAL_ENDPOINT 默认塞进去的 openai 式 input/output 路径
  // ——否则非 openai 协议(如 claude:Anthropic 用 usage.input_tokens)会被 usage.prompt_tokens
  // 覆盖,运行时取不到 token(=0)。只清「openai 默认值」,用户在「展开覆盖」里手动设的不动。
  React.useEffect(() => {
    if (!specHasPreset) return;
    const base = `endpoints.${index}.aiConfig.tokenJsonPaths`;
    if (
      getValues(`${base}.inputTokens`) === PROVIDER_PRESETS.openai.inputTokens
    )
      setValue(`${base}.inputTokens`, '', { shouldDirty: true });
    if (
      getValues(`${base}.outputTokens`) === PROVIDER_PRESETS.openai.outputTokens
    )
      setValue(`${base}.outputTokens`, '', { shouldDirty: true });
  }, [specHasPreset, aiSpecId, index]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showPaths, setShowPaths] = React.useState(false);
  const modelPricingArray = useFieldArray({
    control,
    name: `endpoints.${index}.aiConfig.modelPricing`,
  });

  const applyPreset = (key) => {
    const preset = PROVIDER_PRESETS[key];
    if (!preset) return;
    Object.entries(preset).forEach(([k, v]) => {
      setValue(`endpoints.${index}.aiConfig.tokenJsonPaths.${k}`, v, {
        shouldDirty: true,
      });
    });
  };

  return (
    <div className="col gap-3">
      {/* Streaming moved to the General/基础 tab — it applies to all billing types, not just PER_TOKEN. */}
      {specHasPreset && !showPaths ? (
        // 识别到已知协议：token 路径自动配置，折叠面板，只露「展开覆盖」。
        <div
          className="card"
          style={{
            padding: '12px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ fontSize: 13, color: 'var(--fg-2)' }}>
            <span style={{ color: 'var(--c-mint, #5fd0a8)', fontWeight: 600 }}>
              ✓ Token 路径已按 <span className="mono">{aiSpecId}</span>{' '}
              协议自动配置
            </span>
            <div
              className="micro"
              style={{ color: 'var(--fg-4)', marginTop: 2 }}
            >
              无需填写 input/output/缓存
              路径与缓存语义；只需在下方配置每个模型的价格。
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setShowPaths(true)}
          >
            展开覆盖 ▾
          </button>
        </div>
      ) : (
        <Panel
          title={t('register.endpoint.token.paths.title')}
          compact
          subtitle={
            specHasPreset
              ? t('register.endpoint.token.paths.overrideSub').replace(
                  '{SPEC}',
                  aiSpecId,
                )
              : t('register.endpoint.token.paths.customSub')
          }
          action={
            <div className="row gap-1">
              {specHasPreset && (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setShowPaths(false)}
                >
                  {t('register.endpoint.token.paths.collapse')}
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => applyPreset('openai')}
              >
                OpenAI
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => applyPreset('anthropic')}
              >
                Anthropic
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => applyPreset('gemini')}
              >
                Gemini
              </button>
            </div>
          }
        >
          <Row cols={2}>
            <Field
              label={t('register.endpoint.token.inputPath')}
              error={tjpErr.inputTokens}
            >
              <input
                className="input mono"
                style={
                  tjpErr.inputTokens ? { borderColor: 'var(--err)' } : undefined
                }
                {...register(
                  `endpoints.${index}.aiConfig.tokenJsonPaths.inputTokens`,
                )}
              />
            </Field>
            <Field
              label={t('register.endpoint.token.outputPath')}
              error={tjpErr.outputTokens}
            >
              <input
                className="input mono"
                style={
                  tjpErr.outputTokens
                    ? { borderColor: 'var(--err)' }
                    : undefined
                }
                {...register(
                  `endpoints.${index}.aiConfig.tokenJsonPaths.outputTokens`,
                )}
              />
            </Field>
            <Field
              label={t('register.endpoint.token.modelPathResponse')}
              error={tjpErr.model}
            >
              <input
                className="input mono"
                style={tjpErr.model ? { borderColor: 'var(--err)' } : undefined}
                {...register(
                  `endpoints.${index}.aiConfig.tokenJsonPaths.model`,
                )}
              />
            </Field>
            <Field
              label={t('register.endpoint.token.modelPathRequest')}
              error={tjpErr.requestModel}
            >
              <input
                className="input mono"
                style={
                  tjpErr.requestModel
                    ? { borderColor: 'var(--err)' }
                    : undefined
                }
                {...register(
                  `endpoints.${index}.aiConfig.tokenJsonPaths.requestModel`,
                )}
              />
            </Field>
            <Field
              label={t('register.endpoint.token.cacheWritePath')}
              hint={t('register.endpoint.token.cachePath.hint')}
            >
              <input
                className="input mono"
                {...register(
                  `endpoints.${index}.aiConfig.tokenJsonPaths.cacheCreationInputTokens`,
                )}
              />
            </Field>
            <Field
              label={t('register.endpoint.token.cacheReadPath')}
              hint={t('register.endpoint.token.cachePath.hint')}
            >
              <input
                className="input mono"
                {...register(
                  `endpoints.${index}.aiConfig.tokenJsonPaths.cacheReadInputTokens`,
                )}
              />
            </Field>
          </Row>
          {/* 缓存是否已计入 input：上游返回的 input/prompt token 数里有没有把「缓存命中的 token」算进去。
            含了就要先减掉再计费，否则缓存 token 会被双重计费(input 价 + 缓存价各扣一次)；
            没含就把缓存单独加。OpenAI 系=已含、Anthropic=未含。已知协议这里锁定由系统自动判定。 */}
          <Field
            label={t('register.endpoint.token.cacheIncluded')}
            hint={t('register.endpoint.token.cacheIncluded.hint')}
            style={{ marginTop: 12 }}
          >
            {autoCache !== undefined ? (
              <div
                className="row gap-2"
                style={{ alignItems: 'center', flexWrap: 'wrap' }}
              >
                <span
                  className="btn btn-sm"
                  style={{
                    opacity: 0.65,
                    pointerEvents: 'none',
                    cursor: 'default',
                  }}
                >
                  {autoCache
                    ? t('register.endpoint.token.cacheIncluded.yes')
                    : t('register.endpoint.token.cacheIncluded.no')}
                </span>
                <span className="micro" style={{ color: 'var(--fg-3)' }}>
                  {
                    t('register.endpoint.token.cacheIncluded.autoBy').split(
                      '{SPEC}',
                    )[0]
                  }
                  <span className="mono">{aiSpecId}</span>
                  {
                    t('register.endpoint.token.cacheIncluded.autoBy').split(
                      '{SPEC}',
                    )[1]
                  }
                  {autoCache
                    ? t('register.endpoint.token.cacheIncluded.autoIncluded')
                    : t('register.endpoint.token.cacheIncluded.autoSeparate')}
                </span>
              </div>
            ) : (
              <Controller
                name={`endpoints.${index}.aiConfig.tokenJsonPaths.cacheIncludedInInput`}
                control={control}
                render={({ field }) => (
                  <button
                    type="button"
                    className={`btn btn-sm ${field.value ? 'btn-primary' : ''}`}
                    onClick={() => field.onChange(!field.value)}
                  >
                    {field.value
                      ? t('register.endpoint.token.cacheIncluded.yes')
                      : t('register.endpoint.token.cacheIncluded.no')}
                  </button>
                )}
              />
            )}
          </Field>
        </Panel>
      )}

      <ModelPricingPanel index={index} arr={modelPricingArray} />
    </div>
  );
};

// ============ Model pricing — Form ⇄ JSON ============
//
// Two editing modes for the same underlying state:
//   • Form: per-row inputs (the existing UX, units = per 1M tokens)
//   • JSON: free-form editor (units = per-token; matches what the backend stores
//     and what providers like Anthropic publish in their docs — so users can
//     paste a snippet from upstream docs directly without unit conversion)
//
// Switching form → json: serialize via pricingArrayToRecord (form → backend shape)
// Switching json → form: parse + validate + replace() the field array
//
// We use @uiw/react-textarea-code-editor (~50KB gzip) for JSON syntax highlighting
// and proper monospaced behavior. Validation is conservative: we only ever swap
// state on a successful parse, so the user can edit JSON freely without their
// form state being clobbered.
const ModelPricingPanel = ({ index, arr }) => {
  const { t } = useT();
  const { watch } = useFormContext();
  const [mode, setMode] = React.useState('form');
  const [jsonText, setJsonText] = React.useState('');
  const [jsonErr, setJsonErr] = React.useState(null);
  // Watch is the source of truth — useFieldArray's `.fields` lags behind because
  // it holds the mounted DOM keys, not the live values.
  const currentRows = watch(`endpoints.${index}.aiConfig.modelPricing`) || [];

  // JSON view mirrors the FORM state directly (per-1M token units), not the
  // backend per-token format. Rationale:
  //   • Consistency with the form view — same numbers visible in both modes.
  //   • Pasteability — Anthropic/OpenAI docs publish "/1M tokens" prices, not
  //     per-token. Users can paste prices directly.
  //   • Submit-time conversion is unchanged: pricingArrayToRecord still maps
  //     per-1M → per-token before sending to the backend.
  // Field names keep `*PricePerToken` for compatibility with the underlying
  // schema; the subtitle makes the per-1M unit explicit.
  const enterJsonMode = () => {
    const record = rowsToPer1MRecord(currentRows);
    setJsonText(stringifyPricesPretty(record));
    setJsonErr(null);
    setMode('json');
  };

  const applyJsonAndExit = () => {
    let obj;
    try {
      obj = JSON.parse(jsonText);
    } catch (e) {
      setJsonErr('Invalid JSON: ' + e.message);
      return;
    }
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      setJsonErr(
        'Top-level must be an object: { "modelName": { inputPricePerToken, outputPricePerToken } }',
      );
      return;
    }
    // Validate each entry — backend will reject otherwise.
    for (const [k, v] of Object.entries(obj)) {
      if (!k.trim()) {
        setJsonErr('Model name must be non-empty.');
        return;
      }
      if (!v || typeof v !== 'object' || Array.isArray(v)) {
        setJsonErr(`"${k}": value must be an object.`);
        return;
      }
      if (
        typeof v.inputPricePerToken !== 'number' ||
        !isFinite(v.inputPricePerToken)
      ) {
        setJsonErr(
          `"${k}.inputPricePerToken": must be a finite number (USD per 1M tokens, e.g. 3.0).`,
        );
        return;
      }
      if (
        typeof v.outputPricePerToken !== 'number' ||
        !isFinite(v.outputPricePerToken)
      ) {
        setJsonErr(`"${k}.outputPricePerToken": must be a finite number.`);
        return;
      }
      for (const field of TOKEN_PRICE_FIELDS) {
        if (
          v[field] != null &&
          (typeof v[field] !== 'number' || !isFinite(v[field]) || v[field] < 0)
        ) {
          setJsonErr(
            `"${k}.${field}": must be a finite, non-negative number if set.`,
          );
          return;
        }
      }
      if (v.toolCallPricing != null) {
        if (
          !v.toolCallPricing ||
          typeof v.toolCallPricing !== 'object' ||
          Array.isArray(v.toolCallPricing)
        ) {
          setJsonErr(`"${k}.toolCallPricing": must be an object if set.`);
          return;
        }
        for (const [tool, price] of Object.entries(v.toolCallPricing)) {
          if (
            !tool ||
            typeof price !== 'number' ||
            !isFinite(price) ||
            price < 0
          ) {
            setJsonErr(
              `"${k}.toolCallPricing.${tool}": must be a finite, non-negative per-call price.`,
            );
            return;
          }
        }
      }
      if (v.tiers != null && !Array.isArray(v.tiers)) {
        setJsonErr(`"${k}.tiers": must be an array if set.`);
        return;
      }
      for (const [tierIndex, tier] of (v.tiers || []).entries()) {
        if (!tier || typeof tier !== 'object' || Array.isArray(tier)) {
          setJsonErr(`"${k}.tiers[${tierIndex}]": must be an object.`);
          return;
        }
        for (const field of TOKEN_PRICE_FIELDS) {
          if (
            tier[field] != null &&
            (typeof tier[field] !== 'number' ||
              !isFinite(tier[field]) ||
              tier[field] < 0)
          ) {
            setJsonErr(
              `"${k}.tiers[${tierIndex}].${field}": must be a finite, non-negative number if set.`,
            );
            return;
          }
        }
      }
    }
    const rows = per1MRecordToRows(obj);
    arr.replace(rows);
    setJsonErr(null);
    setMode('form');
  };

  const cancelJson = () => {
    setJsonErr(null);
    setMode('form');
  };

  const action =
    mode === 'form' ? (
      <div className="row gap-2">
        <button
          type="button"
          className="btn btn-sm"
          onClick={enterJsonMode}
          title={t('register.endpoint.pricing.editJsonTitle')}
        >
          <XIcons.IconCode size={11} /> JSON
        </button>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() =>
            arr.append({
              modelName: '',
              inputPricePerToken: 1.0,
              outputPricePerToken: 2.0,
            })
          }
        >
          <XIcons.IconPlus size={11} />{' '}
          {t('register.endpoint.pricing.addModel')}
        </button>
      </div>
    ) : (
      <div className="row gap-2">
        <button type="button" className="btn btn-sm" onClick={cancelJson}>
          {t('register.endpoint.pricing.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={applyJsonAndExit}
        >
          <XIcons.IconCheck size={11} /> {t('register.endpoint.pricing.apply')}
        </button>
      </div>
    );

  const subtitle =
    mode === 'form'
      ? t('register.endpoint.pricing.subtitle')
      : 'Per 1M tokens (same as form view). Example: { "default": { "inputPricePerToken": 3, "outputPricePerToken": 15 } }';

  return (
    <Panel
      title={t('register.endpoint.pricing.title')}
      subtitle={subtitle}
      compact
      action={action}
    >
      {mode === 'form' ? (
        <ModelPricingRows index={index} arr={arr} />
      ) : (
        <div className="col gap-2">
          {jsonErr && (
            <div
              className="row gap-2"
              style={{
                padding: '8px 10px',
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 6,
                fontSize: 12,
                color: 'var(--err, #f87171)',
              }}
            >
              <XIcons.IconX size={12} />
              <span style={{ wordBreak: 'break-word' }}>{jsonErr}</span>
            </div>
          )}
          <div
            style={{
              border: '1px solid var(--line-2)',
              borderRadius: 6,
              background: 'var(--bg-1)',
              overflow: 'hidden',
            }}
          >
            <React.Suspense
              fallback={
                <textarea
                  className="input mono"
                  value={jsonText}
                  onChange={(e) => setJsonText(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: 200,
                    fontSize: 12,
                    border: 'none',
                    resize: 'vertical',
                  }}
                  placeholder={t('register.endpoint.pricing.loadingEditor')}
                />
              }
            >
              <CodeEditor
                value={jsonText}
                language="json"
                onChange={(e) => setJsonText(e.target.value)}
                padding={12}
                data-color-mode="dark"
                style={{
                  fontSize: 12,
                  fontFamily:
                    'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)',
                  backgroundColor: 'transparent',
                  minHeight: 200,
                }}
              />
            </React.Suspense>
          </div>
          <div
            className="micro-mono"
            style={{
              textTransform: 'none',
              letterSpacing: 0,
              color: 'var(--fg-4)',
            }}
          >
            {t('register.endpoint.pricing.hintPrefix')}
            <span className="mono">3</span>
            {t('register.endpoint.pricing.hintMid')}
            <span className="mono">cacheCreation5mPricePerToken</span>,{' '}
            <span className="mono">imagePricePerToken</span>,{' '}
            <span className="mono">audioInputPricePerToken</span>,{' '}
            <span className="mono">reasoningPricePerToken</span>,{' '}
            <span className="mono">toolCallPricing</span>,{' '}
            <span className="mono">tiers</span>
            {t('register.endpoint.pricing.hintSuffix')}
          </div>
        </div>
      )}
    </Panel>
  );
};

// Format prices without scientific notation. With the per-1M unit most values
// are plain decimals (e.g. 3, 0.5), but the helper still guards against tiny
// inputs (e.g. someone paste-edits per-token by mistake).
const PRICE_SIGIL = '__num__';
const fmtDecimal = (n) => {
  if (!isFinite(n)) return String(n);
  if (n === 0) return '0';
  // 12 位避免 JSON 模式把小额 toolCallPricing 四舍五入成 0。
  return n.toFixed(12).replace(/0+$/, '').replace(/\.$/, '');
};

const projectPer1MTokenPrices = (source, requiredDefaults = false) => {
  const out = {};
  for (const field of TOKEN_PRICE_FIELDS) {
    const value = source?.[field];
    if (value != null && value !== '') out[field] = Number(value);
    else if (
      requiredDefaults &&
      (field === 'inputPricePerToken' || field === 'outputPricePerToken')
    )
      out[field] = 0;
  }
  return out;
};

// Build a per-1M-unit record from form rows. The form already stores prices
// in per-1M (e.g. `inputPricePerToken: 3` means $3 per 1M tokens — see the
// "Input /1M" column header). We just project the array into a {modelName:{...}}
// shape and keep tiers nested.
//
// On the way out, pricingArrayToRecord (called at submit time) converts
// per-1M → per-token for the backend. The JSON editor doesn't touch that path.
const rowsToPer1MRecord = (rows) => {
  const out = {};
  for (const r of rows || []) {
    const name = (r.modelName || '').trim() || 'unnamed';
    const entry = projectPer1MTokenPrices(r, true);
    if (
      r.toolCallPricing &&
      typeof r.toolCallPricing === 'object' &&
      !Array.isArray(r.toolCallPricing)
    ) {
      entry.toolCallPricing = { ...r.toolCallPricing };
    }
    if (Array.isArray(r.tiers) && r.tiers.length > 0) {
      entry.tiers = r.tiers.map((t) => {
        return {
          inputTokenThreshold: Number(t.inputTokenThreshold || 0),
          ...projectPer1MTokenPrices(t, true),
        };
      });
    }
    out[name] = entry;
  }
  return out;
};

// Inverse of rowsToPer1MRecord — JSON edit → form rows (still per-1M).
const per1MRecordToRows = (record) =>
  Object.entries(record || {}).map(([modelName, p]) => ({
    modelName,
    ...projectPer1MTokenPrices(p, true),
    ...(p.toolCallPricing &&
    typeof p.toolCallPricing === 'object' &&
    !Array.isArray(p.toolCallPricing)
      ? { toolCallPricing: { ...p.toolCallPricing } }
      : {}),
    tiers: Array.isArray(p.tiers)
      ? p.tiers.map((t) => ({
          inputTokenThreshold: Number(t.inputTokenThreshold ?? 0),
          ...projectPer1MTokenPrices(t, true),
        }))
      : undefined,
  }));
const stringifyPricesPretty = (obj) => {
  const raw = JSON.stringify(
    obj,
    (_k, v) => {
      if (typeof v === 'number') return PRICE_SIGIL + fmtDecimal(v);
      return v;
    },
    2,
  );
  // The replacer wrapped numbers as "__num__0.0000005"; strip the wrapping.
  // Sigil-prefix prevents collision with user strings (no real string ever
  // starts with our underscore-sigil).
  return raw.replace(new RegExp(`"${PRICE_SIGIL}([0-9.eE+\\-]+)"`, 'g'), '$1');
};

export const ModelPricingRows = ({ index, arr }) => {
  const { t } = useT();
  const { register, control } = useFormContext();
  return (
    <div className="col gap-3">
      <div
        className="row"
        style={{
          padding: '6px 0',
          fontSize: 11,
          color: 'var(--fg-4)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          gap: 8,
        }}
      >
        <span style={{ flex: 3 }}>
          {t('register.endpoint.pricing.table.model')}
        </span>
        <span style={{ flex: 2 }}>
          {t('register.endpoint.pricing.table.input')}
        </span>
        <span style={{ flex: 2 }}>
          {t('register.endpoint.pricing.table.output')}
        </span>
        <span style={{ flex: 2 }}>
          {t('register.endpoint.pricing.table.cacheWrite')}
        </span>
        <span style={{ flex: 2 }}>
          {t('register.endpoint.pricing.table.cacheRead')}
        </span>
        <span style={{ width: 28 }} />
      </div>
      {arr.fields.map((row, i) => (
        <ModelPricingRow key={row.id} index={index} i={i} arr={arr} />
      ))}
    </div>
  );
};

// Keep the user's text draft separate from react-hook-form's numeric value.
// Native number inputs expose transient decimal edits such as `1.` as an empty
// value; combined with the page-level endpoints watch, that can make a price
// appear to snap back while the user is still typing. A decimal keyboard hint
// plus a local draft preserves the exact edit, while the form continues to
// receive numbers (or undefined/NaN so the existing Zod validation can report
// incomplete/invalid values).
export const ModelPriceInput = ({ name, optional = false, ...props }) => {
  const { control } = useFormContext();
  const { field } = useController({ control, name });
  const focusedRef = React.useRef(false);
  const lastFormValueRef = React.useRef(field.value);
  const [draft, setDraft] = React.useState(() =>
    field.value == null || Number.isNaN(field.value) ? '' : String(field.value),
  );

  React.useEffect(() => {
    const changedExternally = !Object.is(lastFormValueRef.current, field.value);
    lastFormValueRef.current = field.value;
    if (changedExternally && !focusedRef.current) {
      setDraft(
        field.value == null || Number.isNaN(field.value)
          ? ''
          : String(field.value),
      );
    }
  }, [field.value]);

  const publishDraft = (next) => {
    const trimmed = next.trim();
    if (trimmed === '') {
      field.onChange(optional ? undefined : Number.NaN);
      return;
    }
    const numeric = Number(trimmed);
    field.onChange(Number.isFinite(numeric) ? numeric : Number.NaN);
  };

  const onChange = (event) => {
    const next = event.target.value;
    setDraft(next);
    publishDraft(next);
  };

  return (
    <input
      {...props}
      ref={field.ref}
      name={field.name}
      type="text"
      inputMode="decimal"
      value={draft}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onChange={onChange}
      onBlur={() => {
        focusedRef.current = false;
        // Re-publish on blur in case an external form refresh happened while
        // this input was focused; the visible draft remains authoritative.
        publishDraft(draft);
        field.onBlur();
      }}
    />
  );
};

const ModelPricingRow = ({ index, i, arr }) => {
  const { t } = useT();
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext();
  const tiersArr = useFieldArray({
    control,
    name: `endpoints.${index}.aiConfig.modelPricing.${i}.tiers`,
  });
  // 该行的校验错误（react-hook-form 镜像结构）。用于红框 + 行内报错，
  // 让「需修正」从抽象变具体（用户配好了也能一眼看到到底哪格不对）。
  const rowErr = errors?.endpoints?.[index]?.aiConfig?.modelPricing?.[i] || {};
  const eb = (field) =>
    rowErr?.[field]?.message ? { borderColor: 'var(--err)' } : {};
  const ROW_LABELS = {
    modelName: '模型名',
    inputPricePerToken: '输入价',
    outputPricePerToken: '输出价',
    cacheCreationPricePerToken: '缓存写价',
    cacheReadPricePerToken: '缓存读价',
  };
  const rowMsgs = Object.keys(ROW_LABELS).filter((k) => rowErr?.[k]?.message);
  return (
    <div
      className="hairline"
      style={{
        padding: 10,
        borderRadius: 6,
        background: 'var(--bg-1)',
        ...(rowMsgs.length ? { boxShadow: 'inset 0 0 0 1px var(--err)' } : {}),
      }}
    >
      <div className="row gap-2" style={{ alignItems: 'center' }}>
        <input
          className="input mono"
          style={{ flex: 3, ...eb('modelName') }}
          placeholder={t('register.endpoint.pricing.modelName.placeholder')}
          {...register(
            `endpoints.${index}.aiConfig.modelPricing.${i}.modelName`,
          )}
        />
        <ModelPriceInput
          className="input mono"
          style={{ flex: 2, ...eb('inputPricePerToken') }}
          name={`endpoints.${index}.aiConfig.modelPricing.${i}.inputPricePerToken`}
        />
        <ModelPriceInput
          className="input mono"
          style={{ flex: 2, ...eb('outputPricePerToken') }}
          name={`endpoints.${index}.aiConfig.modelPricing.${i}.outputPricePerToken`}
        />
        <ModelPriceInput
          className="input mono"
          style={{ flex: 2, ...eb('cacheCreationPricePerToken') }}
          name={`endpoints.${index}.aiConfig.modelPricing.${i}.cacheCreationPricePerToken`}
          optional
          placeholder="—"
        />
        <ModelPriceInput
          className="input mono"
          style={{ flex: 2, ...eb('cacheReadPricePerToken') }}
          name={`endpoints.${index}.aiConfig.modelPricing.${i}.cacheReadPricePerToken`}
          optional
          placeholder="—"
        />
        <button
          type="button"
          className="btn btn-sm btn-ghost btn-icon"
          onClick={() => arr.remove(i)}
        >
          <XIcons.IconTrash size={11} />
        </button>
      </div>
      {rowMsgs.length > 0 && (
        <div
          style={{
            marginTop: 6,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '2px 14px',
          }}
        >
          {rowMsgs.map((k) => (
            <span key={k} style={{ fontSize: 11, color: 'var(--err)' }}>
              <b style={{ fontWeight: 600 }}>{ROW_LABELS[k]}</b>：
              {rowErr[k].message}
            </span>
          ))}
        </div>
      )}

      {/* Tiers — same column shape as the model row so the cache fields stay
          available at the per-tier granularity. Schema (tierSchema) already
          permits these as optional; only the UI was missing the inputs. */}
      <div
        style={{
          marginTop: 10,
          paddingLeft: 14,
          borderLeft: '2px solid var(--line)',
        }}
      >
        <div className="row between" style={{ marginBottom: 6 }}>
          <span
            className="micro-mono"
            style={{
              textTransform: 'none',
              letterSpacing: 0,
              color: 'var(--fg-4)',
            }}
          >
            {t('register.endpoint.pricing.tiers.title')}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() =>
              tiersArr.append({
                inputTokenThreshold: 1000000,
                inputPricePerToken: 0,
                outputPricePerToken: 0,
                cacheCreationPricePerToken: undefined,
                cacheReadPricePerToken: undefined,
              })
            }
          >
            <XIcons.IconPlus size={10} />{' '}
            {t('register.endpoint.pricing.tiers.add')}
          </button>
        </div>
        {tiersArr.fields.length === 0 ? (
          <div
            className="micro-mono"
            style={{
              textTransform: 'none',
              letterSpacing: 0,
              color: 'var(--fg-4)',
            }}
          >
            {t('register.endpoint.pricing.tiers.empty')}
          </div>
        ) : (
          <div className="col gap-2">
            {/* tier column header — mirrors the model row's labels (compact) */}
            <div
              className="row"
              style={{
                padding: '2px 0 2px 18px',
                fontSize: 10,
                color: 'var(--fg-4)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                gap: 8,
              }}
            >
              <span style={{ flex: 2 }}>
                {t('register.endpoint.pricing.tiers.threshold')}
              </span>
              <span style={{ flex: 2 }}>
                {t('register.endpoint.pricing.table.input')}
              </span>
              <span style={{ flex: 2 }}>
                {t('register.endpoint.pricing.table.output')}
              </span>
              <span style={{ flex: 2 }}>
                {t('register.endpoint.pricing.table.cacheWrite')}
              </span>
              <span style={{ flex: 2 }}>
                {t('register.endpoint.pricing.table.cacheRead')}
              </span>
              <span style={{ width: 22 }} />
            </div>
            {tiersArr.fields.map((tier, j) => {
              const tErr = rowErr?.tiers?.[j] || {};
              const tb = (f) =>
                tErr?.[f]?.message ? { borderColor: 'var(--err)' } : {};
              const tLabels = {
                inputTokenThreshold: '阈值',
                inputPricePerToken: '输入价',
                outputPricePerToken: '输出价',
                cacheCreationPricePerToken: '缓存写价',
                cacheReadPricePerToken: '缓存读价',
              };
              const tMsgs = Object.keys(tLabels).filter(
                (f) => tErr?.[f]?.message,
              );
              return (
                <div key={tier.id}>
                  <div className="row gap-2" style={{ alignItems: 'center' }}>
                    <span
                      className="mono"
                      style={{
                        color: 'var(--fg-3)',
                        fontSize: 11,
                        width: 10,
                        textAlign: 'center',
                      }}
                    >
                      ≥
                    </span>
                    <input
                      className="input mono"
                      style={{ flex: 2, ...tb('inputTokenThreshold') }}
                      type="number"
                      placeholder={t(
                        'register.endpoint.pricing.tiers.thresholdPlaceholder',
                      )}
                      {...register(
                        `endpoints.${index}.aiConfig.modelPricing.${i}.tiers.${j}.inputTokenThreshold`,
                        { valueAsNumber: true },
                      )}
                    />
                    <ModelPriceInput
                      className="input mono"
                      style={{ flex: 2, ...tb('inputPricePerToken') }}
                      name={`endpoints.${index}.aiConfig.modelPricing.${i}.tiers.${j}.inputPricePerToken`}
                      placeholder={t(
                        'register.endpoint.pricing.tiers.inputPlaceholder',
                      )}
                    />
                    <ModelPriceInput
                      className="input mono"
                      style={{ flex: 2, ...tb('outputPricePerToken') }}
                      name={`endpoints.${index}.aiConfig.modelPricing.${i}.tiers.${j}.outputPricePerToken`}
                      placeholder={t(
                        'register.endpoint.pricing.tiers.outputPlaceholder',
                      )}
                    />
                    <ModelPriceInput
                      className="input mono"
                      style={{ flex: 2 }}
                      name={`endpoints.${index}.aiConfig.modelPricing.${i}.tiers.${j}.cacheCreationPricePerToken`}
                      optional
                      placeholder="—"
                    />
                    <ModelPriceInput
                      className="input mono"
                      style={{ flex: 2 }}
                      name={`endpoints.${index}.aiConfig.modelPricing.${i}.tiers.${j}.cacheReadPricePerToken`}
                      optional
                      placeholder="—"
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost btn-icon"
                      onClick={() => tiersArr.remove(j)}
                    >
                      <XIcons.IconTrash size={10} />
                    </button>
                  </div>
                  {tMsgs.length > 0 && (
                    <div
                      style={{
                        margin: '4px 0 0 18px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '2px 14px',
                      }}
                    >
                      {tMsgs.map((f) => (
                        <span
                          key={f}
                          style={{ fontSize: 11, color: 'var(--err)' }}
                        >
                          <b style={{ fontWeight: 600 }}>
                            分层{j + 1} {tLabels[f]}
                          </b>
                          ：{tErr[f].message}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ============ Shared bits ============

// section=true：作为「表单小节」渲染——上分隔线 + 左侧彩条 + 加粗标题，让各组区分明显。
// first=true：小节但不画上分隔线（用于第一段）。不传 section 时与原样式完全一致。
const Panel = ({
  title,
  subtitle,
  action,
  children,
  compact,
  section,
  first,
}) => (
  <div
    className="panel-flat"
    style={{
      padding: compact ? '12px 0' : '16px 0',
      ...(section && !first
        ? { borderTop: '1px solid var(--line)', paddingTop: 18, marginTop: 8 }
        : {}),
    }}
  >
    <div className="row between" style={{ marginBottom: compact ? 10 : 12 }}>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            color: 'var(--fg-1)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            ...(section ? { fontWeight: 600 } : {}),
          }}
        >
          {section && (
            <span
              style={{
                width: 3,
                height: 13,
                borderRadius: 2,
                background: 'var(--c-mint, #5fd0a8)',
                flexShrink: 0,
                display: 'inline-block',
              }}
            />
          )}
          <span>{title}</span>
        </div>
        {subtitle && (
          <div
            className="micro-mono"
            style={{
              textTransform: 'none',
              letterSpacing: 0,
              marginTop: 2,
              ...(section ? { marginLeft: 11 } : {}),
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      {action}
    </div>
    <div>{children}</div>
  </div>
);

const Row = ({ cols, children }) => (
  <div
    className="register-row"
    style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${cols || 2}, 1fr)`,
      gap: 14,
      marginBottom: 14,
    }}
  >
    {children}
  </div>
);

const Field = ({ label, hint, required, error, children, style }) => (
  <div style={style}>
    <label className="field-label">
      {label} {required && <span style={{ color: 'var(--err)' }}>*</span>}
    </label>
    {children}
    {error?.message && <ErrorText text={error.message} />}
    {hint && !error?.message && (
      <div
        className="micro-mono"
        style={{ textTransform: 'none', letterSpacing: 0, marginTop: 5 }}
      >
        {hint}
      </div>
    )}
  </div>
);

const ErrorText = ({ text }) => (
  <div style={{ marginTop: 5, color: 'var(--err)', fontSize: 11 }}>{text}</div>
);

const EmptyState = ({ icon, text, sub }) => (
  <div style={{ padding: 24, textAlign: 'center', color: 'var(--fg-4)' }}>
    <div style={{ marginBottom: 8 }}>{icon}</div>
    <div style={{ fontSize: 13, color: 'var(--fg-3)' }}>{text}</div>
    {sub && (
      <div
        className="micro-mono"
        style={{ marginTop: 4, textTransform: 'none', letterSpacing: 0 }}
      >
        {sub}
      </div>
    )}
  </div>
);

// ============ Helpers ============

const countErrorsInPaths = (errors, paths) => {
  if (!errors) return 0;
  let count = 0;
  const walk = (node) => {
    if (!node) return;
    if (typeof node === 'object') {
      if (node.message) count += 1;
      else Object.values(node).forEach(walk);
    }
  };
  for (const p of paths) {
    walk(errors[p]);
  }
  return count;
};

export default RegisterPage;
export { RegisterPage };
